/**
 * API client for Mission Control.
 *
 * Auth model: all authenticated requests read their bearer token from
 * tokenManager.getToken(). On a 401 response the client calls
 * tokenManager.refresh() once; if the refresh succeeds the request is retried
 * once with the new token; if it fails, UnauthorizedError is thrown (the
 * tokenManager will also have called onExpired to transition the App to the
 * unauthenticated state).
 *
 * getConfig() and getMe() are bootstrap helpers that run before the manager is
 * initialised; getMe() still takes an explicit token argument for that reason.
 */

import { tokenManager } from '../auth/tokenManager'

export type RegistrationState = 'registered' | 'pending' | 'blocked'

export interface ChatCommandArgument { input: string; value: string; defaulted: boolean }
export interface ChatCommandPreview { state: 'command' | 'not_a_command'; activity: string; agent: string; argument: ChatCommandArgument | null; usage: string }
export interface ChatCommandSuggestion { value: string; text: string }
export interface ChatCommandSuggestions { activity: string; input: string; items: ChatCommandSuggestion[]; selected: string | null }
export interface ChatCommandPreviewResponse { preview: ChatCommandPreview | null; suggestions: ChatCommandSuggestions | null }

/** One consumer row of a Used-by panel (ADR-127 D4). */
/** Result of POST /api/improvements/:id/deny. */
export interface DenyImprovementResult {
  ok: boolean
  number: number
  closed: boolean
  /** The PR's head branch. */
  branch: string
  /** True when the head branch was deleted as part of the deny. */
  branch_deleted: boolean
  /** Present when the branch was deliberately kept: not this repo, not a Mission Control lane. */
  branch_kept?: string
  /** Present when the PR closed but the branch was not deleted (an error, or the ref changed since the PR was read); the name may still be reserved. */
  branch_delete_error?: string
  /** True when the improvement row was recorded as `declined`. Absent from an older backend. */
  status_recorded?: boolean
  /** Present when the PR closed but the row could not be recorded as `declined`. */
  status_error?: string
}

export interface DataConsumer {
  kind: 'skill' | 'workflow' | 'cron'
  name: string
  path: string
  line: number
  alias: string
  /** True for a cron: it declares no pin, it drives a workflow that does. Marked rather than
   *  hidden — "this cron will break" and "this workflow will break" read differently. */
  indirect: boolean
  via?: string
}

export interface DataUsedBy {
  set: string
  consumers: DataConsumer[]
  count: number
  /** False when config/registry-policy.json EXISTS but could not be parsed, so a role-bound
   *  consumer may be missing and the panel says so rather than implying completeness.
   *
   *  An ABSENT policy file is TRUE (ADR-147 D3): no binding exists, so no role-bound consumer
   *  can, and the list is exhaustive. It used to be false — the server read the file off MC's
   *  /fleet mount and could not tell absent from unparseable — which left the four fleets with
   *  no policy file showing the caveat permanently, warning about a consumer that cannot exist. */
  role_bindings_readable: boolean
}

/** One row of the Data area's set list (ADR-127 D1), and the input a version badge needs (D5). */
export interface DataSetRow {
  set: string
  schema: string
  klass: 'fleet_owned' | 'core_owned' | 'external_fleet_instance' | 'external_shared_instance'
  editable: boolean
  badges: string[]
  why: string | null
  route: string | null
  status: string | null
  provider: string | null
  external: boolean
  listed: boolean
  ownership_mismatch: string | null
  /** ADR-168 D2 — may THIS fleet publish the next version of this set's schema?
   *
   *  THREE states, not a boolean, and `state` is the ONLY encoding of the verdict. A caller
   *  that collapses it to a boolean tells an operator "you may not" when the truth is "we
   *  could not determine ownership" — which is what sends them to hand-edit the fleet repo.
   *  Absent on a fleet whose rules script could not answer at all. */
  schema_publish?: SchemaPublishVerdict
}

/** The canonical `schema_publish` decision, as `data_area_rules.py` prints it and the
 *  `/sets` and `/set` endpoints re-emit it verbatim (ADR-168 D2). */
export interface SchemaPublishVerdict {
  state: 'allowed' | 'denied' | 'unavailable'
  /** Why, in operator language. `null` for `allowed`, which has no reason to give. */
  reason: string | null
  /** The ONE next step that works when this fleet may not publish here. `null` for
   *  `allowed`, which has no route to give. */
  route: string | null
}

/**
 * The READINESS route, for the one case where the server sent no `route` of its own.
 *
 * `unavailable` means the schema could not be READ, and repairing a schema is the fleet's own
 * `reference-data-authoring` capability rather than core's — which is why this wording belongs to
 * the fleet. The authority is `READINESS_ROUTE` in `scripts/data_area_rules.py`, mirrored by the
 * backend's `readinessRoute`; every verdict those two produce carries it on the wire, so this
 * constant is only the fallback for a verdict that arrived without one. It lives here, in the
 * module that owns the wire types, so no component states it inline a second time.
 */
export const READINESS_ROUTE = 'Ask the reference-data agent to repair this schema'

/** ONE set's full read model (ADR-127 D2/D3) — the three documents the generated form needs,
 *  plus the same ownership row the list serves so the two cannot disagree. */
export interface DataSetDetail extends DataSetRow {
  data_path: string
  schema_path: string
  /** The RAW schema: layer 2 lives in its `x-` keys, and the FORM MODEL reads raw. */
  schema_document: unknown
  /** The COMPILED schema: layer-2 effects materialised, and VALIDATION reads compiled.
   *  `null` — never `{}` — when the compiler could not be run: an empty schema would read as
   *  "no constraints" and validate anything. When it is null the set is not editable, and
   *  `notes` says why. */
  compiled_schema: object | null
  document: unknown
  /** Read-degradations, in operator language. Empty is the normal case. Being read-only
   *  because a set is core-owned or external is NOT a degradation and produces no note. */
  notes: string[]
  read_only_reason?: string
}

/** One consumer row in an impact verdict — the shape `impact_of_change` actually emits
 *  (`scripts/validate_data_refs.py:1624-1637`): the consumer's `kind`, `name` and repo-relative
 *  `path`, plus the new `errors` and the `lines` they were found on. `lines` is parallel to
 *  `errors`, so a row can name each consumer by kind, name, path AND line.
 *
 *  Every field is optional because the Go handler re-emits the checker's stdout as
 *  `map[string]any` and validates nothing on the way through (`data_references.go:361`), so a
 *  renamed field on the Python side arrives here as `undefined` rather than as an error.
 *
 *  This carries ONLY the emitted fields. It briefly also declared `consumer`, `line` and `why`
 *  as deprecated, because `DataArea.tsx` still read them and printed `path:undefined —
 *  undefined` for every blocked record edit; that render now reads the real fields, so the three
 *  are gone. Adding a field back here that the checker does not emit re-opens that failure. */
export interface DataImpactRow {
  kind?: string
  name?: string
  path?: string
  errors?: string[]
  lines?: number[]
  warnings?: string[]
  /** The third channel. A pending row is emitted as `dict(row, pending=...)` — the same shape as
   *  a warning row, so `{kind, name, path, pending}` with NO `lines`. The consumer references
   *  reference data that is not committed yet, which is a sequencing fact rather than a reason to
   *  refuse the change: `blocks_propose` follows `breaking` alone. */
  pending?: string[]
}

/** ADR-127 D3 layer 2 — THE propose gate's verdict.
 *
 *  `blocks_propose` is OPTIONAL on purpose, and the omission is the contract: a 503 carries no
 *  verdict at all rather than `false`, so a caller cannot accidentally read "nothing would
 *  break" out of "we could not check". Treat `undefined` as NO ANSWER — never as a pass. */
export interface DataImpactVerdict {
  breaking?: DataImpactRow[]
  warnings?: DataImpactRow[]
  /** Consumers left referencing data nobody has committed yet. Never counted into
   *  `blocks_propose`: the producer of this change is not who commits that data. */
  pending?: DataImpactRow[]
  consumers_checked?: number
  blocks_propose?: boolean
  error?: string
  detail?: string
}

/** One finding from the canonical Python validator — `entry()` in
 *  `scripts/validate-reference-data.py:214`, re-emitted verbatim by the Go handler
 *  (`data_schema_draft.go:196-210`), so this is the exact wire shape.
 *
 *  A SIBLING of `schemaValidator.ts`'s `SchemaFinding`, not a reuse of it: that one describes the
 *  in-browser Ajv engine and carries `keyword`, which parity assertions there key on. This one
 *  describes the Python engine and carries `layer` + `code` instead, and `code` is the stable
 *  machine handle on that side. Collapsing the two would lose a field either way. */
export interface DataFinding {
  /** RFC 6901 pointer into the draft; `""` for a document-level finding. */
  pointer: string
  /** Which validation layer refused it — `"structure"` or `"semantics"`. */
  layer: string
  /** The stable machine handle, e.g. `"core_ownership_mismatch"`. */
  code: string
  /** The human-readable text. */
  message: string
}

/** The one place a `DataFinding` becomes text for an operator.
 *
 *  ONE function, not a helper copied into each page: the crash this fixes exists at two render
 *  sites only because the render code exists twice, so a second copy of the fix reproduces the
 *  cause it is meant to remove.
 *
 *  The `pointer || '(document)'` form follows `DataSetForm.tsx:417`/`:439`, which already render a
 *  finding this way. The ` [code]` suffix is a deliberate addition those two do not have: `code` is
 *  the stable machine handle on the Python side and the Ajv side has no equivalent, so it is what an
 *  operator quotes in a bug report. `layer` is carried on the type but not shown — it is the
 *  validator's internal partition, not something an operator acts on. */
export function dataFindingLine(f: DataFinding): string {
  // DRIFT STAYS LEGIBLE. Nothing validates this shape on the way through: the Go handler
  // re-emits the validator's stdout as `map[string]any`, so a renamed field in the Python
  // `entry()` reaches here as `undefined` and would print `(document): undefined [undefined]`
  // — indistinguishable from a real finding, and silent. Printing the raw entry instead is
  // ugly on purpose: it is still the validator's answer, and it names its own field spellings.
  if (!f.message) return JSON.stringify(f)
  return `${f.pointer || '(document)'}: ${f.message} [${f.code}]`
}

/** ADR-127 D2/D3 — the canonical validator's verdict on an UNSAVED draft.
 *
 *  `findings` is ABSENT (not empty) when the validator could not run: an empty array is what a
 *  caller renders as "no problems found", and that is the one conclusion a missing engine does
 *  not support. Treat `undefined` as "unchecked". */
export interface DataDraftVerdict {
  findings?: { rel?: string; errors?: DataFinding[] }[]
  valid?: boolean
  compiled_schema?: object | null
  compile_note?: string
  error?: string
  detail?: string
}

export interface DataSetList {
  sets: DataSetRow[]
  /** Present when the list could not be computed: empty because we could not ask, which is a
   *  different fact from "this fleet has none". */
  note?: string
}

/** What an external set may bind to (ADR-135 D5 / S8 E2). */
export interface DataSourceProviderList {
  /** OMITTED — not empty — when the catalog could not be read, so a caller cannot render
   *  "this platform integrates nothing" out of "we could not look". */
  providers?: {
    provider: string
    class: 'mcp' | 'http'
    name: string
    /** The entities this provider declares (S1). `fields` is per ENTITY because one provider
     *  holds both — Asana's `task` is introspectable, its `section` is not. Empty for an
     *  `http:` provider by design (S3's column spec answers the field question). */
    entities: {
      entity: string
      label: string
      fields: boolean
      /** Whether the INSTANCE tier resolves — independent of `fields`. */
      instance?: boolean
      /** What the field tier needs supplied before it can be resolved (ADR-135 4a). Present
       *  and empty means "nothing"; `from` chooses the control the picker renders. */
      fields_scope?: { name: string; from: string; values?: string[] }[]
    }[]
  }[]
  /** Whether S1's `management.data` is populated ANYWHERE. Kept alongside the per-provider
   *  lists: "the catalog declares none yet" is a different sentence from "this provider has
   *  none", and different again from "we could not read the catalog". */
  management_data?: boolean
  note?: string
}

/** One resolved key — an entity INSTANCE (`{id, name}`) or a FIELD DEFINITION
 *  (`{id, name, type}`) — as ADR-135 4a's gateway returned it. */
export interface ResolvedDataKey {
  id: string
  name: string
  /** Present on the `fields` tier only, and already NORMALISED by the gateway to the
   *  vocabulary a schema column understands. A provider's own type name never reaches
   *  here, so the UI has no provider-specific mapping to keep in sync. */
  type?: string
}

/** The answer to "what can I pick here" (ADR-135 D4). A CLOSED discriminated union: the
 *  UI must branch on `outcome`, because the three failures need three different sentences
 *  and none of them is "there is nothing here".
 *
 *  `keys` exists ONLY on `ok`. That is deliberate and load-bearing — an empty list is what
 *  a caller renders as "this project has no sections", which is a different and wrong claim
 *  from "we could not look". */
export type ResolveKeysVerdict =
  | { outcome: 'ok'; keys: ResolvedDataKey[]; truncated: boolean; cap?: number }
  /** The provider does not offer this tier (S1 F4). Not an error: the operator types the
   *  value instead and the set is mode `asserted` (S8 E6). */
  | { outcome: 'unsupported'; reason: string }
  /** The provider, or the gateway in front of it, could not be reached now. Retryable. */
  | { outcome: 'unavailable'; reason: string }
  /** The credential is missing or rejected (S9 R4). Distinct from `unavailable` because the
   *  fix is an operator action, not a retry. */
  | { outcome: 'unauthorized'; reason: string }

/** One reference the picker may offer (ADR-127 D9-D15). `token` is inserted verbatim;
 * `chip` is what the operator reads and must parse back to `token` (D13). */
export interface DataReferenceOffer {
  token: string
  chip: string
  path: string
  slot: string
  /** A real value from the live set, for the hover (D11). Null when no record carries
   * the field — absent rather than invented, because a made-up sample reads as data. */
  sample: string | number | boolean | null
  /** ADR-127 D14 semantic markers carried through from a match candidate, when this offer
   *  came from one. The browse mode's offers do not carry them. */
  notes?: string[]
}

export interface DataReferenceSet {
  set: string
  /** The pinned SCHEMA version the hover shows — not the same string as `set`. */
  schema: string
  suggested_alias: string
  keys: string[]
  keys_truncated: boolean
  key_limit: number
  deprecated: boolean
  offers: DataReferenceOffer[]
}

export interface DataReferenceOffers {
  slot: string
  /** True for an expression slot (`for.in`), which takes `.data.…` rather than
   * `{{data.…}}` (ADR-126 D1). The UI must not choose this for itself. */
  expression?: boolean
  sets: DataReferenceSet[]
  /** Present when the generator was unavailable: the list is empty because we could
   * not ask, which is a different fact from "this fleet has no reference data". */
  note?: string
}

/** Where one literal sits in the parsed draft (ADR-179 P1/P2).
 *
 * `index` addresses a `command.args` entry, `key` a `python.inputs` / `run.inputs` mapping
 * entry, and `agent.run` carries neither. Task names are unique across a valid activity, so
 * the tuple is an address rather than a search. */
export interface DataReferenceMatchLocation {
  task: string
  slot: string
  index?: number
  key?: string
}

/** One reference whose resolved value EQUALS the literal at a location.
 *
 * Everything the picker needs to render and to write is here, so accepting a candidate fetches
 * nothing: `token` goes into the field verbatim and `pin` is the pin that makes it resolvable.
 * `notes` carries the ADR-127 D14 semantic markers (`deprecated_field`, `deprecated_set`). */
export interface DataReferenceMatchCandidate {
  token: string
  chip: string
  path: string
  slot: string
  value: string | number | boolean
  set: string
  schema_version: string
  pin: { alias: string; set: string }
  notes: string[]
}

/** One literal site and the candidates that equal it. An empty `candidates` is a real answer —
 * "no published set carries this value" — not a failure to look. */
export interface DataReferenceMatch {
  location: DataReferenceMatchLocation
  literal: string | number | boolean
  type: 'string' | 'number' | 'boolean'
  candidates: DataReferenceMatchCandidate[]
}

/** What the matcher scanned, so "no exact match found" is read with its limits in view: the
 * per-set key cap, and any data document that could not be read. */
export interface DataReferenceMatchCoverage {
  sets_scanned: number
  sets_skipped: { path: string; reason: string }[]
  key_limit: number
  key_limit_hit: string[]
  /** Value-equal references the author-time checks refused, so they were never offered. A
   *  row with none left reads as "no exact match found", which is a different claim — the
   *  coverage line says how many were dropped so the two are not confused. */
  candidates_dropped: number
}

export interface DataReferenceMatches {
  /** Echoed verbatim from the request. The caller owns the counter and keeps only the response
   *  whose revision is still current — a late reply about an older draft is not an answer. */
  revision: string
  /** Absent on the degraded (`note`) response, which carries only what it could answer. */
  catalog_revision?: string
  slots_scanned?: string[]
  coverage?: DataReferenceMatchCoverage
  locations: DataReferenceMatch[]
  /** Requested locations the draft no longer has. Only populated when `locations` was sent. */
  unknown_locations: DataReferenceMatchLocation[]
  /** Present when the matcher could not be asked: the list is empty because we could not look,
   *  which is a different fact from "nothing matched". */
  note?: string
}

// ---------------------------------------------------------------------------
// Error types
// ---------------------------------------------------------------------------

export class UnauthorizedError extends Error {
  constructor(message = 'Unauthorized') {
    super(message)
    this.name = 'UnauthorizedError'
  }
}

export class ForbiddenError extends Error {
  /**
   * The backend's `route` — the ONE next step that actually works ("Propose a
   * change to core", "Edit the field projection in this fleet's MCP Admin").
   *
   * Carried as a field AND folded into `message`, deliberately. ADR-127 D1 is
   * explicit that "not editable here" with no route is what pushes an operator
   * into hand-editing the fleet repo, and ADR-147 D4 therefore sends a route with
   * every ownership refusal. Every panel in the app renders a caught error as
   * `e.message`, so a route that lived only in a field would reach no screen at
   * all — which is the state this replaces. The field is what a caller that wants
   * to render the step distinctly reads; `message` is what the eleven existing
   * `em(e)` call sites already show.
   */
  readonly route?: string

  constructor(message = 'Forbidden', route?: string) {
    super(route ? `${message}\n\nNext step: ${route}` : message)
    this.name = 'ForbiddenError'
    this.route = route
  }
}

/** A write/copilot-draft call targeted a core-owned artifact (ADR-103 hard
 * lock — a core agent's own artifact, or a fleet skill shadowing a universal
 * name). Thrown instead of the generic ForbiddenError so callers can surface
 * `message` (the backend's `detail`, e.g. "brain/delegation-protocol is
 * core-owned…") directly rather than a bare "Forbidden". */
export class CoreOwnedError extends ForbiddenError {
  constructor(message = 'This is core-owned and can only be changed through core.', route?: string) {
    // ADR-103's write guards send no route — the sentence already says "through
    // core" — but ADR-147 D4's core_owned dataset refusal does, and it is the
    // same coded 403. Optional here for that reason, not because the two kinds of
    // refusal are different.
    super(message, route)
    this.name = 'CoreOwnedError'
  }
}

/** POST /api/activities/copilot returns 503 when the fleet has no Anthropic
 * key configured (ADR-079 Phase 5d). Thrown distinctly so callers can show
 * "the copilot isn't configured here — use Field/YAML" instead of a generic
 * failure. */
export class CopilotNotConfiguredError extends Error {
  constructor(message = "The AI copilot isn't configured for this fleet.") {
    super(message)
    this.name = 'CopilotNotConfiguredError'
  }
}

export class UnsavedAnswerGoneError extends Error {
  constructor(message = 'the server no longer holds this answer; copy it from the chat before leaving') {
    super(message)
    this.name = 'UnsavedAnswerGoneError'
  }
}

export class StaleDraftError extends Error {
  constructor(message = 'This draft is no longer current.') {
    super(message)
    this.name = 'StaleDraftError'
  }
}

export class RunFinishedError extends Error {
  readonly runId: string | null

  constructor(message = 'This run is finished.', runId: string | null = null) {
    super(message)
    this.name = 'RunFinishedError'
    this.runId = runId
  }
}

export class RunChangedError extends Error {
  constructor(message = 'This run changed, so nothing was discarded.') {
    super(message)
    this.name = 'RunChangedError'
  }
}

function staleDraftFrom(status: number, text: string): StaleDraftError | null {
  if (status !== 409) return null
  try {
    return JSON.parse(text).error === 'stale_draft' ? new StaleDraftError() : null
  } catch {
    return null
  }
}

function runFinishedFrom(status: number, text: string): RunFinishedError | null {
  if (status !== 409) return null
  try {
    const body = JSON.parse(text)
    if (body.error !== 'run_finished') return null
    const runId = typeof body.run_id === 'string' && body.run_id ? body.run_id : null
    return new RunFinishedError(body.detail, runId)
  } catch {
    return null
  }
}

function runChangedFrom(err: unknown): RunChangedError | null {
  if (!(err instanceof Error) || !err.message.startsWith('HTTP 409: ')) return null
  try {
    const body = JSON.parse(err.message.slice('HTTP 409: '.length))
    return body.error === 'run_changed' ? new RunChangedError(body.detail) : null
  } catch {
    return null
  }
}

/**
 * ADR-107 P2: a propose/convert/rename/cron-author call returned 409
 * `{"error":"author_tests_failing","checks":[...]}` — the fleet's OWN
 * author tests fail against the proposed content, caught IN-APP before a
 * PR ever opened (the in-app author-test gate). `checks` is the SAME
 * `FailingCheck[]` shape ImprovementPRReview's `checks_failing` 409 already
 * carries (ADR-1027/#1027) — callers render it with the existing
 * FailingChecksPanel, not a generic error toast, and keep the form open so
 * the operator can fix + retry.
 */
export class AuthorTestsFailingError extends Error {
  checks: FailingCheck[]
  constructor(checks: FailingCheck[], message = "This change fails the fleet's own author tests.") {
    super(message)
    this.name = 'AuthorTestsFailingError'
    this.checks = checks
  }
}

// ---------------------------------------------------------------------------
// Response types — Phase 1
// ---------------------------------------------------------------------------

export interface MeResponse {
  email: string
  fleet_role: string
  scope: Record<string, unknown> | null
}

export interface ClientConfig {
  fleet_slug: string
  console_url: string
  /** True when the fleet has an Anthropic key (the AI copilot/conversion works).
   * Absent on older MC images → treat as configured to avoid a false warning. */
  copilot_configured?: boolean
}

// ---------------------------------------------------------------------------
// Response types — Phase 2/3 (ADR-022 frozen contracts)
// ---------------------------------------------------------------------------

export interface DelegationStats {
  total: number
  success: number
  timeout: number
  error: number
  empty: number
  success_rate: number
  avg_duration_s: number
}

export interface AgentStatus {
  profile: string
  last_seen: string | null
  last_outcome: string | null
  calls_24h: number
  failures_24h: number
}

export interface ApprovalStats {
  pending: number
  responded_24h: number
  expired_24h: number
  oldest_pending_min: number | null
}

export interface GradeStats {
  count: number
  avg: number | null
}

export interface ReactionGradingReadiness {
  ready: boolean
  fleet_id_set: boolean
  brain_profile: string | null
  reactions_24h: number
}

export interface FeedbackHealth {
  reaction_grading: ReactionGradingReadiness
}

/** ADR-161 capture — armed for ONE run, consumed by it. */
export interface CaptureArmed {
  armed: boolean
  note?: string
}

export interface CaptureIndex {
  armed: boolean
  runs: {
    run_id: string
    captures: number
    gaps?: number
    recorded_at: string | null
    /** Which servers/tools this run actually recorded — a UUID says nothing about what
     *  the recording was FOR, and after a day there are a dozen of them. */
    subjects?: string[]
  }[]
}

export interface CaptureFiles {
  run_id: string
  files: { file: string; bytes: number | null }[]
}

export interface StorageHealth {
  total_bytes?: number
  used_bytes?: number
  free_bytes?: number
  used_percent?: number
  warn_percent?: number
  warn?: boolean
  error?: string
  /** Whether the chat warning can post, and how its last attempt went. */
  chat_alert?: { armed?: boolean; last_error?: string | null; last_posted_at?: string | null }
}

export interface FleetHealth {
  execution_pause?: {state?:string;paused?:boolean;reason?:string|null;set_by?:string|null;set_at?:string|null;console_unreachable_since?:string|null;test_resume?:{producer:string;until:string;set_by:string;set_at:string;reason:string}|null}
  restored_schema?: {status?:string;reason?:string;archive_id?:string;schema_version?:number;restored_at?:string}
  kanban_storage?: {status:string;reason?:string;db_bytes?:number;free_bytes?:number;threshold_free_bytes?:number;attention?:boolean}
  slack_serial?: {status?:string;reason?:string;serial_eligible?:number;serial_contention?:number;serial_wait_seconds?:number;serial_timeout?:number}
  fleet_id: string
  generated_at: string
  delegation_24h: DelegationStats
  agents: AgentStatus[]
  approvals: ApprovalStats
  grades_24h: GradeStats
  feedback?: FeedbackHealth // ADR-028; absent on older fleet images
  /** ADR-206 D10 — the data volume; absent on older fleet images. */
  storage?: StorageHealth
  /** ADR-206 D9 — whether run workspaces are swept after 48h on this fleet. */
  run_workspace_sweep?: 'on' | 'off'
  /**
   * ADR-105 D2 — the agentcore commit the AGENT-HOST runtime (this fleet's
   * hermes containers, auto-deployed) was actually built from. Baked into the
   * image (docker/Dockerfile.hermes), surfaced by the agent-host's own
   * `/internal/health`, and forwarded verbatim by GET /api/fleet/health — the
   * SAME field scripts/deploy-fleet.sh's ADR-105 deploy-lag guard reads. This
   * is a DIFFERENT track from CoreStatus.deployed_sha (the MC container's own
   * image, operator-gated) — the two legitimately differ since agents
   * auto-deploy and MC does not. `null` on a pre-ADR-105 image or if the
   * agent-host was unreachable when this response was generated; never
   * absent (the upstream always includes the key).
   */
  code_sha?: string | null
  build_time?: string | null
}

/** Agent-host half of ApplyCoreUpdateResult (ADR-099 D8.3): the
 * console-mediated dispatch of the fleet's own deploy-fleet.yml@main.
 * `dispatched` is false — never surfaced as an HTTP error — when the
 * console's GitHub App isn't yet authorized with Actions:write on this
 * fleet's repo (`unauthorized: true`) or any other failure occurred;
 * `detail` is always a diagnosable message for the operator. Before D8.3
 * this was a direct MC->GitHub dispatch that always succeeded or threw; now
 * it mirrors `mission_control` below exactly. */
export interface AgentHostApplyResult {
  dispatched: boolean
  held?: boolean
  workflow: string
  repo: string
  ref: string
  unauthorized?: boolean
  detail: string
}

/** Mission Control container half of ApplyCoreUpdateResult (ADR-099 D8.2):
 * the console-mediated, cross-repo dispatch of agentfleet-infra's
 * deploy-mc.yml, which redeploys THIS fleet's MC. `dispatched` is false —
 * never surfaced as an HTTP error — when the console's GitHub token isn't
 * yet authorized for that repo (`unauthorized: true`) or any other failure
 * occurred; `detail` is always a diagnosable message for the operator. */
export interface MissionControlApplyResult {
  dispatched: boolean
  held?: boolean
  unauthorized?: boolean
  detail: string
}

/** Result of POST /api/deploy/apply (ADR-099 P3.2/P3.3/D8.2/D8.3 — the
 * operator-gated "apply latest core" action). 202 on success; reports BOTH
 * halves of "apply the latest core to this fleet" — the agent-host redeploy
 * and the Mission Control container redeploy — so the caller never mistakes
 * one updating for both updating. As of D8.3 BOTH halves are dispatched by
 * the console (over the same X-Fleet-Key channel) and neither one fails the
 * whole request on its own — check `agent_host.dispatched` and
 * `mission_control.dispatched` independently; each carries its own
 * `unauthorized`/`detail` for the "needs authorization" state. */
export interface ApplyCoreUpdateResult {
  agent_host: AgentHostApplyResult
  mission_control: MissionControlApplyResult
}

/** One commit between this fleet's deployed core SHA and core main
 * (ADR-099 D7 changelog entry, relayed verbatim by GET /api/core-status). */
export interface CoreChangelogEntry {
  sha: string
  subject: string
}

/**
 * Result of GET /api/core-status (ADR-099 D7/D8) — the "Core Updates" nav
 * badge, persistent banner, and Updates page all derive from this one call.
 *
 * `available` is false whenever the MC backend couldn't reach the console
 * (network error, this fleet's MC not yet provisioned with FLEET_KEY, a
 * non-200 upstream) — the remaining fields are then meaningless and MUST NOT
 * be treated as "up to date." Callers should render a neutral "status
 * unavailable" state rather than either an update banner or a false
 * all-clear.
 */
export interface CoreStatus {
  available: boolean
  reason?: string
  hold?: CoreStatusHold
  deployed_sha?: string
  latest_sha?: string
  update_available: boolean
  commits_behind: number
  changelog?: CoreChangelogEntry[]
  dormant_capabilities?: string[]
  checked_at?: string
}

/** Deploy hold state reported by GET /api/core-status. */
export interface CoreStatusHold {
  state?: 'none' | 'frozen' | 'pinned' | 'unknown'
  reason?: string
  set_by?: string
  set_at?: string
  revision?: number
  core_sha?: string
  [key: string]: unknown
}

// ADR-028 Phase 2 — the Feedback hub (GET /api/fleet/feedback)
export interface FeedbackByAgent {
  agent: string
  count: number
  avg_stars: number
}
export interface FeedbackReaction {
  grader_id: string
  target_agent: string | null
  stars: number
  reaction: string | null
  artifact_ref: string | null
  created_at: string
}
export interface FeedbackRetro {
  participant: string
  retro_date: string
  good: string[]
  not_good: string[]
}
export interface FeedbackTheme {
  label: string
  occurrence_count: number
  sentiment_positive: number
  sentiment_negative: number
}
export interface FleetFeedback {
  fleet_id: string
  generated_at: string
  grades: {
    count_24h: number
    avg_stars_24h: number | null
    reactions_24h: number
    distribution: Record<string, number>
    by_agent: FeedbackByAgent[]
  }
  reactions: FeedbackReaction[]
  retros: FeedbackRetro[]
  themes: FeedbackTheme[]
  changes: {
    self_edits: { agent: string; title: string; status: string; pr_url: string | null; created_at: string }[]
    improvements: { owner_agent: string; verbatim: string; status: string; pr_url: string | null }[]
  }
}

export interface ActivityEvent {
  id: string
  type: string
  ts: string
  actor: string
  summary: string
  ref: string | null
}

export interface Approval {
  id: string
  agent_id: string
  action_type: string
  title: string
  body: string
  status: string
  created_at: string
  expires_at: string | null
  responded_by: string | null
  responded_at: string | null
  grade: number | null
}

/** An operator-improvement signal (ADR-026 WS3) returned by GET /api/improvements. */
export interface Improvement {
  id: string
  owner_agent: string
  operator: string | null
  source?: string | null
  verbatim: string
  context: string | null
  status: string
  pr_url: string | null
  outcome: string | null
  created_at: string
  updated_at: string
  conversation_id?: string | null
  operator_note?: string | null
  /**
   * ADR-122 D5 — why the last status check could not read this change's PR
   * (e.g. `cannot_read_pr:roofmaxx/agentcore-maxximus`). Null/absent means
   * `status` was confirmed against GitHub; when set, `status` is the last state
   * we could confirm rather than necessarily the current one.
   */
  status_error?: string | null
  /**
   * ADR-122 D3 — where the `status` above came from on THIS request:
   *  - `live`   — resolved against GitHub just now (PR state, and for a merged
   *               PR whether a successful deploy contains its merge commit).
   *  - `stored` — GitHub could not be reached, or the row fell past the
   *               resolution cap, so `status` is the cached value.
   *  - `n/a`    — nothing to resolve against (no PR yet, or terminal).
   * Absent when the backend has no GitHub client configured.
   */
  status_source?: 'live' | 'stored' | 'n/a'
  /**
   * What GitHub says about this change's PULL REQUEST, which is a narrower
   * claim than `status` and decided by a different party.
   *
   * `status` is the lifecycle, and only a person may move it to `declined`;
   * `pr_state` is whether the pull request is still open, which is GitHub's to
   * say. Live resolution has always established this and then discarded it, so
   * a PR closed without merging left its row reading `proposed` with no way for
   * any surface to know — athena's imp_POlPvXpIIhZb_A sat that way for four
   * days, offering its owner no control to settle it.
   *
   * ABSENT MEANS NOT ESTABLISHED, never "closed": the row fell past the
   * resolution cap, GitHub could not be reached, or no GitHub client is
   * configured. Treating absence as a state is how this defect gets rebuilt.
   */
  pr_state?: 'open' | 'merged' | 'closed_unmerged' | 'none'
  /** Whether the linked repository belongs to this fleet. */
  owned_by_this_fleet?: boolean
}

/**
 * The improvement reconciler's own liveness, served beside the rows it advances.
 *
 * WHY A BOARD NEEDS THIS. The stored `status` is a cache, and the reconciler
 * sweep is its only writer — so a wall of `proposed` changes means one thing if
 * the sweep ran four minutes ago and something else entirely if it has not run
 * since July. It had not: the sweep died on athena and matthaios in late July
 * 2026 and nobody knew until a fleet owner reported, on 2026-09-18, that the
 * workflow editor would not let them propose. athena had 153 rows frozen at
 * `proposed` and 17 workflows locked behind them.
 *
 * `last_run_at` vs `last_ok_at` carries the distinction that was missing: a
 * sweep that wakes every ten minutes and fails to resolve a credential bumps the
 * first and not the second.
 *
 * ABSENT OR NULL MEANS THE SWEEP HAS NEVER REPORTED — either it has never run,
 * or this fleet is on an image that predates the record. Both are findings. A
 * consumer that treats a missing signal as a healthy one rebuilds the defect.
 */
export interface ReconcilerHealth {
  status: 'ok' | 'no_token' | 'disabled' | 'error'
  detail: string | null
  last_run_at: string
  last_ok_at: string | null
  checked: number
  advanced: number
}

/**
 * GET /api/activities/:agent/:name/pending-proposal — would proposing a change
 * to this workflow open a SECOND pull request for it?
 *
 * One question, answered from one live read of the one pull request that could
 * be duplicated. The editor used to work this out in the browser from the
 * improvements index, which cannot support it: that list's `status` filter is
 * applied to the stored column and then overwritten by live resolution, and
 * resolution itself stops after the newest 25 rows fleet-wide.
 */
export interface PendingProposal {
  /** Whether a propose right now would duplicate an open pull request. */
  pending: boolean
  /**
   * Why. `pr_open` is the only reason that reflects a real conflict;
   * `unresolved`/`no_pr_url` are pending because the answer could not be
   * established, which must not read as permission.
   */
  reason: 'pr_open' | 'pr_closed' | 'pr_merged' | 'no_row' | 'no_pr_url' | 'unresolved'
  improvement_id?: string
  pr_url?: string | null
  pr_number?: number
  pr_state?: 'open' | 'merged' | 'closed_unmerged' | 'none' | 'unknown'
  context?: string | null
  verbatim?: string
}

// ---------------------------------------------------------------------------
// In-MC PR review + Approve & Deploy (GET/POST /api/improvements/:id/pr|approve|deny)
// ---------------------------------------------------------------------------

/** ADR-160 D2 — whether a copilot turn is already running for a workflow slot. */
export interface CopilotInFlight {
  running: boolean
  /** True when the CALLER started it. Only then may the client re-attach: adopting
   *  another operator's turn would drop their draft into this editor. */
  mine: boolean
  /** Present only when `mine` — the handle to poll. */
  job_id?: string
  /** Who started it, so a second operator is told who rather than merely that it is busy. */
  operator?: string
  elapsed_s?: number
  started_at?: string
}

/** `unreadable` is NOT `none`. A 403 on the checks read means Mission Control
 *  could not look; `none` means it looked and there was nothing. spartan #939
 *  rendered the first as the second — "waiting for them to start" over a red
 *  `validate`, polled every 5s, unable ever to change. */
export type ImprovementPRChecks =
  | 'passing'
  | 'failing'
  /** ADR-130 D2's retryable bucket — cancelled / timed out / startup failure /
   *  awaiting approval. The backend has returned this since the D2 split and this
   *  union never listed it, so it fell through every comparison to the same
   *  in-progress rendering `none` got: a state that blocks the merge, displayed as
   *  one that is still working on it. */
  | 'stalled'
  | 'pending'
  | 'none'
  | 'unreadable'

export interface ImprovementPRFile {
  path: string
  status: string
  additions: number
  deletions: number
  patch: string
}

export interface ImprovementPRCheckRun {
  name: string
  status: string
  conclusion: string
  url: string
}

/**
 * One CHECK as an operator reads it, rather than one execution: the newest
 * execution's answer, how many older executions it replaced, and those older
 * executions under `history`.
 *
 * The four original `ImprovementPRCheckRun` fields carry the LATEST execution,
 * so a caller that reads only name/status/conclusion/url sees the current
 * answer and nothing else. `superseded` is 0 and `history` absent when the
 * check ran once; both are absent on an older backend, which is the only
 * reason `superseded` is optional here.
 *
 * `history` holds ImprovementPRCheckRun, not the group: an execution behind
 * the current one has no executions of its own to count or carry.
 */
export interface ImprovementPRCheckRunGroup extends ImprovementPRCheckRun {
  superseded?: number
  history?: ImprovementPRCheckRun[]
}

/**
 * Has anything on this change been checked since the fleet itself changed?
 *
 * Three states, never two. `unknown` is an answer of its own — there are no
 * check runs, the read was refused, the base commit carries no date, or the
 * change is no longer open — and rendering it as `checked_since` would report
 * silence as success.
 *
 * `not_checked_since` is a NECESSARY condition for staleness, never a
 * sufficient one: it clears as soon as any check runs, including a check that
 * itself tested an older fleet. Mission Control cannot know which base a
 * completed check run tested, so the converse must never be claimed.
 *
 * `reason` is operator language and is empty for a determined state.
 *
 * Three strings and nothing else. The commit and the two timestamps the
 * comparison is computed FROM are deliberately not in the response: the
 * operator reads the answer, not the arithmetic.
 */
export interface ImprovementPRBaseState {
  state: 'checked_since' | 'not_checked_since' | 'unknown'
  base_ref: string
  reason: string
}

/**
 * What this Mission Control knows about its OWN ability to re-run a check run,
 * declared by the credential selected at startup rather than inferred from a
 * successful Actions read — that read runs on a different credential.
 *
 * `unavailable` means the grant is known to be absent (a broker fleet holds no
 * `actions` grant at all), so the control must not be offered. `unknown` means
 * the grant table is not held here, and the control stays offered because
 * hiding it would remove working function.
 */
export type ImprovementPRChecksRerun = 'available' | 'unavailable' | 'unknown'

/** Result of POST /api/improvements/:id/pr/rerun — a 200 only. A re-run where
 *  every eligible run was declined is a 409 `checks_rerun_unavailable`, so a
 *  200 always started at least something OR had nothing to start. */
export interface RerunImprovementPRChecksResult {
  /** How many check runs were started again. */
  rerun: number
  /** How many the server tried to start and could not. Greater than zero makes
   *  this a partial re-run, which the page must not report as a clean one. */
  refused: number
  /** The commit the re-run was aimed at. */
  head_sha: string
  /** Operator sentence for a partial re-run. Present only when `refused > 0`. */
  detail?: string
}

/** Result of POST /api/improvements/:id/pr/update-branch — a 200 only. Every
 *  refusal is an error status with a coded body (`core_owned`, `not_open`,
 *  `head_elsewhere`, `head_moved`, `update_refused`), so there is no
 *  not-accepted success shape to render. */
export interface UpdateImprovementPRBranchResult {
  /** GitHub accepted and SCHEDULED the update. It is not done, and no new head
   *  commit exists yet — whether the checks have been re-created is a separate
   *  question the page has to poll for. A literal `true`: the server returns
   *  this field only on the accepted path. */
  accepted: true
  /** The head commit the update was AIMED at — the one that was sent. Never a
   *  new head. */
  head_sha_at_request: string
}

/**
 * The real Deploy Fleet (deploy-fleet.yml, on main) workflow-run result for a
 * merged improvement PR (ADR-110 Layer B) — present only once the PR is
 * merged; null/absent otherwise or when it couldn't be determined (never a
 * reason to fail the whole PR view).
 *
 * `status` is the GitHub Actions run status: 'queued' | 'in_progress' |
 * 'completed' | 'none' (no run found yet at all — e.g. right after a promote,
 * before the workflow has started).
 *
 * `conclusion` is populated once `status === 'completed'`: 'success' |
 * 'failure' | 'cancelled' | 'timed_out' | 'action_required' | 'skipped' |
 * 'neutral' | 'stale'; empty otherwise.
 *
 * `includes_change` is true only when this run's commit is (or descends
 * from) the improvement's own develop-merge commit — false means the most
 * recent deploy run predates this specific change (or ancestry couldn't be
 * resolved), so its conclusion must NOT be read as this change's result.
 */
export interface DeployInfo {
  status: string
  conclusion: string
  url: string
  sha: string
  includes_change: boolean
}

/** The live GitHub PR behind an improvement signal (GET /api/improvements/:id/pr). */
/** One row on the ADR-167 in-flight index — a piece of work, or a source that could
 *  not be read. `kind: 'unreadable'` always carries `reason`. */
export interface InFlightRow {
  card_id?: string
  board?: string
  waiting_event_id?: number
  can_control?: boolean
  case_owner?: string
  allowed_actions?: string[]
  kind: string
  subject: string
  detail?: string
  state: string
  age_s: number
  beat_age_s?: number
  stale?: boolean
  owner?: string
  href?: string
  action?: string
  /** A condition worth knowing about an otherwise-healthy row — chiefly that a deploy
   *  is landing WHILE other work runs, so a result read now may span two versions. */
  warn?: string
  reason?: string
  /** ADR-200 D6 — set on a row PARKED ON A PERSON rather than running: a run whose brief
   *  waits for someone to confirm (`brief_confirmation`) or amend (`brief_amendment`) it.
   *  Counted in `waiting`, never in `working`. */
  waiting_for?: 'brief_confirmation' | 'brief_amendment' | string
  /** Keyed on Mission Control's machine identity — started by a trigger, in nobody's editor. */
  unattended?: boolean
  /** Whether THIS reader may confirm the brief from the in-flight view. */
  decidable?: boolean
  run_agent?: string
  run_workflow?: string
  run_operator?: string
}

export interface InFlightResult {
  rows: InFlightRow[]
  working: number
  /** ADR-200 D6 — runs parked on a person, counted apart from `working`. Absent from a
   *  Mission Control older than D6. */
  waiting?: number
  unreadable: number
  not_configured: number
  stale_after_s: number
  observed_at: string
}

/** ADR-166 — the phase state envelope. The run's own shape lives in
 *  components/AuthoringRail.tsx alongside the code that renders it; importing it here
 *  would make the client depend on a component, so the field is typed at the seam. */
export interface AuthoringRunResult {
  run: import('../components/AuthoringRail').AuthoringRunState | null
  /** TRUE means this instance has no session store, which is NOT the same as having no
   *  run. Kept distinct for the reason D3 keeps "unreadable" distinct from "empty". */
  not_configured?: boolean
  detail?: string
  pending_checkpoint?: import('../components/AuthoringRail').Checkpoint | null
}

/**
 * ADR-177 D1 — a row of the run history.
 *
 * `closed_at` is ABSENT, not zero, while a run is still going. A zero timestamp renders
 * as 1970 in every client that forgets to check, and "when did this end" is the first
 * thing a reader of a list asks.
 */
export interface FleetFileResult {
  path: string
  content: string
  found: boolean
  core_owned?: boolean
}

export interface AuthoringRunSummary {
  id: string
  agent: string
  workflow: string
  operator: string
  task: string
  /** '' while in flight. Otherwise complete | refused | no-change | awaiting-decision | timed-out. */
  outcome: string
  phase: string
  mc_version: string
  /** ADR-175: the model this run REQUESTED. An intention, not a measurement. */
  model: string
  /** ADR-183: the vendor, by the model catalog's own slug. '' when not recorded. */
  provider: string
  /** ADR-194: WHICH LAYER chose this run's model — request | operator | fleet |
   *  platform. '' for a run older than the field.
   *
   *  A run on gpt-6-astra because the FLEET pinned it is different evidence from
   *  the same model someone picked by hand, and only this field tells them apart. */
  model_source?: 'request' | 'operator' | 'fleet' | 'platform' | ''
  /** ADR-194 D5: a pinned model could not be called and the deployment default
   *  answered instead. Such a run is not evidence about the pinned model. */
  model_substituted?: boolean
  /** ADR-183: the model the vendor said ANSWERED.
   *
   *  Separate from `model` and never derived from it. An alias resolves to a dated
   *  snapshot (`claude-sonnet-5` → `claude-sonnet-5-20260114`) and a vendor can
   *  substitute under load, so where the two differ, the difference IS the finding.
   *  '' means nothing measured it — a turn answered by the Architect (attributed on
   *  the fleet side by turn id), or any run from before attribution shipped. It
   *  renders as unknown, NEVER as the current default: a row claiming a model nobody
   *  measured is indistinguishable from one that was. */
  response_model: string
  opened_at: string
  closed_at?: string
  attempts: number
  refusals: number
  /** ADR-178 D3 — the pull request this run PRODUCED, and where to approve it.
   *
   *  `improvement_id` is the one a surface links: ADR-079 approvals never require
   *  GitHub, so it deep-links Mission Control's own /improvements/:id/pr. `pr_url`
   *  is retained for audit and is deliberately not offered as a GitHub link.
   *
   *  All absent/zero when the run recorded no proposal — still going, failed to
   *  propose, or older than the column. Never inferred. */
  pr_number?: number
  pr_url?: string | null
  improvement_id?: string | null
}

export interface AuthoringRunHistory extends AuthoringRunSummary {
  scope: string
  /** The engine's own run document — what the tree renders from, and the truth where it
   *  and the scalar columns disagree. */
  run: import('../components/AuthoringRail').AuthoringRunState | null
}

export interface AttributionTableCoverage {
  table: string
  rows: number
  attributed: number
  unattributed: number
  /** NULL when the table has no rows in the window.
   *
   *  An empty window is neither attributed nor unattributed — there is nothing to
   *  attribute — so a number here would be read as a verdict either way: 100
   *  would render a quiet hour as green, 0 would raise an alarm about nothing. */
  percent: number | null
  /** 'live' when something writes these columns, 'absent' when nothing does. */
  writer: 'live' | 'absent'
  /** Why nothing writes it. Present only when writer === 'absent', and it is what
   *  keeps a future regression in a WORKING writer from being indistinguishable
   *  from the status quo — which is 0%. */
  note?: string
}

export interface AttributionModelCount {
  provider: string
  model: string
  rows: number
}

export interface AttributionCoverage {
  since: string
  window_hours: number
  tables: AttributionTableCoverage[]
  by_model: AttributionModelCount[]
  /** How many attempts in the window carry no model. Stated rather than left to
   *  be derived: a caller comparing two models needs to know how much of the
   *  window it cannot see, or a small attributed sample reads like the whole
   *  story. */
  unattributed_attempts: number
}

export interface AttributionCoverageResult {
  coverage: AttributionCoverage | null
  /** This Mission Control has no Postgres, so it records none of this. Distinct
   *  from a report of zero coverage, which is a much more alarming claim. */
  not_configured?: boolean
  detail?: string
}

export interface AuthoringRunListResult {
  runs: AuthoringRunSummary[]
  /** Distinct from an empty list: "this Mission Control does not archive runs" is not
   *  "this fleet has authored nothing", and rendering the first as the second is the
   *  RefChecksStatus failure that showed "waiting for checks" over a red PR. */
  not_configured?: boolean
  detail?: string
}

export interface AuthoringRunDetailResult {
  run: AuthoringRunHistory | null
  not_configured?: boolean
  detail?: string
}

export interface ImprovementPR {
  number: number
  title: string
  state: string // open|closed
  merged: boolean // true when a closed PR was merged (vs closed unmerged)
  html_url: string
  mergeable: boolean | null
  base_ref: string
  head_ref: string
  head_sha: string
  checks: ImprovementPRChecks
  files: ImprovementPRFile[]
  check_runs: ImprovementPRCheckRunGroup[]
  /** Has anything here been checked since the fleet changed. Absent on an
   *  older backend, which then behaves as before. */
  base_state?: ImprovementPRBaseState
  /** Whether this Mission Control can re-run a check run at all. Absent on an
   *  older backend; treat an absent value as `unknown`, never as unavailable. */
  checks_rerun?: ImprovementPRChecksRerun
  /** ADR-110 Layer B — see DeployInfo. Absent/null unless `merged` is true. */
  deploy?: DeployInfo | null
  /** Whether Mission Control's fleet author-test gate checked this proposal. */
  author_tests?: { verdict: 'passed' | 'inconclusive'; reason?: string } | null
  /**
   * ADR-122 D-core-read — which repo this change's PR lives in, and whether it
   * is this fleet's to act on.
   *
   * A CORE change (`owned_by_this_fleet: false`) is READABLE but not actionable
   * here: it fans out to every fleet, is reviewed by the platform, and is never
   * merged from a fleet (ADR-054, ADR-103). Before this the fleet could not even
   * see it — the read was refused with a repo-mismatch error.
   *
   * This is a fact about the CHANGE, not a permission the client holds: Approve
   * and Deny enforce the identity repo server-side regardless of what is
   * rendered. Absent on an older backend, which then behaves as before.
   */
  repo?: string
  owned_by_this_fleet?: boolean
}

/** Result of POST /api/improvements/:id/approve. */
export interface ApproveImprovementPRResult {
  merged_to_develop: boolean
  develop_merge_sha: string
  promoted: boolean
  main_merge_sha: string
  promote_pr_url: string
  queued: boolean
}

/**
 * GET /api/workflows/pending (ADR-114 D2) — whether this fleet's `develop`
 * is ahead of `main`, i.e. whether /workflows' main-tracking checkout
 * (ADR-100) is missing something from ANY source (a merged-but-unpromoted
 * improvement, or an older stranded direct-to-develop commit).
 * `configured` is false when this fleet has no GitHub integration — the
 * banner should stay hidden rather than render a false "not pending".
 */
/** One real (non-merge) change waiting to go live — ADR-122 D8. */
export interface PendingChange {
  summary: string
  sha: string
  /** True when the PLATFORM pushed this into the fleet (an ADR-040 canonical
   * reconcile) rather than anyone here making it. */
  platform_sync: boolean
}

export interface WorkflowsPendingStatus {
  configured: boolean
  pending: boolean
  /** RAW commit count, merge commits included. Kept for compatibility — do not
   * show it to an operator; use `change_count`. */
  pending_count: number
  /** Real changes waiting, merge commits excluded (ADR-122 D8). Optional so an
   * older backend degrades instead of rendering NaN. */
  change_count?: number
  /** What is actually waiting, newest first (capped server-side; the count
   * above stays exact). */
  changes?: PendingChange[]
  develop_sha: string
  main_sha: string
}

// ---------------------------------------------------------------------------
// The Ready worklist — GET /api/changes/pending (ADR-122 D1a/D1c/D1d)
//
// WorkflowsPendingStatus above answers "is something waiting?" with a repo-wide
// commit count. D8 replaces it: the same question, answered with NAMED Changes
// carrying provenance, so an operator reads "Updated skill brand-application,
// proposed by designer" instead of "2 changes staged on develop".
// ---------------------------------------------------------------------------

/** What a Change touched, named in operator language rather than by path. */
export interface ChangeArtifact {
  kind: string
  /** Operator language, never a path. */
  name: string
  /** The owning profile where one applies. */
  agent?: string
  paths: string[]
  correlation_key?: string
}

/**
 * One Change: a single fleet modification in operator language. `origin`
 * distinguishes a pull request from a commit pushed straight to `develop` —
 * both are surfaced, because per D5 silence about a change is a defect, so
 * "skip what cannot be attributed" would be the bug.
 */
export interface PendingChangeItem {
  title: string
  origin: 'pull_request' | 'direct_commit'
  pr_number?: number
  author?: string
  artifacts: ChangeArtifact[]
  paths: string[]
  /** Set only where D1d permits cross-fleet correlation. */
  correlation_key?: string
}

export interface PendingChangesStatus {
  configured: boolean
  /** True when anything at all waits — including a platform sync with no
   * operator Change. */
  pending: boolean
  /** The operator worklist (D6: what needs attention). */
  changes: PendingChangeItem[]
  /** Canonical platform reconciles, a separate category shown as one Platform update item (ADR-122 D8b); never an operator Change. */
  platform_syncs: PendingChangeItem[]
  /** Generated files that changed with nothing explaining them. */
  anomalies: ChangeArtifact[]
  develop_sha?: string
  main_sha?: string
  /** True when some detail could not be read. The list is still returned —
   * under-reporting silently is the failure ADR-122 exists to end. */
  degraded: boolean
  warnings?: string[]
}

/**
 * GET /api/fleet/pending-paths (ADR-111 D2) — the per-FILE companion to
 * WorkflowsPendingStatus above: WHICH overlay paths differ between `develop`
 * and `main`, not just how many commits are pending. /workflows' banner
 * covers the page-level "something is staged" signal; this is what backs
 * the Fleet Editor's per-artifact "staged — not yet live" badges (an
 * individual skill, workflow, agent file, or resource), since the editor's
 * lists/tree render from the main-tracking /fleet mount (ADR-100) and
 * otherwise have no way to tell WHICH entry is stale. Same `configured`
 * degrade-gracefully contract as WorkflowsPendingStatus.
 */
export interface FleetPendingPathsStatus {
  configured: boolean
  pending: boolean
  pending_count: number
  develop_sha: string
  main_sha: string
  /** Repo-relative overlay paths that differ between main and develop (both
   * old and new name for a rename). Empty when nothing is pending. */
  paths: string[]
}

/**
 * POST /api/workflows/deploy result — mirrors ApproveImprovementPRResult's
 * promote fields; this endpoint promotes develop→main directly (no PR to
 * merge first), for changes with no PR to review on /improvements.
 */
export interface DeployWorkflowsPendingResult {
  promoted: boolean
  main_merge_sha: string
  promote_pr_url: string
  queued: boolean
}

/**
 * GET /api/promotion/status (ADR-130 D4) — the resolved state of an in-flight
 * develop→main promotion.
 *
 * This exists because the Ready worklist used to render a local `applied`
 * boolean that was set once and never reset: a promotion stalled for 18 hours
 * still showed "Taking these live now" directly above the unchanged list of
 * changes that had not shipped. `phase` is resolved from GitHub by the
 * promotion reconciler, so the banner can only say what is actually true.
 */
export type PromotionPhase =
  | 'idle'
  | 'awaiting_checks'
  | 'merging'
  | 'deploying'
  | 'live'
  | 'failed'

export interface PromotionStatus {
  phase: PromotionPhase
  /** Machine-readable outcome (e.g. `develop_red`, `checks_never_reported`). */
  code: string
  /** One sentence of operator language, safe to render directly. */
  reason: string
  pr_number?: number
  pr_url?: string
  /** How many self-healing remedies the platform has applied so far. */
  attempts: number
  in_flight: boolean
  /** A read partially failed; the phase is best-effort. */
  degraded: boolean
  /** What the platform did, unattended. Shown only on a failure. */
  remedy_log?: string[]
}

/**
 * One "exact problem in the code" item under a FailingCheck, populated from
 * GitHub check-run annotations when present, else an excerpt extracted from
 * the failing job's log (see checks_failing.go, backend). `path`/`line` are
 * absent when no file:line could be resolved — render `title`/`message`
 * alone then. `message` is always populated when this item exists.
 */
export interface CheckFailureDetail {
  path?: string
  line?: number
  title?: string
  message: string
}

/**
 * One failing/blocking check run for a PR's head SHA (GET
 * /api/improvements/:id/pr/checks, and echoed on a 409 `checks_failing`
 * approve response). `details` is always an array (never null/undefined) —
 * empty means the check is failing but its detail couldn't be fetched (a
 * token permission gap); render `name`/`conclusion` + the `details_url`
 * link alone then.
 */
export interface FailingCheck {
  /** Stable identity for the row — the Actions job id for a `job` row, the run
   *  id for a `workflow` or `unenumerable` one. Absent on the author-tests
   *  payload. Two distinct runs of one workflow at a SHA carry the same
   *  `name`, so this is what the panel keys on. */
  id?: number
  /** `job` for a real failing Actions job, `workflow` for a completed run that
   *  failed with none of its enumerated jobs failing, `unenumerable` for a run
   *  whose jobs could not be listed at all. Absent on the author-tests payload,
   *  which renders as a plain check. Nothing here is described as *required*:
   *  requiredness needs `checks: read`, which this route does not have. */
  kind?: string
  name: string
  conclusion: string
  details_url: string
  details: CheckFailureDetail[]
}

/**
 * How complete a per-check detail read was, mirroring the backend's
 * `DetailState` (see checks_failing.go). ADR-122 D3a's "fail open, and say so"
 * one level down: a truncated enumeration must never render as a complete one,
 * and a refused read must never render as "nothing is failing".
 */
export type DetailState = 'complete' | 'partial' | 'unreadable'

/** GET /api/improvements/:id/pr/checks. `detail_state`/`detail_message` are
 *  absent on older backends; treat an absent state as `complete`. */
export interface FailingChecksResponse {
  checks: FailingCheck[]
  detail_state?: DetailState
  detail_message?: string
}

export interface AuditEntry {
  event: string
  actor: string
  metadata: Record<string, unknown> | null
  created_at: string
}

export interface ModuleSummary {
  module_id: string
  title: string
  icon: string | null
  version: string
}

// ---------------------------------------------------------------------------
// Response types — ADR-075 Track 1 Phase 5a (Workflows viewer, read-only)
// ---------------------------------------------------------------------------

/** One agents/<agent>/activities/*.yaml file, from GET /api/activities. */
export interface ActivitySummary {
  agent: string
  name: string
  /** The ADR-075 dsl marker string ("agentfleet/serverless-workflow/v1"), or
   * "" for a legacy stages/steps activity. */
  dsl: string
  version?: string
  triggers?: string[]
  /** ADR-103: true when this activity's agent is core-owned — the Workflow
   * Studio hides edit/delete/convert and shows a read-only badge instead. */
  core: boolean
  /** `run.workflow` composition edges (ADR-075 Track 1 Phase 3): the workflows this
   * one invokes as sub-steps, and the ones invoking it. Absent when empty.
   *
   * Reuse that is invisible gets re-implemented by copy-paste — the duplication the
   * feature exists to prevent. And `called_by` is the warning owed BEFORE editing or
   * retiring a shared workflow: a `run.workflow` reference hard-blocks its delete
   * (ADR-123 D5). */
  calls?: string[]
  called_by?: string[]
  /** Input names this activity cannot run without — no `default:`, so a caller
   * MUST supply a value. Absent when empty.
   *
   * A cron supplies no values unless it declares `inputs:`, and a required input
   * with no value fails inside instantiateActivity BEFORE dispatch: no
   * conversation, no run history, silently, on every tick. The cron panel needs
   * this to refuse a cron it can already tell will never fire. */
  required_inputs?: string[]
  /** ADR-134 D8 — TWO DIFFERENT FACTS, and collapsing them misreports both.
   *
   * `draft`: this row is an UNPUBLISHED editor session. There is no file behind it,
   * so before D8 it could not appear in this list at all — the catalog lists files,
   * and a workflow being authored for the first time has none. That is what made a
   * new draft reachable only by retyping its exact name into the create flow.
   *
   * `has_draft`: this row IS a published activity that ALSO has unproposed work in
   * a session. The workflow is live; someone has a change in flight on it.
   *
   * A `draft` row carries NO `dsl`, `version` or `triggers` — the server leaves them
   * empty on purpose, because nothing has been proposed and the catalog must not
   * report a shape the fleet does not have. So the renderer must branch on `draft`
   * BEFORE it reads `dsl`: `item.dsl ? 'dsl' : 'legacy'` would badge every draft
   * "legacy", which is a claim about a file that does not exist. */
  draft?: boolean
  has_draft?: boolean
  /** RFC3339. Describes the session, not the file. */
  draft_updated_at?: string
  /** Whose draft. Carried because a draft is per-operator (D5): a row with no name
   * beside it invites the reader to assume it is theirs. */
  draft_operator?: string
  /** ADR-134 D10 — how many operators hold a draft here; `draft_operator` and
   * `draft_updated_at` describe the NEWEST of them.
   *
   * Ordinary once the read is fleet-wide, and worth rendering when >1: the row is ONE
   * row because the workflow's path is its identity, so two drafts of it cannot both
   * become that file — they collide at propose. Absent (not 0) when there are none. */
  draft_count?: number
}

/** Server-side validation result for one activity (GET /api/activities/:agent/:name). */
export interface ActivityValidation {
  ok: boolean
  errors: string[]
  /** The validator's third channel, carried here for the same reason it is on
   *  `ValidateActivityResult`: this verdict is rendered by ValidationPanel too
   *  (`Workflows.tsx:4174`) and is seeded into the editor's draft verdict
   *  (`:2778`, `:2887`), so dropping it here loses the finding on a workflow
   *  opened from disk rather than typed. */
  pending?: string[]
  /** Set instead of a real result when the canonical validator (python3 +
   * scripts/validate-activities.py) isn't available in this environment —
   * the read never fails outright just because validation couldn't run. */
  note?: string
}

/** GET /api/activities/:agent/:name — raw file + parsed JSON + validation. */
export interface ActivityDetail {
  yaml: string
  /** The activity YAML parsed to JSON (arbitrary shape — dsl `do` graph or
   * legacy stages/steps). The frontend builds the node graph from this. */
  activity: unknown
  validation: ActivityValidation
}

// ---------------------------------------------------------------------------
// Response types — ADR-079 Phase 5b (Workflow Studio: validate + propose)
// ---------------------------------------------------------------------------

/** One file in an ADR-134 D1 propose changeset. */
export interface ProposedChangesetFile {
  path: string
  /** Required for a write; ignored for a delete. */
  content?: string
  /** "write" (default) or "delete". A RENAME is write-new + delete-old in ONE changeset. */
  op?: 'write' | 'delete'
}

/** ADR-134 D4 — why the copilot's repair loop could not converge. */
export interface CopilotBlocker {
  errors: string[]
  /** The files those errors implicate. */
  files: string[]
  /** True when the fix lies beyond this change — not the operator's to make here. */
  outside: boolean
  attempts: number
}

/** ADR-134 D5 — a durable Fleet Editor session: the changeset plus the reasoning
 *  that produced it. */
export interface EditorSession {
  agent: string
  workflow: string
  /** ADR-134 D10 — whose draft this is. Serialized only since D10 made the read
   *  fleet-wide; before that the only session you could fetch was your own, so it said
   *  nothing. Compare it with the signed-in user: if it differs, you are looking at a
   *  colleague's changeset and the editor must stay READ-ONLY (owner's decision,
   *  2026-09-14: visibility, not co-edit — and not a fork either). */
  operator?: string
  draft_id?: string
  files: ProposedChangesetFile[]
  transcript: { role: string; content: string }[]
  validation?: ValidateActivityResult
  gate?: unknown
  /** The develop SHA this was last validated against. When develop has moved the
   *  session is not wrong, but it is no longer proven. */
  base_ref?: string
  /** The catalog conversation this transcript belongs to. Absent on sessions written
   *  before the link existed — "unknown", never "new". */
  conversation_id?: string
  updated_at: string
}

/** ADR-150 D3/D5 — one thing a SOUL draft does to the core layers above it.
 *  `blocking` is carried on the finding rather than derived from `kind`, so the
 *  editor and the propose gate cannot disagree about what stops a proposal. */
export interface SoulFinding {
  kind: 'shadow' | 'restatement'
  section: string
  draft_line: number
  draft_text: string
  /** The core file and line this conflicts with. ADR-150 D4 permits a BLOCK only
   *  when it can cite one, so these are populated on every blocking finding. */
  core_source: string
  core_line?: number
  core_text?: string
  blocking: boolean
}

/** ADR-150 D2 — one SOUL authoring turn. Mirrors SkillCopilotResponse; the
 *  difference that matters is `review`: the turn is repaired against the SAME
 *  override check the propose gate runs, so a draft that comes back here is a
 *  draft the gate will accept. */
export interface SoulCopilotResponse {
  /** The background job that produced this turn, attached by pollLLMJob. */
  job_id?: string
  draft: string
  rationale: string
  review: SoulReviewResult
  model: string
  attempts: number
  /** Prose to READ rather than a draft to apply — the engine asked a question. */
  answer?: string
  /** False for every answer and every failure, so a turn that produced no
   *  document can never blank the operator's in-progress SOUL. */
  draft_changed: boolean
  engine?: string
}

export interface SoulReviewResult {
  findings: SoulFinding[]
  /** Set when the review could NOT run. Never treat this as "clean" — a review
   *  that cannot read the rules reporting no objections is the failure ADR-149
   *  D2b named. */
  unavailable?: string
  governed_sections: string[]
  core_layers: string[]
}

  /** ADR-137 — a row in the assistant's conversation catalog. Deliberately
 *  without the messages: a slot's whole history would otherwise be fetched to
 *  render a list of titles, and the transcripts are the large part. */
export interface ConversationSummary {
  id: string
  title: string
  turns: number
  /** Who STARTED this conversation (ADR-137 D8) — what the author filter filters
   *  on, and what a shared catalog shows so a row says whose thinking it was.
   *  Not "who last spoke in it": D9 lets anyone continue anyone's, and per-turn
   *  authorship rides on the messages (D10). */
  operator: string
  /** The PR this conversation produced, when it produced one (ADR-137 D3). */
  pr_number?: number
  created_at: string
  updated_at: string
}

/** A resumed conversation. Carries the transcript and NOTHING a caller could
 *  restore over the draft — ADR-137 D2: resuming changes which conversation is
 *  active, never the artifact. */
export interface EditorConversation {
  id: string
  title: string
  /** ADR-137 D10 — `author` is who wrote an operator turn. Absent on assistant
   *  turns and on turns written before the transcript could be shared. Assigned
   *  server-side from the JWT and never accepted from a client, so a save cannot
   *  move or clear an existing turn's author. */
  messages: { role: string; content: string; author?: string; job_id?: string }[]
  pr_number?: number
  created_at: string
  updated_at: string
}

/** ADR-153 D3 — one `{{vars.*}}` a picker may offer at a task. The token arrives
 *  already spelled for the slot; the client never assembles one. */
export interface VarsOffer {
  name: string
  token: string
  /** The `set` task that bound it, so the operator can see where it came from. */
  bound_by?: string
}

export interface VarsOffers {
  slot: string
  task: string
  offers: VarsOffer[]
  /** Present only when the generator could not run — an empty list WITH a note
   *  is "could not look", which is a different fact from "binds nothing". */
  note?: string
}

/** POST /api/activities/validate — validates an unsaved draft's YAML. */
export interface ValidateActivityResult {
  ok: boolean
  errors: string[]
  /** The validator's third channel: references that are legal but whose reference data is not
   *  committed yet. `ok` follows `errors` ALONE, so a draft with pending findings and no errors
   *  is proposable, and is refused where the publish gate runs — the gate being a fleet's own
   *  deploy-fleet.yml, which not every path that publishes invokes (issue #2120), so this key
   *  does not carry "cannot be published". Optional because a validator that predates the
   *  channel omits the key, and an absent key is "nothing pending", not "unknown". */
  pending?: string[]
  note?: string
}

/** POST /api/activities/:agent/:name/propose — a successful propose. */
export interface ProposeActivityResult {
  pr_number: number
  url: string
  /**
   * The in-app review handle: the improvement id the studio deep-links to at
   * /improvements/:id/pr (Mission Control's own Approve & Deploy). Approvals
   * never require GitHub (ADR-079). Empty when the signal could not be
   * registered, in which case `improvement_warning` carries what happened.
   */
  improvement_id?: string
  /**
   * Set when the change was saved but registering it on the Improvements list
   * did not confirm. The change is NOT reachable from the Improvements list,
   * and nothing derives one from an open pull request, so the UI must render
   * this instead of the success copy rather than alongside it.
   */
  improvement_warning?: string
  registration_state?: RegistrationState
}

// ---------------------------------------------------------------------------
// Response types — ADR-080 (skill -> skill + workflows conversion)
// ---------------------------------------------------------------------------

/** One materialized workflow + its DSL-validation verdict. */
export interface ConversionActivity {
  name: string
  yaml: string
  validation: ValidateActivityResult
}

/** POST /api/activities/convert — plan or materialize (discriminated by `mode`). */
export interface ConvertResult {
  mode: 'plan' | 'materialize'
  /** plan mode: the decomposition plan object (or null if the model failed to produce one). */
  plan?: unknown
  /** materialize mode: the revised (leaner) skill. */
  skill?: { name: string; content: string } | null
  /** materialize mode: the workflows, each with its validation verdict. */
  activities?: ConversionActivity[]
  /** materialize mode: true when every activity passed the DSL gate. */
  all_valid?: boolean
  /** A bounded excerpt of the model's last output, present only when nothing
   * parsed (a null plan, or a materialize that produced no files) — for showing
   * the user why it failed instead of a silent empty result. */
  debug?: string
  model: string
  attempts: number
}

/** POST /api/activities/convert (materialize) — the 202 kickoff ack. The heavy
 * materialize runs as a background job; poll /convert/status/:id for the result. */
export interface ConvertJobAck {
  job_id: string
  status: 'running'
}

/** One convertible skill (GET /api/agents/skills). `slug` is the identifier the
 * convert endpoints expect; `name`/`description` are for display. */
export interface SkillInfo {
  slug: string
  name: string
  description: string
  /** How many `routes:` this skill declares (ADR-094) — 0 for a non-router skill. */
  route_count: number
  /** ADR-103: true when this skill is core-owned — either its agent is a core
   * agent, or its name shadows a universal skill. Read-only in the Studio. */
  core: boolean
  /** Where the skill file came from, when supplied by the server. */
  source?: 'fleet' | 'core'
  /** Manifest bucket that composes this skill into the agent. */
  binding?: SkillBinding
}

/** Which manifest bucket composes this skill name into an agent. */
export type SkillBinding = 'all' | 'role' | 'none'

export interface PlatformSkillAgent {
  agent: string
  role: string
  composed: boolean
}

export interface PlatformSkill {
  name: string
  version: string
  description: string
  path: string
  file_present: boolean
  exclude_roles: string[]
  composed_count: number
  agents: PlatformSkillAgent[]
}

export interface PlatformSkills {
  roster_available: boolean
  roster_size: number
  skills: PlatformSkill[]
}

/** One agent and its convertible skills (only agents with >=1 skill appear). */
export interface AgentSkills {
  agent: string
  skills: SkillInfo[]
  /** ADR-103: true when this agent itself is core-owned — every one of its
   * skills is core-owned regardless of name, and it's excluded from "New
   * skill" authoring pickers (it still appears in read-only browse lists). */
  core: boolean
}

/** One declared skill→workflow link (ADR-094 D1 `routes:`). `workflow` names an
 *  activity under the same agent; `intent` is the human/AI-readable trigger. */
export interface SkillRoute {
  intent: string
  workflow: string
  inputs?: Record<string, string>
}

/** GET /api/agents/:agent/skills/:slug (ADR-094 D6) — one skill's full read
 *  model: raw SKILL.md + parsed frontmatter (incl. routes) + prose body. The
 *  skill-side counterpart to ActivityDetail, backing the skill studio. */
export interface SkillContent {
  agent: string
  slug: string
  name: string
  description: string
  mcp: string[]
  mcp_tools: string[]
  routes: SkillRoute[]
  raw: string
  body: string
  found: boolean
}

/** POST /api/agents/:agent/skills/:slug/validate (ADR-094 D2) — validation
 *  verdict for a SKILL.md draft: structural rules + cross-artifact `routes:`
 *  linkage. `errors` block (dangling workflow, bad structure); `warnings` are
 *  advisory (routes↔triggers drift, orphan activities, ambiguous intents). */
export interface SkillValidation {
  ok: boolean
  errors: string[]
  warnings?: string[]
}

/** POST /api/agents/:agent/skills/:slug/propose (ADR-094 D6) — a successful
 *  single-skill edit propose (one SKILL.md → PR + in-app improvement). */
export interface ProposeSkillResult {
  pr_number: number
  url: string
  improvement_id?: string
  /** Set when the PR opened but the in-app review row failed to register. */
  improvement_warning?: string
  registration_state?: RegistrationState
  /** The paths that were actually committed — a deletion whose target had
   *  already gone is not among them. */
  files: string[]
  /** ADR-107: whether the fleet's own author tests actually ran against this
   *  change. "inconclusive" means they did not — the agent host was
   *  unreachable, or it did not confirm it understood the deletions — so the
   *  operator must not read "Sent for review" as "checked". */
  gate?: 'passed' | 'inconclusive' | 'failed'
  gate_reason?: 'timed_out' | 'unavailable' | 'deletes_unconfirmed'
}

/** One convertible prompt-cron (GET /api/crons/prompt) — a scheduled model turn
 *  that can become a deterministic workflow (ADR-066/ADR-080). */
export interface PromptCron {
  name: string
  agent: string
  schedule: string
  prompt: string
  deliver?: string
  layer: 'fleet' | 'agent'
  file: string
}

/** A cron being authored in MC (create or edit) — ADR-094 ext. */
export interface CronInput {
  layer: 'agent' | 'fleet'
  agent: string
  name: string
  schedule: string
  kind: 'activity'
  activity?: string
  deliver?: string
  /** Literal values for an `activity:` cron's required inputs, by input name.
   * Almost always absent — the convention is that a scheduled entry point takes
   * no arguments. Values are LITERALS; a `{{...}}` is refused by the validator. */
  inputs?: Record<string, string>
}

/** One existing cron of any kind, both layers — backs the cron management list. */
export interface CronSummary {
  name: string
  agent: string
  schedule: string
  kind: 'prompt' | 'activity'
  prompt?: string
  activity?: string
  deliver?: string
  layer: 'fleet' | 'agent'
  file: string
  /** ADR-103: true when this cron's agent is core-owned — the cron management
   * list hides edit/delete and shows a read-only badge instead. */
  core: boolean
  /** Literal input values on an `activity:` cron. Hydrated into the authoring
   * panel on edit: the writer rewrites `inputs:` wholesale, so a panel that
   * could not see these would silently delete them on an unrelated edit. */
  inputs?: Record<string, string>
}

/** POST /api/activities/convert/propose — a successful multi-file propose. */
export interface ProposeConversionResult {
  pr_number: number
  url: string
  improvement_id?: string
  /** Set when the PR was opened but the in-app review row failed to register,
   *  so the change will NOT appear under Improvements. */
  improvement_warning?: string
  registration_state?: RegistrationState
  files: string[]
}

// ---------------------------------------------------------------------------
// Fleet Editor — ADR-111 D2: the generic overlay file browser/editor + the
// agent-editor propose path.
// ---------------------------------------------------------------------------

/** One editable file under a FleetAgentNode (GET /api/fleet/tree). */
export interface FleetFileNode {
  label: string
  path: string
  /** ADR-139: this exact path is core-owned and synced, OR its agent is
   *  core-owned. Distinct from FleetAgentNode.core_owned, which cannot express
   *  a locked file inside a fleet-owned agent — editing or deleting one is
   *  refused by the backend, and the next fleet sync would restore it. */
  core_owned: boolean
}

/** One skill under an agent's skills/ directory (GET /api/fleet/tree). */
export interface FleetSkillNode {
  skill_name: string
  path: string
}

/** One group in the fleet overlay tree — an agent, the shared resources/
 *  folder, or the single config/fleet.yaml (GET /api/fleet/tree). */
export interface FleetAgentNode {
  agent: string
  kind: 'agent' | 'resources' | 'config'
  path_prefix: string
  soul_path?: string
  crons_path?: string
  skills: FleetSkillNode[]
  files: FleetFileNode[]
  /** ADR-103: true when this node's agent is core-owned — its files render
   *  read-only in the Fleet Editor's Files tab. */
  core_owned: boolean
}

/** GET /api/fleet/tree (ADR-111 D2.1) — the Fleet Editor's file-browser data source. */
export interface FleetTree {
  repo: string
  agents: FleetAgentNode[]
}

/** GET /api/fleet/file?path=… (ADR-111 D2.2a) — one overlay file's content. */
export interface FleetFile {
  path: string
  content: string
  found: boolean
  core_owned: boolean
}

/** GET /api/agents/:agent's response — the ONE agent's current profile
 *  fields, sourced from the SAME fleet.yaml roster.Load parses for
 *  listAgents(). `model` is the agent's explicit model.default, or '' if the
 *  profile doesn't set one (it then inherits the fleet/platform default);
 *  `model_is_explicit` is true iff `model` is non-empty. Backs the Fleet
 *  Editor's Profile facet pre-select (ADR-111 IA-4). */
export interface AgentProfile {
  profile: string
  model: string
  enabled: boolean
  model_is_explicit: boolean
}

/** One model the platform accepts, tagged with the vendor that offers it
 *  (ADR-182: a choice is a (provider, model) PAIR, never a bare id). */
export interface CatalogModel {
  id: string
  label: string
  default: boolean
  provider: string
  provider_label: string
  variant?: boolean
  context_length?: number
}

/** One vendor, and whether THIS FLEET can authenticate to it (ADR-182 D5).
 *
 *  `credential` is 'satisfied' | 'missing' | 'unknown', and the three are NOT
 *  interchangeable: `missing` means the vault answered and the item is not
 *  there, `unknown` means nobody could look. The backend already decided
 *  `selectable` from them — the UI must not re-derive it, or the two can
 *  disagree about what may be proposed.
 */
export interface ModelProvider {
  provider: string
  label: string
  key_env: string
  op_ref: string
  /** 'live' | 'preserved' | 'seed' | 'fallback' — where this model list came
   *  from. A seeded list must not be shown as vendor-confirmed. */
  source: string
  /** Does this FLEET have a key for the vendor, per the console's read of
   *  1Password. NOT the same question as `mc_callable` below. */
  credential: 'satisfied' | 'missing' | 'unknown'
  credential_detail?: string
  console_path?: string
  /** May this vendor be PROPOSED for an agent's profile. An agent runs on its
   *  own host with its own injected key, so this stays true for a vendor
   *  Mission Control itself cannot call. */
  selectable: boolean
  /** ADR-183 Phase 6 — can MISSION CONTROL ITSELF place a call to this vendor
   *  right now: its own environment holds a key AND it implements the wire.
   *
   *  A THIRD FACT, and the one the copilot's own model selector must gate on.
   *  The case that makes it necessary: a key pasted into 1Password with no MC
   *  redeploy since — `credential` is 'satisfied' and this is false. Gating the
   *  selector on `credential` would offer a model the copilot then fails to
   *  call, the failure arriving later as a 503 with nothing saying which of the
   *  two facts was missing. */
  mc_callable: boolean
  /** Which of the two is missing, when one is. A greyed option with no reason
   *  sends an operator to check the key they already pasted. */
  mc_callable_detail?: string
  models: CatalogModel[]
}

export interface ModelCatalogResponse {
  providers: ModelProvider[]
  default_model: string
  default_provider: string
  catalog_generated_at?: string
  /** 'ok' | 'unavailable' — whether the credential half could be obtained at
   *  all. Without it a page cannot tell "every vendor is unprovisioned" from
   *  "the console did not answer", and those need different words. */
  credential_check: 'ok' | 'unavailable'
}

/** One layer of the Workflow Assistant's model resolution (ADR-194 D1).
 *
 *  Four are reported and only one wins. A surface that showed the winner alone
 *  could not tell an operator whether THEY chose it, their fleet did, or the
 *  deployment did — and those three have different remedies when it is wrong. */
export interface AssistantModelLayer {
  provider: string
  model: string
  /** Only on `effective`: which of the other layers won. */
  source?: 'request' | 'operator' | 'fleet' | 'platform'
  /** Only on `operator`: when that person set it. */
  set_at?: string
}

/** GET /api/assistant-model — every layer, the winner, and the fleet file's own
 *  refusal when it has one.
 *
 *  `fleet_error` is NOT a failure of this request: the endpoint answers 200 with
 *  the cause in a field, because a committed pin nobody can read must be VISIBLE
 *  rather than fatal and must never read as "no pin". */
export interface AssistantModelView {
  effective: AssistantModelLayer
  operator: AssistantModelLayer
  fleet: AssistantModelLayer
  platform: AssistantModelLayer
  fleet_path: string
  fleet_error?: string
}

/** One MCP capability bound to an agent, joined against the bundled catalog
 *  (ADR-111 D4/D5 addendum phase 1, GET /api/agents/:agent/capabilities).
 *
 *  `config_keys` and `scoping_env` are env var NAMES only — the backend never
 *  serializes values, so a literal secret hand-edited into config/fleet.yaml
 *  cannot reach the browser. Do not add a values field.
 *
 *  `in_catalog: false` means the agent mounts a server the bundled manifest
 *  does not describe (drift). Everything but `key` is then empty — render it
 *  as unknown rather than as a capability that has no tools. */
export interface AgentCapability {
  key: string
  label: string
  summary?: string
  access?: string
  risk?: string
  tier?: string
  requires_prerequisite?: string
  /** The console's answer for `requires_prerequisite`.
   *
   *  `unknown` means MC COULD NOT VERIFY — console unreachable, no fleet key,
   *  or a prereq key the console does not track. It is deliberately NOT
   *  `unsatisfied`: an outage is not evidence of a missing credential, so it
   *  warns rather than blocks (owner decision 2026-08-21). */
  prerequisite_state: 'satisfied' | 'unsatisfied' | 'unknown' | 'not_required'
  config_keys: string[]
  /** Values for the subset of `config_keys` the CATALOG declares non-secret
   *  (`management.non_secret_config`) — the agent's actual scoping, e.g. which
   *  Asana projects. Anything not declared has no entry here: the allowlist
   *  fails closed, so a new credential is hidden by default. */
  config: Record<string, string>
  scoping_env: string[]
  tools: string[]
  in_catalog: boolean
  /** The PER-AGENT credential answer for this capability. Empty when the
   *  capability needs none — or when the read failed, which
   *  `credentials_readable` on the envelope distinguishes. */
  credentials: CapabilityCredential[]
}

/** One entry in the platform MCP catalog (GET /api/capabilities) — the menu the
 *  Capabilities facet enables FROM. Served by MC rather than hardcoded in the
 *  frontend: a hardcoded list would be a second copy of a core-owned catalog,
 *  free to drift the moment a server is added. */
export interface CapabilityCatalogEntry {
  key: string
  label: string
  summary?: string
  access?: string
  risk?: string
  tier?: string
  /** e.g. "self_serve" | "admin_only" | "request". Reported so the UI can show
   *  what a fleet owner cannot self-serve, instead of offering it and having the
   *  propose come back 409. */
  availability?: string
  requires_prerequisite?: string
  tools: string[]
}

/** Result of POST /api/agents/:agent/capabilities/propose.
 *  `changed: false` means the capability was already in the requested state —
 *  a success, not an error, and no PR was opened. */
export interface CapabilityProposeResult {
  changed: boolean
  pr_number?: number
  url?: string
  improvement_id?: string
  improvement_warning?: string
  registration_state?: RegistrationState
  files?: string[]
  /** Set when the credential could not be VERIFIED (console unreachable). The
   *  propose still happened — this is not a failure. */
  prereq_warning?: string
  manual_wiring?: { env_var: string; message: string }[]
  detail?: string
}

/** One credential a capability needs, for ONE agent — the per-agent answer from
 *  Console's credential-status route.
 *
 *  WHY THIS EXISTS ALONGSIDE `prerequisite_state`. That field is FLEET-level.
 *  For orion it reported hubspot_app satisfied — truthfully, five agents use
 *  HubSpot — while the 1Password item a sixth agent's binding needed did not
 *  exist. The row rendered green beside an absent credential, and the absence
 *  only surfaced when `op inject` aborted the fleet's deploy. This is the answer
 *  at the scope the deploy actually cares about. */
export interface CapabilityCredential {
  capability: string
  /** The scoping env var the MCP server reads. */
  env_var: string
  /** The Console prerequisite that provisions this credential. */
  prerequisite_key: string
  prerequisite_label: string
  /** The 1Password LOCATION, never a secret — these strings are already
   *  committed in every fleet's `agents/<agent>/op.env.tpl`. Showing it is what
   *  turns "not provisioned" into an instruction. */
  op_ref: string
  /** true = scoped to this agent; false = shared by the fleet. Rendered because
   *  it is the blast radius of revoking: one agent, or all of them. */
  per_agent: boolean
  /** `missing` = the vault answered and it is not there. `unknown` = we could
   *  not ASK — never rendered as missing, or an operator gets sent to create a
   *  credential that already exists. */
  state: 'satisfied' | 'missing' | 'unknown' | 'not_required'
  detail: string
  /** RELATIVE path, built by Console (which owns its own URL shape). Rendered
   *  against the console base URL; never assembled here. */
  console_path: string
  /** The prerequisite reports satisfied only because a manual override carries
   *  it over an unprovisioned required field.
   *
   *  Server-suppressed once THIS agent's credential is verified: the warning
   *  describes the FLEET-LEVEL field, and beside a confirmed per-agent
   *  credential it produced a row reading "✓ is provisioned" and then "it is
   *  not evidence this credential exists" — two contradictory sentences about
   *  different things. */
  override_masks_fields: boolean
  /** Whether the agent's COMMITTED op.env.tpl actually has a live line for this
   *  var.
   *
   *  BOTH must be true for the capability to work: `state` says the credential
   *  EXISTS, `wired` says the binding USES it. crm-operator sat in the gap —
   *  item created through Prerequisites, op.env.tpl still holding the
   *  placeholder written before it existed, so the gateway was deployed with no
   *  token while the row rendered a plain green "provisioned". */
  wired: boolean
  /** The ONE thing to do next, in the operator's words. Present on every state
   *  that is not already working, including the read failures where the correct
   *  action is explicitly "nothing". */
  next_action?: string
}

export interface AgentCapabilities {
  agent: string
  /** Core-owned agents (ADR-103) get capabilities from core, not the fleet. */
  core_owned: boolean
  capabilities: AgentCapability[]
  /** false = the catalog could not be READ, so metadata is absent rather than
   *  empty. "Could not look" must never render as "found nothing". */
  catalog_readable: boolean
  /** false = the console could not be asked about credentials at all, so the UI
   *  says so once rather than on every row. Same rule as catalog_readable. */
  prerequisites_readable: boolean
  /** false = the PER-AGENT credential read failed. Distinct from
   *  prerequisites_readable: the two hit different Console routes and either can
   *  fail alone, so one flag standing for both would misreport whichever broke.
   *  When false, an empty `credentials` must NOT be read as "needs none". */
  credentials_readable: boolean
}

/** One skill stub to scaffold alongside a newly CREATEd agent
 *  (POST /api/agents/propose). */
export interface AgentProposeSkillInput {
  name: string
  description?: string
}

/** POST /api/agents/propose's request body (ADR-111 D2.3, fleet_owner) —
 *  create a brand-new agent profile, or edit fields on an existing one.
 *  EDIT only applies the fields that are set; omitted fields are untouched. */
export interface AgentProposeInput {
  profile: string
  mode: 'create' | 'edit'
  model?: string
  /** ADR-182 D3: the vendor that serves `model`. Sent alongside it so the
   *  backend never has to resolve (and can never mis-resolve) the pair. */
  provider?: string
  role_description?: string
  skills?: AgentProposeSkillInput[]
  enabled?: boolean
  message?: string
}

// ADR-111 item 1 / ADR-113: a specialist's routing: intent (how the brain
// decides to delegate to it) + optional capability bullets. Proposing this
// regenerates the brain's fleet-roster server-side.
export interface RoutingProposeInput {
  intent: string
  capabilities?: string[]
  message?: string
}

// ---------------------------------------------------------------------------
// Response types — ADR-079 Phase 5d (the AI copilot / Chat mode)
// ---------------------------------------------------------------------------

/** One turn in the copilot chat request body (POST /api/activities/copilot). */
export interface CopilotMessage {
  role: 'user' | 'assistant'
  content: string
}

/** POST /api/activities/copilot's response — a validated draft + rationale.
 * Never a write; Propose is the separate path that turns a reviewed draft
 * into a PR. `validation` is produced by the SAME validator as
 * validateWorkflowDraft/proposeWorkflowActivity (ADR-079 D4: "the AI can be
 * wrong; the merge cannot"). */
export interface CopilotResponse {
  draft: string
  draft_id?: string
  /** Supporting changeset files returned by handler/copilot.go. */
  files?: ProposedChangesetFile[]
  rationale: string
  validation: ValidateActivityResult
  /** The model this turn REQUESTED — the deployment's configured one, or the
   *  per-run override if one was given. An intention, not a measurement. */
  model: string
  /** ADR-183 — the vendor, and the model the vendor said ANSWERED.
   *
   *  Present only when Mission Control itself placed the call: an Architect turn
   *  is answered by a fleet agent, which MC cannot measure from here (it is
   *  attributed on the fleet side by turn id). Never derived from `model` — a
   *  turn claiming an answered model nobody measured would be
   *  indistinguishable from one that was. */
  provider?: string
  response_model?: string
  attempts: number
  /** The background job that produced this turn, attached by the 202 ack. */
  job_id?: string
  /** The answer was produced but its transcript could not be saved. */
  unsaved?: boolean
  /** Save failure shown beside the retained answer. */
  save_error?: string
  /** ADR-134 D4 — set when the repair loop spent its budget with the draft still
   *  invalid. Absent on success. `outside: true` means the fix is not the
   *  operator's to make from this editor. */
  blocker?: CopilotBlocker
  /** ADR-142 — a capability gap this turn filed (the request needed a capability
   *  the platform lacks). `filed` false with a `note` means a block was emitted
   *  but could not be recorded; it is reported rather than dropped. */
  capability_gap?: {
    want: string
    filed: boolean
    investigation_id?: string
    conversation_id?: string
    note?: string
  }
  /** Prose to READ rather than a draft to apply: an ask-mode answer, or an
   *  edit-mode clarifying question. Empty on a normal authoring turn. */
  answer?: string
  /** Whether `draft` carries a new activity the editor should adopt. FALSE for
   *  every answer and every failure.
   *
   *  Load-bearing, not informational: the client used to apply `draft`
   *  unconditionally, so a turn that produced no YAML — which is what a question
   *  produced — replaced the operator's in-progress draft with an empty string.
   *  Asking the copilot a question destroyed the work in the editor. */
  draft_changed?: boolean
  /** Which engine reasoned this turn: "fleet-architect" or "mission-control". */
  engine?: string
}

/** Whether a copilot turn is authoring ("edit") or answering a question
 *  ("ask"). Omitted => "edit", the pre-existing behaviour. */
export type CopilotIntent = 'edit' | 'ask'

/** POST /api/agents/:agent/skills/:slug/copilot's response (ADR-094 P1.5) — a
 *  validated SKILL.md draft + rationale. `validation` is SkillValidation
 *  (structural + routes linkage); gated the same way the activity copilot is. */
export interface SkillCopilotResponse {
  /** The background job that produced this turn, attached by pollLLMJob. */
  job_id?: string
  draft: string
  rationale: string
  validation: SkillValidation
  model: string
  attempts: number
  /** Prose to READ rather than a draft to apply (a clarifying question). */
  answer?: string
  /** Whether `draft` carries a revision to adopt — false for every answer and
   *  every failure, so a turn with no document can't blank the editor. */
  draft_changed?: boolean
  /** Which engine reasoned this turn: "fleet-architect" or "mission-control". */
  engine?: string
}

/** One actionable finding from POST /api/agents/:agent/skills/:slug/critique
 *  (ADR-121 D2). Advisory only — the Critique action never writes; applying a
 *  fix flows back through the skill copilot -> validate -> propose path. */
export interface SkillCritiqueSuggestion {
  area: string
  severity: 'high' | 'med' | 'low'
  finding: string
  suggestion: string
}

/** POST /api/agents/:agent/skills/:slug/critique's response (ADR-121 D2) — a
 *  grounded review of the skill, reusing the SAME grounding bundle + completer
 *  as the skill copilot (ADR-121 D3: one engine). `ok:false` means the model
 *  never returned a well-formed critique after retrying — `error` carries why,
 *  and `summary` carries a snippet of its raw last reply for visibility. */
export interface SkillCritiqueResponse {
  ok: boolean
  summary: string
  suggestions: SkillCritiqueSuggestion[]
  model: string
  attempts: number
  error?: string
  /** Which engine reasoned this critique: "fleet-architect" or "mission-control". */
  engine?: string
}

export interface PluginNav {
  position: string
  group?: string
}

export interface PluginSummary {
  id: string
  title: string
  icon?: string | null
  version: string
  entry: string
  nav: PluginNav
  enabled: boolean
  source_commit: string
}

export interface PluginDataScope {
  id: string
  source: string
  access: 'read' | 'write'
  type?: 'agent' | 'fleet_metric' | 'artifact' | 'files'
  profile?: string
  metric?: string
  prompt?: string
  refresh_seconds?: number
}

export interface PluginAPIAction {
  action: string
  requires_approval: boolean
}

export interface PluginManifest {
  id: string
  title: string
  icon?: string | null
  version: string
  entry: string
  nav: PluginNav
  enabled: boolean
  source_commit: string
  visibility: string[]
  data_scopes: PluginDataScope[]
  api_surface: PluginAPIAction[]
}

export interface DataSource {
  id: string
  type: 'agent' | 'fleet_metric'
  profile?: string
  metric?: string
  prompt?: string
  refresh_seconds?: number
}

// Component node in the manifest layout tree.
export interface ManifestNode {
  component: string
  props?: Record<string, unknown>
  children?: ManifestNode[]
}

export interface Manifest {
  module_id: string
  title: string
  icon: string | null
  version: string
  author_claw: string
  visibility: string[]
  data_sources: DataSource[]
  layout: ManifestNode
  actions?: unknown[]
}

export interface RespondApprovalBody {
  action: 'approve' | 'reject'
  grade?: number | null
  grade_why?: string | null
}

// ---------------------------------------------------------------------------
// Response types — ADR-082 durable diagnostic investigations (Phase 5)
// ---------------------------------------------------------------------------

/** Investigation lifecycle statuses (chat.Investigation* constants, backend). */
export type InvestigationStatus =
  | 'active'
  | 'awaiting-approval'
  | 'awaiting-evidence'
  | 'resolved'
  | 'insufficient-evidence'
  | 'abandoned'

/**
 * One cost-ranked evidence-acquisition option the diagnostician proposed
 * (chat.Acquisition, backend) — carried inside an `acquisition_proposed`
 * steps_tried entry's `acquisitions` array. `kind` drives both the RBAC tier
 * (new_instrumentation/access_request require fleet_owner) and the PR-budget
 * check (new_instrumentation is the only kind that consumes one).
 */
export interface Acquisition {
  kind: 'wait_for_capture' | 'flag_gated' | 'new_instrumentation' | 'probe_repro' | 'access_request' | string
  description: string
  rationale: string
  cost: string
}

/**
 * One append-only steps_tried entry (chat.AppendInvestigationStep, backend).
 * Every entry carries ts/kind/detail; `acquisitions` is only present on
 * `acquisition_proposed` entries, `acquisition`/`approved_by` only on
 * `acquisition_approved` entries — every other field is optional accordingly.
 */
export interface InvestigationStep {
  ts: string
  kind: 'acquisition_proposed' | 'acquisition_approved' | 'resume_attempted' | 'resolved' | 'budget_exhausted' | 'deadline_exceeded' | string
  detail: string
  acquisitions?: Acquisition[]
  acquisition?: string
  approved_by?: string
  /** WHO took the step, when a human did. The server has always written this on
   *  `closed_by_operator` and now writes it on ADR-142's `capability_gap_wont_build`
   *  / `..._revoked`; the type simply never learned it, so reading the operator's
   *  identity off a step needed a cast. A refusal that cannot say who made it is not
   *  much of a record. */
  by?: string
}

/** [{observable, why_unavailable}] — the shape of evidence_needed entries. */
export interface EvidenceNeededEntry {
  observable: string
  why_unavailable: string
}

/** [{claim, citation}] — the ADR-078 dual-citation shape of evidence_had entries. */
export interface EvidenceHadEntry {
  claim: string
  citation: string
}

/** [{id, text, state}] — one hypothesis; state ∈ open|confirmed|refuted|blocked-on-evidence. */
export interface InvestigationHypothesis {
  id: string
  text: string
  state: 'open' | 'confirmed' | 'refuted' | 'blocked-on-evidence' | string
}

/**
 * A durable ADR-082 diagnostic investigation case (GET /api/investigations,
 * GET /api/investigations/:id, POST /api/investigations/:id/approve — all
 * return this shape). JSON keys are snake_case (chat.DiagnosticInvestigation,
 * backend). The JSONB fields (scope/hypotheses/evidence_had/evidence_needed/
 * steps_tried/resume_trigger) are typed loosely where their internal shape is
 * writer-owned and only rendered generically by this UI; steps_tried and the
 * evidence arrays have a known-enough shape to type precisely.
 */
export interface DiagnosticInvestigation {
  id: string
  conversation_id: string
  origin_message_id: string
  resume_target: string
  symptom: string
  scope: Record<string, unknown> | null
  hypotheses: InvestigationHypothesis[]
  evidence_had: EvidenceHadEntry[]
  evidence_needed: EvidenceNeededEntry[]
  steps_tried: InvestigationStep[]
  current_intent: string
  next_action: string
  resume_trigger: Record<string, unknown> | null
  evidence_summary: string
  iterations: number
  max_iterations: number
  acquisition_prs: number
  max_acquisition_prs: number
  deadline: string | null
  status: InvestigationStatus
  resolution: string
  /** ADR-142 D3 — 'diagnosis' or 'capability_gap'. The list always carried both;
   *  without this the UI could not tell them apart, so a gap read as a diagnosis
   *  whose symptom happened to mention a missing capability. */
  kind?: string
  /** ADR-142 D1 — the gap envelope, verbatim, for a capability_gap case.
   *  `invalid: true` marks a REPORT that could not be filed (a malformed
   *  envelope) rather than a capability that is missing. */
  gap?: Record<string, unknown> | null
  created_at: string
  updated_at: string
}

export interface ApproveAcquisitionBody {
  acquisition_index: number
}

// ---------------------------------------------------------------------------
// Response types — ADR-024 Logs
// ---------------------------------------------------------------------------

/**
 * A single row returned by GET /api/logs.
 *
 * Frozen contract from ADR-024 D1:
 *   level  ∈ {error, warn, info, debug}
 *   source ∈ {event, agent, stdout}
 */
export interface LogRow {
  id: number | string | null
  _tier1_id?: string
  created_at?: string
  ts: string
  level: 'error' | 'warn' | 'info' | 'debug'
  source: 'event' | 'agent' | 'stdout'
  agent: string | null
  skill: string | null
  type: string | null
  logger: string | null
  container: string | null
  correlation_id: string | null
  summary: string
  detail: string | null
}

export interface LogsParams {
  agent?: string
  skill?: string
  /** Comma-separated: error,warn,info,debug */
  level?: string
  /** Comma-separated: delegation,approval,signal_audit,grade,self_edit,experiment */
  type?: string
  /** Comma-separated: event,agent,stdout */
  source?: string
  q?: string
  since?: string
  until?: string
  limit?: number
  cursor?: string
  fence_after?: string
  fence_through?: string
}

export interface LogsResponse {
  logs: LogRow[]
  next_cursor: string | null
  ingest_fence?: string
}

// ---------------------------------------------------------------------------
// Response types — ADR-024 Log pipeline health stats
// ---------------------------------------------------------------------------

/** Stats from the logs SQLite database (Tiers 2 and 3). */
export interface LogsDbStats {
  total: number
  rows_24h_by_source: { event: number; agent: number; stdout: number }
  last_ts_by_source: { event: string | null; agent: string | null; stdout: string | null }
}

/** In-process counters for the Tier-3 Vector→callback POST ingestor. */
export interface IngestHttpStats {
  posts_received: number
  events_parsed: number
  rows_inserted: number
  rows_skipped: number
  last_post_at: string | null
}

/** In-process counters for the Tier-2 NDJSON file tailer. */
export interface AgentTailStats {
  files_seen: number
  lines_ingested: number
  rows_inserted: number
  last_tail_at: string | null
}

/** Full pipeline health snapshot returned by GET /api/logs/stats. */
export interface LogsStats {
  generated_at: string
  logs_db: LogsDbStats
  ingest_http: IngestHttpStats
  agent_tail: AgentTailStats
  tier1: { delegation_24h: number; rows_24h?: number; last_ts?: string | null }
}

// ---------------------------------------------------------------------------
// Response types — ADR-051 Delegations (operator trace view)
// ---------------------------------------------------------------------------

/** A single delegation event row from GET /api/delegations. */
export interface DelegationEvent {
  id: number
  caller: string | null
  profile: string
  outcome: 'success' | 'timeout' | 'error' | 'empty' | string
  exit_code: number | null
  duration_s: number | null
  query: string | null
  turn_id: string | null
  delegation_id: string | null
  alerted: number | null
  created_at: string
}

/** A single per-call attribution row from GET /api/delegations/trace. */
export interface DelegationCall {
  id: number
  turn_id: string | null
  delegation_id: string | null
  caller: string | null
  profile: string | null
  seq: number | null
  kind: string | null
  target: string | null
  latency_ms: number | null
  retries: number | null
  backoff_ms: number | null
  http_code: number | null
  error_class: string | null
  tokens_in: number | null
  tokens_out: number | null
  outcome: string | null
  detail: string | null
  created_at: string
}

export interface TraceRollup {
  call_count: number
  total_latency_ms: number
  longest_call: { target: string | null; kind: string | null; latency_ms: number } | null
  outcomes: Record<string, number>
}

export interface ResourceSample {
  id: number
  ts: string
  load1: number | null
  load5: number | null
  load15: number | null
  ncpu: number | null
  mem_total_bytes: number | null
  mem_available_bytes: number | null
  mem_used_pct: number | null
}

export interface ResourceRollup {
  sample_count: number
  max_load1: number
  avg_load1: number
  max_mem_used_pct: number
  ncpu: number | null
  load_per_core: number | null
  elevated_load: boolean | null
  elevated_mem: boolean
}

export interface ResourceWindow {
  samples: ResourceSample[]
  rollup: ResourceRollup | null
}

export interface DelegationTraceResponse {
  fleet_id: string | null
  filter: { turn_id: string | null; delegation_id: string | null }
  delegations: DelegationEvent[]
  calls: DelegationCall[]
  rollup: TraceRollup
  gateway_join: string
  resource_window: ResourceWindow | null
  /** Present when the data plane has joined the telemetry span reader. */
  spans?: LlmUsageTraceSpan[]
  telemetry?: 'ok' | 'unavailable'
}

export interface DelegationsListResponse {
  delegations: DelegationEvent[]
  window_hours: number
  fleet_id: string | null
}

export interface DelegationsParams {
  window_hours?: number
  /** 'timeout' | 'error' | 'all' */
  outcome?: string
  limit?: number
}

// ---------------------------------------------------------------------------
// Response types — LLM Usage (ADR-183 / ADR-188)
// ---------------------------------------------------------------------------

export interface LlmUsageHealth {
  accepted_24h: number
  dropped_24h: number
  rejected_24h: number
  oldest_started_at: string | null
  ingest_lag_ms: number | null
  db_bytes: number
  spool_bytes: number
  target_coverage_days: number
  estimated_coverage_days: number | null
  /** Set when a telemetry migration failed; the ledger may be incomplete. */
  migration_error?: string | null
}

export interface LlmUsageSubstitutionPair {
  requested: string
  answered: string
  calls: number
}

export interface LlmUsageSubstitutions {
  count: number
  pairs: LlmUsageSubstitutionPair[]
}

export interface LlmUsageMissingBuckets {
  input_tokens?: number
  cache_read_tokens?: number
  cache_write_tokens?: number
  output_tokens?: number
  reasoning_tokens?: number
}

export interface LlmUsageRollupRow {
  key?: string | null
  label?: string | null
  trigger: string | null
  includes_authoring?: boolean
  calls: number
  input_tokens: number | null
  cache_read_tokens: number | null
  cache_write_tokens: number | null
  output_tokens: number | null
  reasoning_tokens: number | null
  total_tokens: number | null
  unattributed_calls: number
  no_usage_calls: number
  missing_buckets?: LlmUsageMissingBuckets
  substitutions: LlmUsageSubstitutions
  p99_latency_ms: number | null
  share: number
}

export interface LlmUsageRollupResponse {
  rows: LlmUsageRollupRow[]
  totals: LlmUsageRollupRow
  health: LlmUsageHealth
  telemetry: 'ok' | 'unavailable'
}

export interface LlmUsageQueryParams {
  window?: string
  group_by?: string
  skill?: string
  provider?: string
  key?: string
  trace_id?: string
  day?: string
  limit?: number
  cursor?: string
}

/**
 * A root's attribution, taken from the root span's own columns. A cron is
 * identified by source and id together; either alone identifies nothing.
 */
export interface LlmUsageAttribution {
  activity: string | null
  run_id: string | null
  cron_id: string | null
  cron_source: string | null
  cron_label: string | null
}

export interface LlmUsageRoot extends LlmUsageAttribution {
  span_id: string
  trace_id: string
  kind: string
  trigger: string | null
  profile: string | null
  name: string | null
  started_at: string
  status: string
  /** Null when no call in the trace reported a measured token bucket. */
  total_tokens: number | null
  calls: number
}

export interface LlmUsageTracesResponse {
  roots: LlmUsageRoot[]
  next_cursor: string | null
  scanned?: number
  health?: LlmUsageHealth
  telemetry: 'ok' | 'unavailable'
}

export interface LlmUsageTraceSpan {
  span_id: string
  trace_id: string
  parent_span_id: string | null
  kind: string
  status: string
  parent_state: 'present' | 'omitted' | 'missing' | 'evicted'
  [key: string]: unknown
}

export interface LlmUsageTraceResponse {
  spans: LlmUsageTraceSpan[]
  attribution?: LlmUsageAttribution | null
  next_cursor: string | null
  incomplete: boolean
  /** Every span in the trace, not the page of them this response carries. */
  span_count?: number | null
  /** Summed over every usage row in the trace, not over the page of spans. */
  total_tokens?: number | null
  calls?: number | null
  /** Calls that handed back no usage object at all. Matches the rollup's field. */
  no_usage_calls?: number | null
  /**
   * Calls that reported no measured token bucket, whether they sent no usage
   * object or an empty one. This is what qualifies the total: an emitter that
   * loses its usage still sends an object, so `no_usage_calls` does not see it.
   */
  unmeasured_calls?: number | null
  delegation_events: unknown[]
  health?: LlmUsageHealth
  telemetry: 'ok' | 'unavailable'
}

export interface LlmUsageDailyProvider {
  provider: string
  calls: number
  input_tokens: number | null
  cache_read_tokens: number | null
  cache_write_tokens: number | null
  output_tokens: number | null
  reasoning_tokens: number | null
  total_tokens: number | null
  missing_buckets?: LlmUsageMissingBuckets
  unattributed_calls: number
  no_usage_calls: number
}

export interface LlmUsageDailyCoverage {
  retained: boolean
  captured: boolean
  dropped: number
  rejected: number
  oldest_started_at: string | null
  ingest_lag_ms: number | null
}

export interface LlmUsageDailyResponse {
  day: string
  providers: LlmUsageDailyProvider[]
  coverage: LlmUsageDailyCoverage
  health?: LlmUsageHealth
  telemetry: 'ok' | 'unavailable'
}

// ---------------------------------------------------------------------------
// Response types — Chat (ADR-TBD Phase 1 non-streaming)
// ---------------------------------------------------------------------------

export interface Project {
  id: string
  name: string
  description: string | null
  core_prompt: string | null
  created_by: string
  created_at: string
  updated_at: string
  archived_at: string | null
}

export interface Conversation {
  id: string
  kind: string | null
  handle?: string | null
  title: string | null
  primary_agent: string | null
  created_by: string | null
  created_at: string
  updated_at: string
  last_message_at: string | null
  archived_at: string | null
  project_id: string | null
  tags: string[]
}

export interface ChannelAgent {
  conversation_id: string
  agent: string
  delivery_modes: string[]
  also_post_slack: boolean
  active: boolean
  created_by: string
  created_at: string
}

export interface CreateChannelBody {
  handle: string
  title?: string
  instructions?: string
  project_id?: string
}

export interface AddChannelAgentBody {
  agent: string
  delivery_mode: string
  also_post_slack?: boolean
}

export interface ChatMessage {
  id: string
  role: 'operator' | 'assistant'
  author_email: string | null
  author_agent: string | null
  parent_message_id?: string | null
  superseded_by?: string | null
  content: string
  status: 'ok' | 'error' | 'pending' | 'streaming' | 'complete' | 'parked' | string
  token_count?: number | null
  created_at: string
  /**
   * ADR-124 D4 — what the operator is actually waiting for. Set only on the
   * pending placeholder a deferral creates; absent on every other message.
   */
  background_goal?: string | null
  /**
   * ADR-203 — the run's approval tasks. A run waiting on one has status
   * `parked`; it resumes only when an operator decides it here.
   */
  approvals?: ActivityApproval[]
}

/** ADR-203 — one approval task of an activity run. */
export interface ActivityApproval {
  id: string
  message_id: string
  step_key: string
  title: string
  /** Exactly what the operator approves; the decision is bound to its hash. */
  preview: string
  preview_sha256: string
  status: 'pending' | 'approved' | 'rejected' | 'superseded'
  decided_by?: string
  decision_note?: string
  decided_role?: string
  decided_at?: string
  gate_key?: string | null
  approver_mode?: 'escalating' | 'requester' | 'directors' | null
  approvers?: Array<{ key: string; name: string; role: string }>
  escalation_window_hours?: number | null
  decided_route?: 'mc' | 'slack' | 'asana_stage' | 'asana_field' | null
  decided_actor_key?: string | null
  can_decide?: boolean
  notices?: Array<{
    kind: 'request' | 'escalation'
    actor_key: string
    name: string
    status: 'pending' | 'sent' | 'failed' | 'undeliverable'
    sent_at?: string | null
    last_error?: string | null
  }>
  board_status?: 'pending' | 'bound' | 'unavailable' | null
  board_detail?: string | null
  created_at: string
}

export interface RegenerateMessageResponse {
  superseded_id: string
  assistant_message: ChatMessage
}

export interface EditMessageResponse {
  superseded_operator_id: string
  superseded_assistant_id?: string
  operator_message: ChatMessage
  assistant_message: ChatMessage
}

export interface ConversationDetail extends Conversation {
  messages: ChatMessage[]
}

export interface ListConversationsParams {
  kind?: string
  project_id?: string
  tag?: string
  creator?: string
  q?: string
  sort?: 'name' | 'created' | 'activity'
}

export interface SendMessageResponse {
  operator_message: ChatMessage
  assistant_message: ChatMessage
}

export interface ChatFile {
  id: string
  conversation_id: string
  filename: string
  content_type: string
  size_bytes: number
  created_by: string
  created_at: string
}

export interface UpdateProjectBody {
  name?: string
  core_prompt?: string | null
}

export interface MentionCandidate {
  email: string
  display_name?: string | null
}

export interface UnreadMention {
  id: string
  message_id: string
  conversation_id: string
  mentioned_email: string
  notify_via: string
  notified_at?: string | null
  read_at?: string | null
  created_at: string
}

// ---------------------------------------------------------------------------
// Internal fetch helpers
// ---------------------------------------------------------------------------

/**
 * Bootstrap GET — no auth, no retry. Used only by getConfig().
 */
// ─────────────────────────────────────────────────────────────────────────────
// ADR-123 — dependency-aware delete.
//
// A delete is a three-step flow: PLAN (read-only impact), optionally ask the
// Fleet Architect to rewrite prose references, then PROPOSE. The plan is
// recomputed server-side on propose, so these types describe what the operator
// SEES, never what the server trusts.
// ─────────────────────────────────────────────────────────────────────────────

/** The entity kinds a delete can target. */
export type DeleteKind = 'agent' | 'skill' | 'workflow' | 'cron' | 'resource' | 'dataset' | 'agent_file'

/**
 * Identifies one deletable thing. `name` is empty for an agent.
 *
 * Three kinds are exceptions to the agent/name shape:
 *
 *   - a `resource`: `agent` must be empty and `name` carries its repo-relative
 *     PATH under `resources/` — a file, or a directory whose whole subtree goes
 *     with it;
 *   - a `dataset` (ADR-147 D1): `agent` must be empty and `name` carries the
 *     VERSIONED set ref, `approval-tiers@v1`. Never the bare name — a name-keyed
 *     identity is indistinguishable from correct until a v2 exists, at which
 *     point every stored reference means the wrong version. It is also
 *     basename-only: the backend refuses a ref containing `/` or `..`, because a
 *     set ref addresses a delete rather than a read query.
 *   - an `agent_file` (ADR-123 D8e): `agent` is the owner and `name` is the
 *     full repo-relative path beneath that agent's activities/ directory.
 *
 * Build special targets with resourceTarget(), dataSetTarget(), or
 * agentFileTarget() rather than by hand.
 */
export interface DeleteTarget {
  kind: DeleteKind
  agent: string
  name?: string
}

/** Builds the DeleteTarget for one resource file or folder. */
export function resourceTarget(path: string): DeleteTarget {
  return { kind: 'resource', agent: '', name: path }
}

/** Builds the DeleteTarget for one file or folder in an agent's activities/ (ADR-123 D8e). */
export function agentFileTarget(agent: string, path: string): DeleteTarget {
  return { kind: 'agent_file', agent, name: path }
}

/**
 * Builds the DeleteTarget for ONE version of a data set (ADR-147 D1).
 *
 * `setRef` must be the versioned ref exactly as the Data area already serves it
 * (`DataSetRow.set`) — passed straight through, never split on `@`, because the
 * version is half the identity.
 */
export function dataSetTarget(setRef: string): DeleteTarget {
  return { kind: 'dataset', agent: '', name: setRef }
}

/** What must happen to one inbound reference (ADR-123 D3). */
export type DeleteVerdict = 'cascade' | 'repair' | 'block' | 'architect_repair' | 'flag'

/** One inbound reference to the target. */
export interface DeleteEdge {
  kind: string
  verdict: DeleteVerdict
  file: string
  line?: number
  // Lowercase since deps.Entity carries JSON tags; it previously serialized as
  // Kind/Agent/Name, which is why this type was capitalized and every
  // Entity-derived label rendered "undefined".
  from?: { kind?: string; agent?: string; name?: string }
  detail: string
}

/**
 * One artifact the delete removes. A `fragment` means the FILE SURVIVES and only
 * that part of it goes — an agent's profile block inside config/fleet.yaml, or a
 * single cron entry. The UI must not describe those as deletions.
 */
export interface DeleteRemoval {
  path: string
  sha?: string
  fragment?: string
}

/** External cleanup Mission Control cannot perform (ADR-123 D7). */
export interface DeleteHandOffItem {
  system: 'console' | '1password' | 'slack' | 'db_projection'
  detail: string
  /** Shared state that must NOT be retired — something else still uses it. */
  shared: boolean
}

/** The read-only impact of a delete. `ok: false` means it is refused (D5). */
export interface DeletePlan {
  target: DeleteTarget
  deleting: DeleteTarget[]
  containment: DeleteRemoval[]
  cascade: DeleteEdge[]
  repair: DeleteEdge[]
  blocked: DeleteEdge[]
  architect: DeleteEdge[]
  flagged: DeleteEdge[]
  hand_off: DeleteHandOffItem[]
  ok: boolean
}

/** One file the Architect was asked to rewrite (ADR-123 D6). */
export interface ProseRepair {
  path: string
  content?: string
  rationale?: string
  validation: { ok: boolean; errors?: string[] }
  /** The Architect declined to rewrite — a designed outcome, not a failure. */
  unchanged: boolean
}

export interface ProseRepairResponse {
  target: DeleteTarget
  repairs: ProseRepair[]
}

/** Thrown when a delete is refused because executable references remain (D5). */
export class DeleteBlockedError extends Error {
  constructor(public blocked: DeleteEdge[], message: string) {
    super(message)
    this.name = 'DeleteBlockedError'
  }
}

/**
 * parseDeleteBlocked recognises the propose endpoint's 409 and lifts its
 * `blocked` edges into a typed error, so the UI can render the blockers rather
 * than a raw HTTP string. Returns null for any other failure.
 */
export function parseDeleteBlocked(e: unknown): DeleteBlockedError | null {
  const msg = e instanceof Error ? e.message : String(e)
  if (!msg.includes('delete_blocked')) return null
  const brace = msg.indexOf('{')
  if (brace < 0) return new DeleteBlockedError([], msg)
  try {
    const body = JSON.parse(msg.slice(brace)) as { detail?: string; blocked?: DeleteEdge[] }
    return new DeleteBlockedError(body.blocked ?? [], body.detail ?? msg)
  } catch {
    return new DeleteBlockedError([], msg)
  }
}

/**
 * Thrown when a delete is refused because this target ALREADY has an open delete
 * proposal (ADR-123 D6c).
 *
 * A distinct type rather than a generic error string, because the operator's way
 * forward is a specific pull request they have to go and look at. Left as the
 * raw HTTP message it rendered as a wall of JSON with the pull request number
 * buried in it — technically visible, and not a surface anyone can act on.
 */
export class DeleteAlreadyProposedError extends Error {
  constructor(
    public prNumber: number,
    public prUrl: string,
    public branch: string,
    message: string,
  ) {
    super(message)
    this.name = 'DeleteAlreadyProposedError'
  }
}

/**
 * parseDeleteAlreadyProposed recognises the propose endpoint's
 * `delete_already_proposed` 409 and lifts the existing pull request out of it.
 * Returns null for any other failure.
 *
 * Mirrors parseDeleteBlocked, including its fallback: a body that will not parse
 * still yields the typed error with the message intact, because losing the
 * refusal entirely would be worse than losing the link.
 */
export function parseDeleteAlreadyProposed(e: unknown): DeleteAlreadyProposedError | null {
  const msg = e instanceof Error ? e.message : String(e)
  if (!msg.includes('delete_already_proposed')) return null
  const brace = msg.indexOf('{')
  if (brace < 0) return new DeleteAlreadyProposedError(0, '', '', msg)
  try {
    const body = JSON.parse(msg.slice(brace)) as {
      detail?: string
      existing_pr_number?: number
      existing_pr_url?: string
      existing_branch?: string
    }
    return new DeleteAlreadyProposedError(
      body.existing_pr_number ?? 0,
      body.existing_pr_url ?? '',
      body.existing_branch ?? '',
      body.detail ?? msg,
    )
  } catch {
    return new DeleteAlreadyProposedError(0, '', '', msg)
  }
}

export class SkillChangePendingError extends Error {
  constructor(
    public code: 'skill_change_pending' | 'skill_change_in_progress',
    public prNumber: number,
    public prUrl: string,
    public improvementId: string,
    message: string,
  ) {
    super(message)
    this.name = 'SkillChangePendingError'
  }
}

/** Pull out actionable links when the skill already has a waiting or active propose. */
export function parseSkillChangePending(e: unknown): SkillChangePendingError | null {
  const msg = e instanceof Error ? e.message : String(e)
  const code = msg.includes('skill_change_pending')
    ? 'skill_change_pending'
    : msg.includes('skill_change_in_progress') ? 'skill_change_in_progress' : null
  if (!code) return null
  const brace = msg.indexOf('{')
  if (brace < 0) return new SkillChangePendingError(code, 0, '', '', msg)
  try {
    const body = JSON.parse(msg.slice(brace)) as {
      detail?: string
      existing_pr_number?: number
      existing_pr_url?: string
      existing_improvement_id?: string
    }
    return new SkillChangePendingError(
      code,
      body.existing_pr_number ?? 0,
      body.existing_pr_url ?? '',
      body.existing_improvement_id ?? '',
      body.detail ?? msg,
    )
  } catch {
    return new SkillChangePendingError(code, 0, '', '', msg)
  }
}

/** One field being added by a publish (ADR-168 D3/D4).
 *
 *  `path` is the `schemas[].note` grammar — `[]` descends a keyed collection's record, a dot
 *  descends an existing object — and every OTHER key is the field's own JSON Schema node,
 *  forwarded verbatim in the order given. The backend splices the raw bytes rather than
 *  re-encoding, so key order is part of the contract (a reordered document would make the
 *  transition rule's append-only check unsatisfiable). Nothing here is validated in the
 *  browser: the canonical validator judges it, and a second opinion would be a second rule. */
export interface SchemaAddition {
  path: string
  [key: string]: unknown
}

/** The 201 from POST /api/data-references/schema/publish.
 *
 *  Deliberately NO `consumers_staying`: the endpoint does not send it, and the panel reads the
 *  staying consumers from the used-by endpoint instead. An optional field here would be
 *  permanently `undefined` and read as "no consumer stays". */
export interface SchemaPublishResult {
  set: string
  source_set: string
  version: number
  schema_ref: string
  schema_path: string
  data_path: string
  branch: string
  pr_number: number
  url: string
  /** The improvement row that makes the PR reviewable in-app (ADR-079), empty when the
   *  registration failed or could not be confirmed. */
  improvement_id?: string
  /** Why the PR may not appear under Improvements, in the server's words. Empty on success.
   *  The server distinguishes a REFUSAL, which it knows created no row, from an outcome it
   *  could not confirm — the fleet's internal API inserts the row before it finishes answering,
   *  so a 5xx or a lost reply leaves a row that may exist. Render it as-is; the difference is
   *  in the wording and it changes what the operator should do next. The publish itself
   *  succeeded either way — the branch is pushed and the PR is open — so this is a warning
   *  about where to go next, never an error. */
  improvement_warning?: string
  registration_state?: RegistrationState
  /** The consumers this publish moved onto the new version, as repo-relative paths.
   *
   *  ALL FOUR repin keys are OPTIONAL because the server sends none of them when the operator
   *  unticked the box — and because a browser holding an open tab across a rollout can be
   *  talking to a server from the other half of the release. An absent key is "the repin did
   *  not run", never an error. */
  repinned?: string[]
  /** The consumers that stayed on the old version, each with why. `[]` when the repin ran and
   *  moved everything; absent when it did not run at all. */
  left_behind?: SchemaRepinNote[]
  /** The superseded schema path, retired by this publish. Mutually exclusive with
   *  `deprecation_skipped`: exactly one of the two is present when the repin ran, so a caller
   *  reads either without testing the other. */
  deprecated?: string
  /** Why the superseded version stayed published, naming the artifact that caused it. */
  deprecation_skipped?: SchemaDeprecationSkip
  /** What `left_behind` could not see, in the server's words. The list is computed from the
   *  `/fleet` mount, which lags `develop`, so a consumer added there — or repinned onto the
   *  superseded version there — since the mount last synced is absent from it and was not
   *  moved. Sent whenever the repin ran, absent on the `repin: false` path like the four
   *  keys above. Render it as-is, beside the list it qualifies. */
  left_behind_note?: string
}

/** One consumer the repin did NOT move, with why.
 *
 *  `reason` is the stable handle (`core_owned`, a role-bound pin) and `detail` is the server's
 *  own sentence — render the sentence, branch on the handle. */
export interface SchemaRepinNote {
  path: string
  reason: string
  detail?: string
}

/** Why the superseded version stayed published. `path` is the artifact that caused it — the
 *  consumer left behind, or the other data document bound to the same schema — because a
 *  reason with no subject is not actionable. */
export interface SchemaDeprecationSkip {
  reason: string
  path: string
  detail?: string
}

/**
 * A coded refusal from the publish endpoint (ADR-168 D4) — every status except the 403, which
 * `post()` already types as ForbiddenError.
 *
 * There are twenty-odd codes across 400/404/409/422/503/500 and an operator cannot act on
 * `HTTP 422: {"code":"transition_refused",…}`. `code` is the stable handle a panel branches on,
 * `findings` is the validator's own shape (422 only), and `route` is folded into `message` for
 * the same reason ForbiddenError folds it: every panel renders `e.message`, so a route living
 * only in a field reaches no screen.
 */
export class SchemaPublishRefusedError extends Error {
  readonly status: number
  readonly code: string
  readonly detail?: string
  readonly findings?: DataFinding[]
  readonly route?: string
  /** The registry path a `target_occupied` refusal names. */
  readonly path?: string
  /** The branch a refusal left behind — `target_on_develop`, `rewrite_target_changed` and
   *  `publish_incomplete` all create one before they refuse. Litter nobody was told about is
   *  litter nobody cleans up, so a panel that has this must show it. */
  readonly branch?: string
  /** `publish_incomplete` only: the files that reached the branch before it failed. */
  readonly landed?: string[]
  /** `publish_incomplete` only: the files that did not — never attempted, or read back off the
   *  branch and found absent. */
  readonly missing?: string[]
  /** `publish_incomplete` only: the files whose commit was neither acknowledged nor settled by
   *  the reconciling read, so the server cannot say whether they reached the branch. Neither
   *  `landed` nor `missing`: only the branch itself can answer. */
  readonly unconfirmed?: string[]
  /** `publish_incomplete` only: some of the consumer moves landed and the rest did not, so the
   *  branch holds a set whose consumers are split across two versions. */
  readonly repinPartial?: boolean

  constructor(init: {
    status: number; code: string; message: string
    detail?: string; findings?: DataFinding[]; route?: string; path?: string
    branch?: string; landed?: string[]; missing?: string[]; unconfirmed?: string[]
    repinPartial?: boolean
  }) {
    super(init.route ? `${init.message}\n\nNext step: ${init.route}` : init.message)
    this.name = 'SchemaPublishRefusedError'
    this.status = init.status
    this.code = init.code
    this.detail = init.detail
    this.findings = init.findings
    this.route = init.route
    this.path = init.path
    this.branch = init.branch
    this.landed = init.landed
    this.missing = init.missing
    this.unconfirmed = init.unconfirmed
    this.repinPartial = init.repinPartial
  }
}

/**
 * Lifts `post()`'s `HTTP <status>: <body>` into the typed refusal — the same shape
 * `parseDeleteBlocked` uses, because `post()` has already consumed the Response and the text is
 * all that survives. Returns null when the body is not a coded refusal (an HTML gateway error,
 * a UnauthorizedError, a network failure), so the caller rethrows the original rather than
 * inventing a `code` that no handler emits.
 */
function parseSchemaPublishRefusal(e: unknown): SchemaPublishRefusedError | null {
  if (!(e instanceof Error) || e instanceof ForbiddenError) return null
  const head = /^HTTP (\d{3}): /.exec(e.message)
  if (!head) return null
  const brace = e.message.indexOf('{')
  if (brace < 0) return null
  try {
    const body = JSON.parse(e.message.slice(brace)) as {
      code?: string; error?: string; detail?: string
      findings?: DataFinding[]; route?: string; path?: string
      branch?: string; landed?: string[]; missing?: string[]; unconfirmed?: string[]
      repin_partial?: boolean
    }
    // `error` is always the human sentence. `code` is absent on the handler's few uncoded
    // refusals (the 404, a malformed body, `github_not_configured`), and those are still worth
    // showing as their sentence — so an empty `code` means "no machine handle", never
    // "not a refusal".
    if (!body?.error) return null
    return new SchemaPublishRefusedError({
      status: Number(head[1]),
      code: body.code ?? '',
      message: body.error,
      detail: body.detail,
      findings: body.findings,
      route: body.route,
      path: body.path,
      branch: body.branch,
      landed: body.landed,
      missing: body.missing,
      unconfirmed: body.unconfirmed,
      repinPartial: body.repin_partial,
    })
  } catch {
    return null
  }
}

async function requestPublic<T>(path: string): Promise<T> {
  const resp = await fetch(path, {
    headers: { 'Content-Type': 'application/json' },
  })
  if (resp.status === 401) throw new UnauthorizedError()
  if (resp.status === 403) throw new ForbiddenError()
  if (!resp.ok) {
    const body = await resp.text()
    throw new Error(`HTTP ${resp.status}: ${body}`)
  }
  return resp.json() as Promise<T>
}

/**
 * Bootstrap GET with an explicit token. Used only by getMe() which runs before
 * the token manager is initialised.
 */
async function requestWithToken<T>(path: string, token: string): Promise<T> {
  const resp = await fetch(path, {
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
  })
  if (resp.status === 401) throw new UnauthorizedError()
  if (resp.status === 403) throw new ForbiddenError()
  if (!resp.ok) {
    const body = await resp.text()
    throw new Error(`HTTP ${resp.status}: ${body}`)
  }
  return resp.json() as Promise<T>
}

function refreshOrAbort(signal?: AbortSignal): Promise<boolean> {
  const refresh = tokenManager.refresh()
  if (!signal) return refresh
  const abortReason = () => signal.reason ?? new DOMException('The request was aborted.', 'AbortError')
  if (signal.aborted) return Promise.reject(abortReason())

  let onAbort!: () => void
  const aborted = new Promise<never>((_resolve, reject) => {
    onAbort = () => reject(abortReason())
    signal.addEventListener('abort', onAbort, { once: true })
  })
  return Promise.race([
    refresh.finally(() => signal.removeEventListener('abort', onAbort)),
    aborted,
  ])
}

/**
 * Authenticated GET — reads the token from the manager. On 401, tries one
 * silent refresh; if that succeeds, retries once. If the refresh fails,
 * tokenManager.onExpired has fired and we throw UnauthorizedError.
 */
async function request<T>(path: string, init?: { signal?: AbortSignal }): Promise<T> {
  const token = tokenManager.getToken()
  const resp = await fetch(path, {
    signal: init?.signal,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
  })

  if (resp.status === 401) {
    const refreshed = await refreshOrAbort(init?.signal)
    if (!refreshed) throw new UnauthorizedError()
    // Retry once with the new token.
    const newToken = tokenManager.getToken()
    const retryResp = await fetch(path, {
      signal: init?.signal,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${newToken}`,
      },
    })
    if (retryResp.status === 401) throw new UnauthorizedError()
    if (retryResp.status === 403) throw new ForbiddenError()
    if (!retryResp.ok) {
      const body = await retryResp.text()
      throw new Error(`HTTP ${retryResp.status}: ${body}`)
    }
    return retryResp.json() as Promise<T>
  }

  if (resp.status === 403) throw new ForbiddenError()
  if (!resp.ok) {
    const body = await resp.text()
    throw new Error(`HTTP ${resp.status}: ${body}`)
  }

  return resp.json() as Promise<T>
}

function llmUsagePath(path: string, params?: LlmUsageQueryParams): string {
  const qs = new URLSearchParams()
  for (const [key, value] of Object.entries(params ?? {})) {
    if (value !== undefined && value !== null && value !== '') {
      qs.set(key, String(value))
    }
  }
  const query = qs.toString()
  return `/api/llm-usage/${path}${query ? `?${query}` : ''}`
}

/**
 * Builds the right error for a 403 response.
 *
 * ADR-103's core-owned write guard returns `{"error":"core_owned","detail":"…"}`
 * and gets its own subclass, because callers branch on it. Every OTHER coded 403
 * — `{"error":"…","detail":"…"}` — keeps its `detail` as the ForbiddenError's
 * message, so the operator reads the refusal the server wrote rather than the
 * word "Forbidden".
 *
 * That last part is not cosmetic. ADR-147 D4's refusals (`ownership_mismatch`,
 * `external_set`) each name THE ROUTE THAT WORKS — "Propose a change to core",
 * "Edit the field projection in this fleet's MCP Admin" — and ADR-127 D1 is
 * explicit that a dead end with no route is what pushes an operator into
 * hand-editing the fleet repo. Discarding the body turned every one of those
 * routes into the word Forbidden.
 *
 * `route` TRAVELS WITH `detail`, NOT INSTEAD OF IT
 * -----------------------------------------------
 * Restoring `detail` alone fixed half of it: the operator was told what was
 * refused and still not where the change goes, which is precisely the "not
 * editable here" dead end D1 names as the failure mode to design against. The
 * backend sends `route` on EVERY ownership refusal, so it is read here and
 * ForbiddenError folds it into the message — see that class for why the field
 * alone would have reached no screen.
 *
 * A plain RBAC 403 has no body at all and still yields the generic message.
 * Consumes the body, so callers must not read it again.
 */
async function forbiddenErrorFrom(resp: Response): Promise<ForbiddenError> {
  try {
    const body = await resp.json() as { error?: string; detail?: string; route?: string }
    if (body?.error === 'core_owned') return new CoreOwnedError(body.detail, body.route)
    if (body?.error && body.detail) return new ForbiddenError(body.detail, body.route)
  } catch {
    // Not JSON (or empty body) — fall through to the generic ForbiddenError.
  }
  return new ForbiddenError()
}

/**
 * ADR-107 P2: builds AuthorTestsFailingError from an already-read 409 body
 * (text, not a live Response — the body can only be read once, and post()
 * needs the raw text either way to build the generic fallback error).
 * Returns null when the body isn't the author_tests_failing shape (some
 * other 409, e.g. a future unrelated conflict) so the caller falls back to
 * the generic error.
 */
function authorTestsFailingErrorFromText(text: string): AuthorTestsFailingError | null {
  try {
    const body = JSON.parse(text) as { error?: string; detail?: string; checks?: FailingCheck[] }
    if (body?.error === 'author_tests_failing') {
      return new AuthorTestsFailingError(body.checks ?? [], body.detail)
    }
  } catch {
    // Not JSON — fall through.
  }
  return null
}

/**
 * Authenticated POST — same 401-refresh-retry logic as request().
 */
async function post<T>(path: string, body: unknown): Promise<T> {
  const token = tokenManager.getToken()
  const resp = await fetch(path, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  })

  if (resp.status === 401) {
    const refreshed = await tokenManager.refresh()
    if (!refreshed) throw new UnauthorizedError()
    const newToken = tokenManager.getToken()
    const retryResp = await fetch(path, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${newToken}`,
      },
      body: JSON.stringify(body),
    })
    if (retryResp.status === 401) throw new UnauthorizedError()
    if (retryResp.status === 403) throw await forbiddenErrorFrom(retryResp)
    if (!retryResp.ok) {
      const text = await retryResp.text()
      if (retryResp.status === 409) {
        const authorErr = authorTestsFailingErrorFromText(text)
        if (authorErr) throw authorErr
      }
      throw new Error(`HTTP ${retryResp.status}: ${text}`)
    }
    return retryResp.json() as Promise<T>
  }

  if (resp.status === 403) throw await forbiddenErrorFrom(resp)
  if (!resp.ok) {
    const text = await resp.text()
    if (resp.status === 409) {
      const authorErr = authorTestsFailingErrorFromText(text)
      if (authorErr) throw authorErr
    }
    throw new Error(`HTTP ${resp.status}: ${text}`)
  }

  return resp.json() as Promise<T>
}

/**
 * Authenticated PATCH — same 401-refresh-retry logic as post().
 */
async function patch<T>(path: string, body: unknown): Promise<T> {
  const token = tokenManager.getToken()
  const resp = await fetch(path, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  })

  if (resp.status === 401) {
    const refreshed = await tokenManager.refresh()
    if (!refreshed) throw new UnauthorizedError()
    const newToken = tokenManager.getToken()
    const retryResp = await fetch(path, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${newToken}`,
      },
      body: JSON.stringify(body),
    })
    if (retryResp.status === 401) throw new UnauthorizedError()
    if (retryResp.status === 403) throw new ForbiddenError()
    if (!retryResp.ok) {
      const text = await retryResp.text()
      throw new Error(`HTTP ${retryResp.status}: ${text}`)
    }
    return retryResp.json() as Promise<T>
  }

  if (resp.status === 403) throw new ForbiddenError()
  if (!resp.ok) {
    const text = await resp.text()
    throw new Error(`HTTP ${resp.status}: ${text}`)
  }

  return resp.json() as Promise<T>
}

/**
 * Authenticated PUT expecting 204 — the ADR-134 D5 autosave shape. Separate from
 * patch() because there is no body to parse and a JSON parse of an empty 204
 * throws.
 */
async function putVoid(path: string, body: unknown, init?: { signal?: AbortSignal }): Promise<void> {
  const send = (token: string | null) =>
    fetch(path, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify(body),
      signal: init?.signal,
    })

  let resp = await send(tokenManager.getToken())
  if (resp.status === 401) {
    if (!(await refreshOrAbort(init?.signal))) throw new UnauthorizedError()
    resp = await send(tokenManager.getToken())
    if (resp.status === 401) throw new UnauthorizedError()
  }
  if (resp.status === 403) throw new ForbiddenError()
  if (!resp.ok) {
    const text = await resp.text()
    const stale = staleDraftFrom(resp.status, text)
    if (stale) throw stale
    throw new Error(`HTTP ${resp.status}: ${text}`)
  }
}

/**
 * Authenticated GET returning the raw Response, so a caller can distinguish 204
 * ("no session") from 200 with a body. request() parses JSON unconditionally and
 * would throw on the empty 204.
 */
async function requestRaw(path: string, init?: { signal?: AbortSignal }): Promise<Response> {
  const send = (token: string | null) =>
    fetch(path, { signal: init?.signal, headers: { 'Authorization': `Bearer ${token}` } })

  let resp = await send(tokenManager.getToken())
  if (resp.status === 401) {
    if (!(await refreshOrAbort(init?.signal))) throw new UnauthorizedError()
    resp = await send(tokenManager.getToken())
    if (resp.status === 401) throw new UnauthorizedError()
  }
  if (resp.status === 403) throw new ForbiddenError()
  return resp
}

/** Reads a fleet overlay file, treating only the API's structured missing-file 404 as empty. */
async function readFleetFile(path: string): Promise<FleetFile> {
  const resp = await requestRaw(`/api/fleet/file?path=${encodeURIComponent(path)}`)
  if (resp.ok) return resp.json() as Promise<FleetFile>

  const body = await resp.text()
  if (resp.status === 404) {
    try {
      const parsed: unknown = JSON.parse(body)
      if (parsed !== null && typeof parsed === 'object' && !Array.isArray(parsed)
        && (parsed as { found?: unknown }).found === false) {
        const coreOwned = (parsed as { core_owned?: unknown }).core_owned
        return {
          path,
          content: '',
          found: false,
          core_owned: typeof coreOwned === 'boolean' ? coreOwned : false,
        }
      }
    } catch {
      // A non-JSON 404 is a route/proxy failure, not a missing fleet file.
    }
  }
  throw new Error(`HTTP ${resp.status}: ${body}`)
}

/**
 * Authenticated DELETE — same 401-refresh-retry logic as post().
 * Returns void (204 No Content expected).
 */
async function del(path: string): Promise<void> {
  const token = tokenManager.getToken()
  const resp = await fetch(path, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
  })

  if (resp.status === 401) {
    const refreshed = await tokenManager.refresh()
    if (!refreshed) throw new UnauthorizedError()
    const newToken = tokenManager.getToken()
    const retryResp = await fetch(path, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${newToken}`,
      },
    })
    if (retryResp.status === 401) throw new UnauthorizedError()
    if (retryResp.status === 403) throw new ForbiddenError()
    if (!retryResp.ok) {
      const text = await retryResp.text()
      const stale = staleDraftFrom(retryResp.status, text)
      if (stale) throw stale
      throw new Error(`HTTP ${retryResp.status}: ${text}`)
    }
    return
  }

  if (resp.status === 403) throw new ForbiddenError()
  if (!resp.ok) {
    const text = await resp.text()
    const stale = staleDraftFrom(resp.status, text)
    if (stale) throw stale
    throw new Error(`HTTP ${resp.status}: ${text}`)
  }
}

/**
 * The shared kickoff-then-poll transport for every Fleet Editor LLM endpoint
 * (copilot, skill copilot, skill critique, convert).
 *
 * WHY none of these can be a plain POST-and-wait: a fleet's Mission Control is
 * served through Cloudflare, which gives the origin 100 seconds. These endpoints
 * reason via the Fleet Architect (an agent turn) or chain several completions
 * through a validate-repair loop, so they legitimately run longer — and when they
 * did, the edge returned a 524 whose HTML body the client could not parse, so the
 * operator saw only "The copilot request failed." with no cause. The POST now
 * returns 202 + a job id in milliseconds and the work is polled.
 *
 * Bespoke rather than the generic post() helper because a 503 here means
 * something distinct ("no Anthropic key for this fleet", ADR-079 Phase 5d) that
 * callers branch on, not a generic transport failure.
 */
async function llmJobRequest<T>(
  url: string,
  body: unknown,
  statusPath: (jobId: string) => string,
  onProgress?: (elapsedMs: number) => void,
  onAccepted?: () => void,
): Promise<T> {
  const doFetch = (tok: string) =>
    fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${tok}`,
      },
      body: JSON.stringify(body),
    })

  let resp = await doFetch(tokenManager.getToken())
  if (resp.status === 401) {
    const refreshed = await tokenManager.refresh()
    if (!refreshed) throw new UnauthorizedError()
    resp = await doFetch(tokenManager.getToken())
    if (resp.status === 401) throw new UnauthorizedError()
  }
  if (resp.status === 503) throw new CopilotNotConfiguredError()
  if (resp.status === 403) throw await forbiddenErrorFrom(resp)
  if (!resp.ok) {
    const text = await resp.text()
    const stale = staleDraftFrom(resp.status, text)
    if (stale) throw stale
    const finished = runFinishedFrom(resp.status, text)
    if (finished) throw finished
    throw new Error(`HTTP ${resp.status}: ${text}`)
  }

  onAccepted?.()
  const ack = (await resp.json()) as { job_id?: string; status?: string }
  if (!ack.job_id) {
    // A server that answered inline (an older MC image mid-rollout) still
    // parses: the body IS the result.
    return ack as T
  }

  return pollLLMJob<T>(ack.job_id, statusPath(ack.job_id), onProgress)
}

/**
 * Poll a background LLM job to completion.
 *
 * Split out of llmJobRequest so ADR-160 D2's RE-ATTACH shares this loop byte for byte
 * rather than reimplementing it. A second copy would drift — and the copy that drifts is
 * the one on the recovery path, which is exercised least and matters most.
 *
 * `alreadyElapsedMs` seeds the progress clock for a turn that started before this page
 * did: the server knows it has been running 4 minutes, and a re-attached client that
 * counted from zero would tell the operator the opposite of the truth.
 */
async function pollLLMJob<T>(
  jobId: string,
  path: string,
  onProgress?: (elapsedMs: number) => void,
  alreadyElapsedMs = 0,
): Promise<T> {
  const started = Date.now() - alreadyElapsedMs
  // Poll past the worker's own ceiling (copilotJobRunTimeout = 1260s): 2s
  // cadence x 700 = ~23 min, so the server's budget is always what expires
  // first and the client never reports a timeout the server hasn't hit.
  for (let i = 0; i < 700; i++) {
    await new Promise(r => setTimeout(r, 2000))
    onProgress?.(Date.now() - started)
    const st = await request<T & { status?: string; error?: string; unsaved?: boolean; result?: T }>(path)
    if (st.status === 'running') continue
    if (st.status === 'error') {
      if (st.unsaved && st.result) {
        return {
          ...st.result,
          job_id: jobId,
          unsaved: true,
          save_error: st.error || 'the answer could not be saved',
        } as T
      }
      throw new Error(st.error || 'the request failed')
    }
    return { ...st, job_id: jobId } as T
  }
  throw new Error('the request timed out — please try again')
}

/**
 * POST /api/activities/copilot — the workflow copilot turn (ADR-079 5d),
 * background-job transport (see llmJobRequest).
 *
 * `intent` is "edit" (author/change the draft) or "ask" (answer a question and
 * change nothing). Ask mode exists because the edit contract demands a fenced
 * YAML activity and nothing else, so a question could only ever be treated as a
 * malformed reply and retried until the budget ran out.
 */
async function copilotAuthorRequest(
  agent: string,
  messages: CopilotMessage[],
  currentDraft: string,
  intent: CopilotIntent = 'edit',
  onProgress?: (elapsedMs: number) => void,
  name?: string,
  focus?: string,
  /** ADR-183 Phase 6 — answer THIS turn on a different model.
   *
   *  Both omitted keeps the deployment's configured model, which is what makes
   *  "did switching help" answerable against a stable baseline. The pair is
   *  validated server-side against the generated catalog AND against what this
   *  Mission Control can actually reach, so a rejection arrives as a 400 with a
   *  reason written for the operator rather than as a failed turn. */
  model?: { provider: string; model: string },
  draftId?: string,
  /** ADR-200 amendment (2026-09-29) — the operator's per-run "Scope this request first"
   *  choice. Read by the server ONLY from the request that opens a run; on a request that
   *  lands on a run already in progress it is ignored, so a follow-up cannot change it.
   *  Omitted means false (no scoping). */
  scoping?: boolean,
  /** Called once the server has taken the turn, before any polling; never for a refusal. */
  onAccepted?: () => void,
): Promise<CopilotResponse> {
  return llmJobRequest<CopilotResponse>(
    '/api/activities/copilot',
    // ADR-134 D3: `name` is the workflow SLOT, not the draft's own header. The
    // repair loop validates at the path propose commits to, so it converges on
    // a draft the gate will actually accept.
    //
    // ADR-136 D1: `focus` is the node selected on the canvas, so "this step"
    // resolves to the one the operator is looking at. Omitted when nothing is
    // selected — an absent scope and an empty one mean the same thing, and
    // sending `""` would only add a key the server has to re-check.
    {
      agent, messages, currentDraft, intent,
      ...(name ? { name } : {}),
      ...(focus ? { focus } : {}),
      // ADR-183 Phase 6. Spread only when a model was chosen: sending
      // `provider: ""` would add keys the server must re-check, and an absent
      // override and an empty one mean the same thing — the default.
      ...(model?.model ? { provider: model.provider, model: model.model } : {}),
      ...(draftId ? { draft_id: draftId } : {}),
      ...(scoping !== undefined ? { scoping } : {}),
    },
    id => `/api/activities/copilot/status/${encodeURIComponent(id)}`,
    onProgress,
    onAccepted,
  )
}

// skillCopilotRequest mirrors copilotAuthorRequest for the ADR-094 P1.5 SKILL.md
// copilot — same background-job transport, same 503/403 handling.
async function skillCopilotRequest(
  agent: string,
  slug: string,
  messages: CopilotMessage[],
  currentDraft: string,
  onProgress?: (elapsedMs: number) => void,
): Promise<SkillCopilotResponse> {
  const base = `/api/agents/${encodeURIComponent(agent)}/skills/${encodeURIComponent(slug)}`
  return llmJobRequest<SkillCopilotResponse>(
    `${base}/copilot`,
    { messages, currentDraft },
    id => `${base}/copilot/status/${encodeURIComponent(id)}`,
    onProgress,
  )
}

async function soulCopilotRequest(
  agent: string,
  messages: CopilotMessage[],
  currentDraft: string,
  onProgress?: (elapsedMs: number) => void,
): Promise<SoulCopilotResponse> {
  const base = `/api/agents/${encodeURIComponent(agent)}/soul`
  return llmJobRequest<SoulCopilotResponse>(
    `${base}/copilot`,
    { messages, currentDraft },
    id => `${base}/copilot/status/${encodeURIComponent(id)}`,
    onProgress,
  )
}

// skillCritiqueRequest mirrors skillCopilotRequest for the ADR-121 D2 per-skill
// Critique action — it hits the SAME engine, just a different endpoint/prompt.
async function skillCritiqueRequest(
  agent: string,
  slug: string,
  content?: string,
  onProgress?: (elapsedMs: number) => void,
): Promise<SkillCritiqueResponse> {
  const base = `/api/agents/${encodeURIComponent(agent)}/skills/${encodeURIComponent(slug)}`
  return llmJobRequest<SkillCritiqueResponse>(
    `${base}/critique`,
    { content: content ?? '' },
    id => `${base}/critique/status/${encodeURIComponent(id)}`,
    onProgress,
  )
}

/** GET /health (no auth) — what build this MC instance is running. `version`
 *  is the agentcore commit SHA baked in at image build (-ldflags); "dev" for a
 *  local/unstamped build. */
export interface HealthInfo {
  status: string
  version: string
  fleet_slug: string
}

// ---------------------------------------------------------------------------
// MCClient
// ---------------------------------------------------------------------------

class MCClient {
  /** Fetch runtime config (no auth required). */
  getConfig(): Promise<ClientConfig> {
    return requestPublic<ClientConfig>('/api/config')
  }

  /** Fetch the running build/version (no auth required). Served at root
   *  (`/health`, not under `/api`). */
  getHealth(): Promise<HealthInfo> {
    return requestPublic<HealthInfo>('/health')
  }

  /**
   * Fetch the current user's identity from the verified JWT.
   *
   * Takes an explicit token because it runs during bootstrap before the token
   * manager is initialised. All other methods read the token from the manager.
   */
  getMe(token: string): Promise<MeResponse> {
    return requestWithToken<MeResponse>('/api/me', token)
  }

  /** ADR-161 — arm capture for the NEXT activity run on this fleet's agent host.
   *
   *  Exists so an operator never touches a console or a shell for this. The arming is
   *  consumed by the next run, so there is nothing to switch off afterwards. */
  armCapture(): Promise<CaptureArmed> {
    return post<CaptureArmed>('/api/fleet/capture-arm', {})
  }

  /** DELETE /api/fleet/capture-arm — changed my mind before the run happened.
   *
   *  Without disarm the only way to clear an arming is to burn it on a run you did not
   *  want recorded. `del` resolves void, so the settled state is stated here rather than
   *  read from a body the helper discards. */
  async disarmCapture(): Promise<CaptureArmed> {
    await del('/api/fleet/capture-arm')
    return { armed: false }
  }

  /** GET /api/fleet/captures — armed state plus which runs hold recordings. */
  captureIndex(): Promise<CaptureIndex> {
    return request<CaptureIndex>('/api/fleet/captures')
  }

  /** GET /api/fleet/captures?run_id= — one run's recordings. */
  captureFiles(runId: string): Promise<CaptureFiles> {
    return request<CaptureFiles>(`/api/fleet/captures?run_id=${encodeURIComponent(runId)}`)
  }

  /** DELETE /api/fleet/captures?run_id= — remove one run's recordings.
   *
   *  Recordings are vendor payloads on disk; an operator who can create them must be able
   *  to remove them without a shell. Removes only the recordings, never the run workspace. */
  async deleteCaptureRun(runId: string): Promise<void> {
    await del(`/api/fleet/captures?run_id=${encodeURIComponent(runId)}`)
  }

  /** GET /api/fleet/captures?run_id=&file= — one recording, verbatim. */
  captureFile(runId: string, file: string): Promise<unknown> {
    return request<unknown>(
      `/api/fleet/captures?run_id=${encodeURIComponent(runId)}&file=${encodeURIComponent(file)}`)
  }

  /** GET /api/fleet/health */
  getFleetHealth(): Promise<FleetHealth> {
    return request<FleetHealth>('/api/fleet/health')
  }

  /**
   * POST /api/deploy/apply — ADR-099 P3.3/D8.2/D8.3: the operator-gated
   * "apply latest core" action. Dispatches BOTH halves of "apply the latest
   * core to this fleet" — this fleet's own deploy-fleet.yml on main
   * (recomposes SOUL/skills, refreshes the base image from core main) AND
   * agentfleet-infra's deploy-mc.yml (redeploys this fleet's Mission Control
   * container) — via the console over the same X-Fleet-Key channel for
   * both. fleet_owner ONLY (403 otherwise). Neither half failing throws on
   * its own; check the returned `agent_host.dispatched` and
   * `mission_control.dispatched` independently. 502 dispatch_failed only
   * when BOTH halves fail for a reason other than "not yet authorized."
   */
  applyCoreUpdate(): Promise<ApplyCoreUpdateResult> {
    return post<ApplyCoreUpdateResult>('/api/deploy/apply', {})
  }

  /**
   * GET /api/core-status — ADR-099 D7/D8: is a core update available for
   * this fleet, and what does it contain. Always resolves (never throws on
   * an unreachable console — the backend fails soft with available:false);
   * still throws UnauthorizedError/ForbiddenError like any other endpoint if
   * the SESSION itself is invalid.
   */
  getCoreStatus(): Promise<CoreStatus> {
    return request<CoreStatus>('/api/core-status')
  }

  /** GET /api/fleet/activity?limit= */
  getActivity(limit?: number): Promise<{ events: ActivityEvent[] }> {
    const qs = limit !== undefined ? `?limit=${limit}` : ''
    return request<{ events: ActivityEvent[] }>(`/api/fleet/activity${qs}`)
  }

  /** GET /api/approvals?status= */
  getApprovals(status?: string): Promise<{ approvals: Approval[] }> {
    const qs = status ? `?status=${encodeURIComponent(status)}` : ''
    return request<{ approvals: Approval[] }>(`/api/approvals${qs}`)
  }

  /** GET /api/improvements?status= (ADR-026 WS3 operator-improvement signals) */
  getImprovements(
    status?: string,
  ): Promise<{ improvements: Improvement[]; reconciler?: ReconcilerHealth | null }> {
    const qs = status ? `?status=${encodeURIComponent(status)}` : ''
    return request<{ improvements: Improvement[]; reconciler?: ReconcilerHealth | null }>(
      `/api/improvements${qs}`,
    )
  }

  /** GET /api/activities/:agent/:name/pending-proposal — see PendingProposal. */
  getPendingProposal(agent: string, workflow: string): Promise<PendingProposal> {
    return request<PendingProposal>(
      `/api/activities/${encodeURIComponent(agent)}/${encodeURIComponent(workflow)}/pending-proposal`,
    )
  }

  /** POST /api/improvements/:id — decline a stale improvement signal (#388 Phase 1). */
  declineImprovement(id: string, outcome: string): Promise<{ improvement: Improvement }> {
    return post<{ improvement: Improvement }>(`/api/improvements/${encodeURIComponent(id)}`, {
      status: 'declined',
      outcome,
    })
  }
  // #388 Phase 4: engage an open item — open a seeded chat with the owning agent
  // ("discuss") or have it act now ("next_step"). Returns the conversation to open.
  engageImprovement(
    id: string,
    mode: 'discuss' | 'next_step',
    note?: string,
  ): Promise<{ conversation_id: string; mode: string; warning?: string }> {
    return post<{ conversation_id: string; mode: string; warning?: string }>(
      `/api/improvements/${encodeURIComponent(id)}/engage`,
      note ? { mode, note } : { mode },
    )
  }
  noteImprovement(id: string, note: string): Promise<{ improvement: Improvement }> {
    return post<{ improvement: Improvement }>(
      `/api/improvements/${encodeURIComponent(id)}/note`,
      { note },
    )
  }

  /**
   * GET /api/improvements/:id/pr — the live GitHub PR behind an improvement
   * signal: title/branch/state, per-file diffs, and CI check state. operator |
   * fleet_owner only (viewer → 403); 503 if GitHub isn't configured for this
   * fleet; 404 if the improvement has no PR linked yet.
   */
  getImprovementPR(id: string): Promise<ImprovementPR> {
    return request<ImprovementPR>(`/api/improvements/${encodeURIComponent(id)}/pr`)
  }

  /**
   * GET /api/improvements/:id/pr/checks — the "why can't I approve this"
   * data source: failing/blocking check runs for the PR's current head SHA,
   * each with best-effort failure detail (check-run annotations, or an
   * excerpt from the failing job's log) down to file:line + the
   * assertion/error message where available. operator|fleet_owner only.
   * `checks` is always an array (never null).
   */
  getImprovementPRFailingChecks(id: string): Promise<FailingChecksResponse> {
    return request<FailingChecksResponse>(`/api/improvements/${encodeURIComponent(id)}/pr/checks`)
  }

  /**
   * POST /api/improvements/:id/approve — Approve & Deploy: merges the PR to
   * develop then promotes develop→main (deploy). fleet_owner ONLY.
   * 409 `checks_failing` (body carries `checks: FailingCheck[]` — the same
   * shape as getImprovementPRFailingChecks, for rendering the rich panel
   * even on an attempted approve) / `checks_pending` / `not_open`.
   * 502 → merged to develop but the production deploy failed (the change is
   * safely on develop).
   */
  approveImprovementPR(id: string): Promise<ApproveImprovementPRResult> {
    return post<ApproveImprovementPRResult>(`/api/improvements/${encodeURIComponent(id)}/approve`, {})
  }

  /**
   * POST /api/improvements/:id/deny — posts `reason` as a PR comment, closes
   * the PR and, when the head branch is on a Mission-Control-owned lane in
   * this fleet's repo, deletes that branch so the name (a version reservation
   * on data-schema-publish/) is released. `branch_deleted` says whether it
   * was; `branch_kept` explains a deliberate keep and `branch_delete_error` a
   * failed delete after a successful close. fleet_owner ONLY.
   */
  denyImprovementPR(id: string, reason: string): Promise<DenyImprovementResult> {
    return post<DenyImprovementResult>(
      `/api/improvements/${encodeURIComponent(id)}/deny`,
      { reason },
    )
  }

  /** GET /api/fleet/feedback (ADR-028 Phase 2 — the Feedback hub) */
  getFeedback(): Promise<FleetFeedback> {
    return request<FleetFeedback>('/api/fleet/feedback')
  }

  /** GET /api/approvals/:id */
  getApproval(id: string): Promise<{ approval: Approval; audit: AuditEntry[] }> {
    return request<{ approval: Approval; audit: AuditEntry[] }>(`/api/approvals/${encodeURIComponent(id)}`)
  }

  /**
   * POST /api/approvals/:id/respond
   * operator|fleet_owner only; viewer → 403 (ForbiddenError).
   * 409 → already responded (the Error message will start with "HTTP 409").
   */
  respondApproval(
    id: string,
    body: RespondApprovalBody,
  ): Promise<{ ok: boolean; status: string }> {
    return post<{ ok: boolean; status: string }>(
      `/api/approvals/${encodeURIComponent(id)}/respond`,
      body,
    )
  }

  // -------------------------------------------------------------------------
  // Investigations API (ADR-082 Phase 5 — durable diagnostic investigations)
  // -------------------------------------------------------------------------

  /**
   * GET /api/investigations?status=&conversation_id= — any authed role
   * (read-only). Both filters optional; omitted means "no filter". The
   * backend always returns `investigations: []` (never null) for an empty
   * result set.
   */
  getInvestigations(params?: { status?: string; conversation_id?: string; kind?: string }): Promise<{ investigations: DiagnosticInvestigation[] }> {
    const qs = new URLSearchParams()
    if (params?.status) qs.set('status', params.status)
    if (params?.conversation_id) qs.set('conversation_id', params.conversation_id)
    if (params?.kind) qs.set('kind', params.kind)
    const query = qs.toString()
    return request<{ investigations: DiagnosticInvestigation[] }>(`/api/investigations${query ? `?${query}` : ''}`)
  }

  /** POST /api/investigations/:id/close — fleet_owner ends a case with a reason.
   *
   *  The lifecycle had no human terminal path: a case ended only by resolving
   *  itself, exhausting its budget, or expiring. So the list was append-only and
   *  filled with cases that were finished or were never real. A reason is required —
   *  a closed case that cannot say why is the thing this avoids creating. */
  closeInvestigation(
    id: string,
    reason: string,
    /** 'wont_build' additionally records a STANDING decision not to build the
     *  capability, which filing then consults — so the next time an agent hits the
     *  same wall, no case is opened and no specialist is dispatched. Capability gaps
     *  only; the API rejects it on a diagnosis rather than silently downgrading. */
    status: 'resolved' | 'abandoned' | 'wont_build' = 'abandoned',
  ): Promise<{ id: string; status: string; resolution: string }> {
    return post(`/api/investigations/${encodeURIComponent(id)}/close`, { reason, status })
  }

  /** POST /api/investigations/:id/allow-again — LIFT a standing `wont_build`
   *  refusal so the capability can be proposed again (fleet_owner; a reason is
   *  required, same bar as refusing it).
   *
   *  Deliberately not a re-open of the case: the case stays terminal, and the next
   *  agent report opens a fresh one with its own deadline and budget. A revived case
   *  would carry a spent iteration budget and an expired deadline. */
  allowCapabilityAgain(id: string, reason: string):
    Promise<{ id: string; refused: boolean; by: string; reason: string }> {
    return post(`/api/investigations/${encodeURIComponent(id)}/allow-again`, { reason })
  }

  /** GET /api/investigations/:id — any authed role (read-only). 404 if missing. */
  getInvestigation(id: string): Promise<DiagnosticInvestigation> {
    return request<DiagnosticInvestigation>(`/api/investigations/${encodeURIComponent(id)}`)
  }

  /**
   * POST /api/investigations/:id/approve — approve one of the case's most
   * recently proposed acquisitions (1-based `acquisition_index`, matching the
   * numbering the operator was shown). Returns the updated investigation.
   *
   * RBAC is tiered by the CHOSEN acquisition's kind, enforced server-side:
   * `new_instrumentation`/`access_request` require fleet_owner (403
   * `insufficient_role_for_acquisition` otherwise); every other kind accepts
   * operator|fleet_owner (403 `forbidden` for a viewer). Other error shapes:
   * 400 `invalid_acquisition_index`/`invalid_body`, 404 `not_found`, 409
   * `not_awaiting_approval` (wrong status) or `acquisition_pr_budget_exhausted`.
   */
  approveInvestigationAcquisition(id: string, acquisitionIndex: number): Promise<DiagnosticInvestigation> {
    return post<DiagnosticInvestigation>(
      `/api/investigations/${encodeURIComponent(id)}/approve`,
      { acquisition_index: acquisitionIndex } satisfies ApproveAcquisitionBody,
    )
  }

  /** GET /api/plugins — list plugins visible to the caller's role. */
  listPlugins(): Promise<{ plugins: PluginSummary[] }> {
    return request<{ plugins: PluginSummary[] }>('/api/plugins')
  }

  /** GET /api/plugins/:id — fetch a single plugin manifest (403 if not visible). */
  getPlugin(id: string): Promise<PluginSummary> {
    return request<PluginSummary>(`/api/plugins/${encodeURIComponent(id)}`)
  }

  /** GET /api/plugins/:id (full manifest with data_scopes/api_surface) */
  getPluginManifest(id: string): Promise<PluginManifest> {
    return request<PluginManifest>(`/api/plugins/${encodeURIComponent(id)}`)
  }

  /** GET /api/plugins/:id/data/:sourceId — host-mediated data proxy. */
  getPluginData(id: string, sourceId: string): Promise<{ ok: boolean; data: unknown }> {
    return request<{ ok: boolean; data: unknown }>(
      `/api/plugins/${encodeURIComponent(id)}/data/${encodeURIComponent(sourceId)}`,
    )
  }

  /** POST /api/plugins/:id/call/:action — host-mediated action proxy. */
  callPluginAction(
    id: string,
    action: string,
    input?: unknown,
  ): Promise<{ ok: boolean; status: string; [key: string]: unknown }> {
    return post<{ ok: boolean; status: string; [key: string]: unknown }>(
      `/api/plugins/${encodeURIComponent(id)}/call/${encodeURIComponent(action)}`,
      input ?? {},
    )
  }

  /** GET /api/modules */
  getModules(): Promise<{ modules: ModuleSummary[] }> {
    return request<{ modules: ModuleSummary[] }>('/api/modules')
  }

  /** GET /api/modules/:id */
  getModule(id: string): Promise<Manifest> {
    return request<Manifest>(`/api/modules/${encodeURIComponent(id)}`)
  }

  /**
   * GET /api/data-references/offers — the picker's offers for one slot
   * (ADR-127 D9-D15).
   *
   * Every offerable reference per published data set, each with the pinned schema
   * version, a SAMPLE value read from the live data, and the chip label that must
   * round-trip. The offers are GENERATED by the canonical validator beside the checks
   * that judge them, so this surface consumes them rather than deriving its own — a
   * picker with its own idea of what is offerable is the ADR-121 failure mode with
   * extra steps.
   *
   * `operands` are the in-scope runtime operands (`inputs.x`, `loop.y`,
   * `steps.z.output`) the picker may offer as a `by_key[...]` index — for a workflow
   * that is generic rather than merely single-sourced.
   */
  getDataReferenceOffers(slot: string, operands: string[] = []): Promise<DataReferenceOffers> {
    const params = new URLSearchParams({ slot })
    for (const operand of operands) params.append('operand', operand)
    return request<DataReferenceOffers>(`/api/data-references/offers?${params.toString()}`)
  }

  /**
   * POST /api/data-references/matches — every literal in the draft that equals the resolved
   * value of a published reference (ADR-179 P1/P2).
   *
   * A projection, not a new rule: the candidates are offers the generator already builds,
   * filtered by exact value-and-type equality and by the author-time checks that judge them.
   * `revision` is the caller's own monotonic counter and comes back verbatim, so a reply about
   * a draft the operator has since edited can be dropped rather than rendered.
   *
   * `locations` restricts the walk to the sites named — how an apply re-verifies the rows it is
   * about to write against the current draft and the current catalog.
   */
  dataReferenceMatches(
    draft: string,
    revision: string,
    locations?: DataReferenceMatchLocation[],
  ): Promise<DataReferenceMatches> {
    return post<DataReferenceMatches>(
      '/api/data-references/matches',
      locations ? { draft, revision, locations } : { draft, revision },
    )
  }

  /**
   * GET /api/data-references/used-by — the consumers that pin one set (ADR-127 D4).
   *
   * Exact, not heuristic: the rows come from DECLARED pins only. A 503 means the fleet could
   * not be read, and the caller must NOT render it as "nothing uses this" — that is the
   * answer which unblocks a retirement.
   */
  getDataReferenceUsedBy(set: string): Promise<DataUsedBy> {
    return request<DataUsedBy>(
      `/api/data-references/used-by?set=${encodeURIComponent(set)}`)
  }

  /** GET /api/data-references/sets — the Data area's list (D1) and the badge inputs (D5),
   *  from one source so the two cannot disagree about a set's status. */
  getDataSets(): Promise<DataSetList> {
    return request<DataSetList>('/api/data-references/sets')
  }

  /** GET /api/data-references/set — ONE set's raw schema, COMPILED schema and document
   *  (ADR-127 D2/D3).
   *
   *  404 means no such set in this fleet's registry — a typo, and deliberately NOT an empty
   *  document. 503 means the question could not be asked at all. Neither is "this set is
   *  empty", and a caller must not render them as one. */
  getDataSet(set: string): Promise<DataSetDetail> {
    return request<DataSetDetail>(
      `/api/data-references/set?set=${encodeURIComponent(set)}`)
  }

  /** POST /api/data-references/impact — would this change break a declared consumer?
   *  (ADR-127 D3 layer 2, the same D5 checks CI runs, with the proposal substituted.)
   *
   *  A 503 answers with NO `blocks_propose` field. The caller must treat that as "unchecked",
   *  never as "safe" — the endpoint omits the field precisely so a `false` cannot be misread. */
  getDataImpact(set: string, proposed: { schema?: unknown; data?: unknown }): Promise<DataImpactVerdict> {
    return post<DataImpactVerdict>('/api/data-references/impact', {
      set,
      ...(proposed.schema !== undefined ? { proposed_schema: proposed.schema } : {}),
      ...(proposed.data !== undefined ? { proposed_data: proposed.data } : {}),
    })
  }

  /** POST /api/data-references/schema/publish — publish v(N+1) of a fleet-owned set by adding
   *  fields to it (ADR-168 D4). `fleet_owner`; there is no force control.
   *
   *  `set` is the SOURCE version the operator opened. The server selects the target version
   *  itself — the operator never types a number — and refuses with `source_not_latest` when a
   *  newer version exists, so this call must not guess.
   *
   *  Resolves with the 201 on success: the new set, the branch and the PR. Every coded refusal
   *  arrives as a SchemaPublishRefusedError except the 403, which is a ForbiddenError with its
   *  route already folded into `message`. Nothing is proposed unless this resolves — the
   *  `*_unavailable` 503s exist so "we could not check" is never read as "nothing would break".
   *
   *  `repin` moves every fleet-owned consumer of the source version onto the new one and
   *  retires the version it supersedes. It is TRI-STATE on the wire: the key is OMITTED when
   *  the argument is not given, and the server reads absent as ON. A `null` — or an
   *  `undefined` left under a `repin` key — is neither of the two states the server
   *  distinguishes, so the key is added only when a boolean was passed.
   */
  async publishSchemaVersion(set: string, additions: SchemaAddition[],
    repin?: boolean): Promise<SchemaPublishResult> {
    try {
      return await post<SchemaPublishResult>('/api/data-references/schema/publish',
        repin === undefined ? { set, additions } : { set, additions, repin })
    } catch (e) {
      const refusal = parseSchemaPublishRefusal(e)
      if (refusal) throw refusal
      throw e
    }
  }

  /** POST /api/data-references/validate-draft — run the CANONICAL validator over an unsaved
   *  schema+data pair, staged over the real registry so an $id collision and the live-version
   *  cap can fire (ADR-134 D3's rule). A browser-side lookalike would answer an easier
   *  question and let the real rules surface only as a red check on the PR. */
  validateDataDraft(draft: {
    schema_path: string; data_path: string; schema: unknown; data: unknown
  }): Promise<DataDraftVerdict> {
    return post<DataDraftVerdict>('/api/data-references/validate-draft', draft)
  }

  /** GET /api/data-references/providers — the provider classes an external set may bind to
   *  (ADR-135 D5 / S8 E2), read from the bundled core-owned catalog so adding a provider needs
   *  no frontend change.
   *
   *  `providers` is OMITTED, never empty, when the catalog could not be read — empty is what a
   *  caller renders as "this platform integrates nothing". `management_data` reports whether
   *  S1's entity declarations exist at all; false means the entity must be TYPED and every
   *  `mcp:` set is mode `asserted` (S8 E6). */
  getDataSourceProviders(): Promise<DataSourceProviderList> {
    return request<DataSourceProviderList>('/api/data-references/providers')
  }

  /** POST /api/data-references/resolve-keys — ADR-135 4a's author-time lister.
   *
   *  Asks the provider what exists RIGHT NOW: `tier: 'instance'` for an entity's instances
   *  (which Asana project?) and `tier: 'fields'` for one instance's field definitions (which
   *  custom field?). `args` carries the scope the entity declares — the `fields` tier is
   *  scoped by the instance the operator just picked, so this is called twice in a cascade.
   *
   *  Always resolves; never throws for a provider-side failure. Branch on `outcome` — see
   *  ResolveKeysVerdict for why `keys` is absent rather than empty on the failures. */
  resolveDataKeys(req: {
    provider: string
    entity: string
    tier: 'instance' | 'fields'
    args?: Record<string, string>
  }): Promise<ResolveKeysVerdict> {
    return post<ResolveKeysVerdict>('/api/data-references/resolve-keys', {
      ...req, args: req.args ?? {},
    })
  }

  /** GET /api/activities — every agent's activities/*.yaml file (ADR-075 Phase 5a). */
  /** ADR-134 D10 — `drafts: 'all'` includes every operator's unproposed drafts, the
   *  same sentinel `listEditorConversations({author: 'all'})` uses. Omitting it lists
   *  only the caller's, following ADR-137 D8: the common case is resuming your own
   *  train of thought, and defaulting to everyone's would make an operator hunt for
   *  theirs in the list that is supposed to be how they find it. */
  listActivities(opts?: { drafts?: 'all' }): Promise<{ activities: ActivitySummary[] }> {
    const q = opts?.drafts === 'all' ? '?drafts=all' : ''
    return request<{ activities: ActivitySummary[] }>(`/api/activities${q}`)
  }

  /**
   * GET /api/activities/:agent/:name — raw YAML + parsed JSON + server-side
   * validation for one dsl/legacy activity (ADR-075 Phase 5a). Named
   * getWorkflowActivity (not getActivity) to avoid colliding with the
   * unrelated getActivity(limit) fleet-activity-feed method above.
   */
  getWorkflowActivity(agent: string, name: string): Promise<ActivityDetail> {
    return request<ActivityDetail>(
      `/api/activities/${encodeURIComponent(agent)}/${encodeURIComponent(name)}`,
    )
  }

  /**
   * POST /api/activities/validate — validates an unsaved draft's YAML
   * against the canonical validator (ADR-079 Phase 5b). Any authed role.
   * Never rejects on an invalid draft — `ok:false` + `errors` is the normal
   * "this draft doesn't pass yet" response; the promise only rejects on a
   * transport/auth failure.
   */
  validateWorkflowDraft(
    yamlText: string,
    slot?: { agent: string; name: string },
    files?: ProposedChangesetFile[],
  ): Promise<ValidateActivityResult> {
    // ADR-134 D3: pass the SLOT (the agent + workflow name propose will commit
    // to) so the draft is validated against the real fleet tree at its real
    // path. Without it the server falls back to the pre-D3 isolated check,
    // which cannot see an orphaned cron, skill route, or run.workflow target —
    // the class of error that made a draft report valid and then fail the gate.
    // ADR-134 D1: `files` supersedes `yaml` server-side and validates the whole
    // changeset. Passing only the activity while the gate judges the cron and
    // the skill route too is the editor/gate divergence D3 calls a defect.
    return post<ValidateActivityResult>('/api/activities/validate', {
      yaml: yamlText,
      ...(slot ? { agent: slot.agent, name: slot.name } : {}),
      ...(files && files.length > 0 ? { files } : {}),
    })
  }

  /**
   * POST /api/activities/:agent/:name/propose — commits the draft to a new
   * branch off develop and opens a PR into develop (ADR-079 Phase 5b, MC's
   * first file-authoring capability). fleet_owner ONLY (403 otherwise).
   * 400 `invalid_activity` → the draft doesn't pass validation and was never
   * proposed (parse the response body for `errors`).
   */
  /**
   * POST /api/activities/:agent/:name/propose with an ADR-134 D1 CHANGESET.
   *
   * A workflow is a node in a reference graph — crons drive it, skills route to
   * it, other activities call it — so a change to it is a changeset, not a file.
   * Adding a discovery activity that calls an existing one, or renaming a
   * workflow a cron drives, is one change to two or three files.
   *
   * The server refuses a changeset that orphans a reference (D2 cascade-or-fail)
   * and NAMES the referring files, so the fix is to widen the changeset.
   *
   * :agent/:name still identify the proposal (branch name, PR title, the
   * improvement row); they no longer constrain which files it may touch.
   */
  /**
   * GET .../session — resume in-progress work, or null when there is none
   * (ADR-134 D5).
   *
   * Before D5 the draft and the architect conversation were React state behind a
   * `beforeunload` guard, which React Router navigation does not fire. Clicking
   * away or failing a Propose discarded both — including the reasoning, which is
   * the expensive part and the thing an operator cannot retype.
   */
  async getEditorSession(
    agent: string,
    name: string,
    /** ADR-134 D10 — whose draft to read. Omitted means the signed-in caller, which is
     *  every pre-D10 call site unchanged. Naming a colleague reads THEIR draft: the read
     *  is fleet-wide because the question most worth asking is about someone else's
     *  workflow, and a per-operator draft cannot answer it.
     *
     *  Reading is not taking, and it is not copying either. A colleague's draft opens
     *  READ-ONLY: the caller writes nothing, so viewing creates no draft of their own.
     *  Check `session.operator` against the signed-in user to tell which you have.
     *
     *  The server still cannot be made to clobber — PUT ignores this param entirely —
     *  but that is a no-clobber guarantee, not a read-only one: a PUT to (agent, name)
     *  is indistinguishable from an operator legitimately starting their own draft at
     *  the same name. Read-only therefore has to hold in the UI, and does so by never
     *  opening the editor. */
    operator?: string,
    opts?: { signal?: AbortSignal },
  ): Promise<EditorSession | null> {
    const q = operator ? `?operator=${encodeURIComponent(operator)}` : ''
    const res = await requestRaw(
      `/api/activities/${encodeURIComponent(agent)}/${encodeURIComponent(name)}/session${q}`,
      opts,
    )
    if (res.status === 204) return null
    if (!res.ok) throw new Error(`session read failed (${res.status})`)
    return (await res.json()) as EditorSession
  }

  /** PUT .../session — autosave. Fire-and-forget by design: a failed autosave
   *  must not interrupt typing, but the caller should surface a persistent
   *  indicator rather than swallowing it, because silently losing work is the
   *  defect D5 exists to fix. */
  saveEditorSession(
    agent: string,
    name: string,
    session: Partial<Pick<EditorSession, 'files' | 'transcript' | 'validation' | 'base_ref' | 'conversation_id' | 'draft_id'>>,
    opts?: { signal?: AbortSignal },
  ): Promise<void> {
    return putVoid(
      `/api/activities/${encodeURIComponent(agent)}/${encodeURIComponent(name)}/session`,
      session,
      opts,
    )
  }

  /** DELETE .../session — discard. The propose path clears it server-side on
   *  success, so this is for an explicit operator discard.
   *
   *  ADR-134 D10: `operator` names whose draft to discard, following ADR-137 D9 —
   *  the author, plus `fleet_owner`. The server returns 403 `not_your_draft` to
   *  anyone else, and does not delete first; do not pre-judge it in the client,
   *  because the client's idea of a role is a claim and the server's is the gate. */
  deleteEditorSession(agent: string, name: string, operator?: string): Promise<void> {
    const q = operator ? `?operator=${encodeURIComponent(operator)}` : ''
    return del(
      `/api/activities/${encodeURIComponent(agent)}/${encodeURIComponent(name)}/session${q}`,
    )
  }

  /** GET .../conversations — the catalog, newest first (ADR-137 D4). */
/** ADR-137 D8 — the catalog is fleet-wide. `author` filters to one operator's
   *  conversations; the literal `'all'` asks for everyone's, and OMITTING it
   *  means the signed-in caller (the server's default).
   *
   *  The dock passes `'all'` and filters in the browser, so switching the filter
   *  is instant and the author list can be derived from the rows themselves. The
   *  server filter still exists and is still the contract — a client that wants
   *  one author's rows should ask for them rather than fetch the slot. */
  /** POST /api/fleet/soul/review (ADR-150 D3/D5) — the SAME override review the
   *  propose gate runs, so the editor can show it while you type instead of
   *  refusing at submit. Any authed role. */
  reviewSoul(agent: string, draft: string): Promise<SoulReviewResult> {
    return post<SoulReviewResult>('/api/fleet/soul/review', { agent, draft })
  }

  // --- ADR-150 D2: the SAME catalog for an agent's SOUL ---------------------
  //
  // Separate methods rather than a `kind` argument on the four above: the routes
  // differ in SHAPE (a workflow slot is /activities/:agent/:name, a SOUL slot is
  // /agents/:agent/soul — an agent has exactly one), so one method would have to
  // branch on kind to build its own URL. Same server handlers, same table.

  async listSoulConversations(agent: string, author?: string): Promise<ConversationSummary[]> {
    const q = author ? `?author=${encodeURIComponent(author)}` : ''
    const res = await request<{ conversations: ConversationSummary[] }>(
      `/api/agents/${encodeURIComponent(agent)}/soul/conversations${q}`,
    )
    return res.conversations ?? []
  }

  getSoulConversation(agent: string, id: string): Promise<EditorConversation> {
    return request<EditorConversation>(
      `/api/agents/${encodeURIComponent(agent)}/soul/conversations/${encodeURIComponent(id)}`,
    )
  }

  saveSoulConversation(
    agent: string,
    body: { id?: string; title?: string; messages: { role: string; content: string }[] },
  ): Promise<{ id: string; title: string }> {
    return post<{ id: string; title: string }>(`/api/agents/${encodeURIComponent(agent)}/soul/conversations`, body)
  }

  deleteSoulConversation(agent: string, id: string): Promise<void> {
    return del(`/api/agents/${encodeURIComponent(agent)}/soul/conversations/${encodeURIComponent(id)}`)
  }

  async listEditorConversations(agent: string, name: string, author?: string): Promise<ConversationSummary[]> {
    const q = author ? `?author=${encodeURIComponent(author)}` : ''
    const res = await request<{ conversations: ConversationSummary[] }>(
      `/api/activities/${encodeURIComponent(agent)}/${encodeURIComponent(name)}/conversations${q}`,
    )
    return res.conversations ?? []
  }

  /** GET .../conversations/:id — resume. Returns the transcript only. */
  getEditorConversation(agent: string, name: string, id: string, opts?: { signal?: AbortSignal }): Promise<EditorConversation> {
    return request<EditorConversation>(
      `/api/activities/${encodeURIComponent(agent)}/${encodeURIComponent(name)}/conversations/${encodeURIComponent(id)}`,
      opts,
    )
  }

  /** POST .../conversations — create (omit id) or update in place. The server
   *  derives the title from the first user turn when none is given (D5). */
  saveEditorConversation(
    agent: string,
    name: string,
    body: { id?: string; title?: string; messages: { role: string; content: string; job_id?: string }[] },
  ): Promise<{ id: string; title: string }> {
    return post<{ id: string; title: string }>(
      `/api/activities/${encodeURIComponent(agent)}/${encodeURIComponent(name)}/conversations`,
      body,
    )
  }

  /** DELETE .../conversations/:id — discard one. */
  deleteEditorConversation(agent: string, name: string, id: string): Promise<void> {
    return del(
      `/api/activities/${encodeURIComponent(agent)}/${encodeURIComponent(name)}/conversations/${encodeURIComponent(id)}`,
    )
  }

  /** `conversationId` is ADR-137 D3: the conversation that produced this
   *  proposal, archived with the PR number instead of being deleted. Optional —
   *  a change authored by hand has none, and the server treats absent as "not
   *  attributable" rather than guessing. */
  /** POST /api/activities/vars-offers (ADR-153 D3) — the workflow-local values
   *  in scope at `task`, with each token already spelled for `slot`.
   *
   *  A POST because the DRAFT is the subject: these come from `set` tasks that
   *  may never have been proposed. */
  varsOffers(draft: string, task: string, slot: string): Promise<VarsOffers> {
    return post<VarsOffers>('/api/activities/vars-offers', { draft, task, slot })
  }

  proposeWorkflowChangeset(
    agent: string,
    name: string,
    files: ProposedChangesetFile[],
    message: string,
    conversationId?: string,
    draftId?: string,
  ): Promise<ProposeActivityResult> {
    return post<ProposeActivityResult>(
      `/api/activities/${encodeURIComponent(agent)}/${encodeURIComponent(name)}/propose`,
      { files, message, ...(conversationId ? { conversation_id: conversationId } : {}), ...(draftId ? { draft_id: draftId } : {}) },
    )
  }

  proposeWorkflowActivity(
    agent: string,
    name: string,
    yamlText: string,
    message?: string,
    conversationId?: string,
    draftId?: string,
  ): Promise<ProposeActivityResult> {
    return post<ProposeActivityResult>(
      `/api/activities/${encodeURIComponent(agent)}/${encodeURIComponent(name)}/propose`,
      { yaml: yamlText, message: message ?? '', ...(conversationId ? { conversation_id: conversationId } : {}), ...(draftId ? { draft_id: draftId } : {}) },
    )
  }

  /**
   * POST /api/activities/copilot — the ADR-079 Phase 5d AI copilot: given a
   * target agent, the running chat history, and (optionally) the draft
   * already loaded in the editor, returns a VALIDATED draft + rationale —
   * the same validator as validateWorkflowDraft/proposeWorkflowActivity, so
   * the copilot's output is never trusted blind (D4). operator|fleet_owner;
   * never writes or executes anything — Propose is the separate write path.
   *
   * Throws CopilotNotConfiguredError on 503 (no Anthropic key for this
   * fleet) — callers should show the manual (Field/YAML) modes as the
   * fallback rather than a generic error.
   */
  /**
   * GET .../copilot/in-flight — is a turn already running for this workflow?
   *
   * ADR-160 D2. Asked on open, because `chatSending` and the poll loop are page state:
   * navigate away during a 110s–710s turn and the editor came back looking idle while
   * the job ran on. An operator who cannot tell "running" from "nothing happened"
   * re-sends, spending another multi-minute turn on the same slot.
   */
  copilotInFlight(agent: string, name: string): Promise<CopilotInFlight> {
    return request<CopilotInFlight>(
      `/api/activities/${encodeURIComponent(agent)}/${encodeURIComponent(name)}/copilot/in-flight`,
    )
  }

  /**
   * Re-attach to a turn that is already running (ADR-160 D2).
   *
   * Same poll loop as a fresh turn, seeded with the SERVER's elapsed time — a client
   * that counted from zero would tell the operator a four-minute turn had just started.
   */
  copilotReattach(
    jobId: string,
    alreadyElapsedMs: number,
    onProgress?: (elapsedMs: number) => void,
  ): Promise<CopilotResponse> {
    return pollLLMJob<CopilotResponse>(
      jobId,
      `/api/activities/copilot/status/${encodeURIComponent(jobId)}`,
      onProgress,
      alreadyElapsedMs,
    )
  }

  copilotAuthor(
    agent: string,
    messages: CopilotMessage[],
    currentDraft?: string,
    intent: CopilotIntent = 'edit',
    onProgress?: (elapsedMs: number) => void,
    name?: string,
    focus?: string,
    /** ADR-183 Phase 6 — answer this turn on a different model. Omitted keeps
     *  the deployment's configured one. */
    model?: { provider: string; model: string },
    draftId?: string,
    /** ADR-200 amendment — the per-run scoping choice; see copilotAuthorRequest. */
    scoping?: boolean,
    /** Called once the server has taken the turn, before any polling; never for a refusal. */
    onAccepted?: () => void,
  ): Promise<CopilotResponse> {
    return copilotAuthorRequest(
      agent, messages, currentDraft ?? '', intent, onProgress, name, focus, model, draftId, scoping, onAccepted)
  }

  /** Retry only the transcript save for an answer retained by a completed job. */
  retryCopilotSave(jobId: string): Promise<void> {
    return post<void>(`/api/activities/copilot/jobs/${encodeURIComponent(jobId)}/save`, {}).catch(err => {
      if (err instanceof Error && err.message.startsWith('HTTP 409')) {
        throw new UnsavedAnswerGoneError()
      }
      throw err
    })
  }

  /**
   * POST /api/agents/:agent/skills/:slug/copilot (ADR-094 P1.5) — the SKILL.md
   * copilot: edits a router skill (format for human+AI, flag ambiguity, suggest
   * prose→workflow refactors, maintain `routes:`), grounded in the agent's real
   * workflows and gated by the skill validator. operator|fleet_owner; never
   * writes (proposeSkill is the write path). Throws CopilotNotConfiguredError
   * on 503 (no Anthropic key).
   */
  skillCopilot(
    agent: string,
    slug: string,
    messages: CopilotMessage[],
    currentDraft?: string,
    onProgress?: (elapsedMs: number) => void,
  ): Promise<SkillCopilotResponse> {
    return skillCopilotRequest(agent, slug, messages, currentDraft ?? '', onProgress)
  }

  /** POST /api/agents/:agent/soul/copilot (ADR-150 D2) — author an agent's
   *  layer-4 SOUL by chat. Grounded in the core layers it composes under, and
   *  repaired against the same override review the propose gate runs.
   *  operator|fleet_owner; never writes. */
  soulCopilot(
    agent: string,
    messages: CopilotMessage[],
    currentDraft?: string,
    onProgress?: (elapsedMs: number) => void,
  ): Promise<SoulCopilotResponse> {
    return soulCopilotRequest(agent, messages, currentDraft ?? '', onProgress)
  }

  /**
   * POST /api/agents/:agent/skills/:slug/critique (ADR-121 D2) — the
   * "Critique" action: a grounded, read-only review of the skill returning
   * actionable suggestions (never a rewrite). Reuses the SAME grounding
   * bundle + completer as skillCopilot (ADR-121 D3). Pass `content` to
   * critique the editor's current (possibly unsaved) draft instead of what's
   * saved on disk. operator|fleet_owner; never writes. Throws
   * CopilotNotConfiguredError on 503 (no Anthropic key).
   */
  critiqueSkill(
    agent: string,
    slug: string,
    content?: string,
    onProgress?: (elapsedMs: number) => void,
  ): Promise<SkillCritiqueResponse> {
    return skillCritiqueRequest(agent, slug, content, onProgress)
  }

  /**
   * POST /api/activities/convert — ADR-080 WS3 conversion copilot. mode="plan"
   * returns a reviewable decomposition plan (no code); mode="materialize"
   * (with the accepted `plan` JSON) returns the leaner skill + N activity
   * YAMLs, each DSL-validated. Never writes — that's proposeConversion.
   */
  /**
   * GET /api/agents/skills — agents that have >=1 skill, each with its skills
   * (slug + display name/description). Backs the Convert studio pickers so the
   * operator selects a real agent + skill instead of typing a slug blind.
   */
  listAgentSkills(): Promise<{ agents: AgentSkills[] }> {
    return request<{ agents: AgentSkills[] }>('/api/agents/skills')
  }

  /**
   * GET /api/agents/:agent/skills/:slug (ADR-094 D6) — one skill's full read
   * model (raw SKILL.md + parsed frontmatter + body). Backs the skill-as-router
   * studio's view/edit/copilot surface (the skill-side GetActivity).
   */
  getSkill(agent: string, slug: string): Promise<SkillContent> {
    return request<SkillContent>(`/api/agents/${encodeURIComponent(agent)}/skills/${encodeURIComponent(slug)}`)
  }

  /**
   * POST /api/agents/:agent/skills/:slug/validate (ADR-094 D2) — structurally
   * validate an unsaved SKILL.md draft. The skill-side validateWorkflowDraft;
   * backs the skill editor's live validation + the propose gate.
   */
  validateSkill(agent: string, slug: string, content: string): Promise<SkillValidation> {
    return post<SkillValidation>(`/api/agents/${encodeURIComponent(agent)}/skills/${encodeURIComponent(slug)}/validate`, { content })
  }

  /**
   * POST /api/agents/:agent/skills/:slug/propose (ADR-094 D6, fleet_owner) —
   * propose a single edited SKILL.md as one PR + in-app improvement. The
   * skill-side proposeWorkflowActivity.
   */
  proposeSkill(
    agent: string,
    slug: string,
    content: string,
    message?: string,
    files?: ProposedChangesetFile[],
  ): Promise<ProposeSkillResult> {
    // `content` is the SKILL.md write and is omitted when empty: a body carrying
    // only deletions is valid, and sending an empty draft alongside them would
    // propose erasing the skill's own definition.
    const body: { content?: string; message?: string; files?: ProposedChangesetFile[] } = { message }
    if (content !== '') body.content = content
    if (files && files.length > 0) body.files = files
    return post<ProposeSkillResult>(`/api/agents/${encodeURIComponent(agent)}/skills/${encodeURIComponent(slug)}/propose`, body)
  }

  /**
   * POST /api/agents/:agent/skills/:slug/rename (ADR-094, fleet_owner) — move the
   * skill directory to new_slug (rewriting SKILL.md's name) as one PR + improvement.
   */
  renameSkill(agent: string, slug: string, newSlug: string, message?: string): Promise<ProposeSkillResult> {
    return post<ProposeSkillResult>(`/api/agents/${encodeURIComponent(agent)}/skills/${encodeURIComponent(slug)}/rename`, { new_slug: newSlug, message })
  }

  /**
   * POST /api/agents/:agent/skills/:slug/delete (ADR-094, fleet_owner) — remove the
   * skill directory as one PR + improvement. Nothing is deleted until approved.
   */
  deleteSkill(agent: string, slug: string, message?: string): Promise<ProposeSkillResult> {
    return post<ProposeSkillResult>(`/api/agents/${encodeURIComponent(agent)}/skills/${encodeURIComponent(slug)}/delete`, { message })
  }

  /**
   * POST /api/fleet/delete/plan (ADR-123 D2, read-only) — the full impact of
   * deleting one entity. Writes NOTHING; this is the "verify-zero" step an
   * operator reads before anything is proposed.
   */
  deletePlan(target: DeleteTarget): Promise<DeletePlan> {
    return post<DeletePlan>('/api/fleet/delete/plan', target)
  }

  /**
   * POST /api/fleet/delete/prose-repair (ADR-123 D6, fleet_owner) — ask the
   * Fleet Architect to rewrite the files that name the entity in prose.
   * Writes nothing; the rewrites come back for review and are then passed to
   * deletePropose. A 503 means no model is configured, which does NOT block the
   * delete — those files are just handled by hand.
   */
  deleteProseRepair(
    target: DeleteTarget,
    replacement?: string,
    onProgress?: (elapsedMs: number) => void,
  ): Promise<ProseRepairResponse> {
    // Background job (see llmJobRequest): the repair runs up to 2 heavy
    // completions PER FILE over every prose reference the delete plan found, so
    // its worst case is a multiple of Cloudflare's 100s origin ceiling. Held
    // synchronous it lost every generated rewrite to an unparseable 524.
    return llmJobRequest<ProseRepairResponse>(
      '/api/fleet/delete/prose-repair',
      { ...target, replacement },
      id => `/api/fleet/delete/prose-repair/status/${encodeURIComponent(id)}`,
      onProgress,
    )
  }

  /**
   * POST /api/fleet/delete/propose (ADR-123 D2, fleet_owner) — the write step.
   * One branch -> one PR -> one in-app improvement, carrying the containment
   * sweep, every mechanical repair, the regenerated brain roster, and any
   * accepted prose rewrites. Nothing is removed until the PR is approved.
   *
   * Throws DeleteBlockedError on 409: executable references still point at the
   * target. There is deliberately no force option (ADR-123 D5).
   */
  async deletePropose(
    target: DeleteTarget,
    opts?: { message?: string; proseRepairs?: Record<string, string> },
  ): Promise<ProposeSkillResult> {
    try {
      return await post<ProposeSkillResult>('/api/fleet/delete/propose', {
        ...target,
        message: opts?.message,
        prose_repairs: opts?.proseRepairs,
      })
    } catch (e) {
      const blocked = parseDeleteBlocked(e)
      if (blocked) throw blocked
      const duplicate = parseDeleteAlreadyProposed(e)
      if (duplicate) throw duplicate
      throw e
    }
  }

  /** GET /api/crons/prompt — the fleet's convertible prompt-crons (both layers). */
  listPromptCrons(): Promise<{ crons: PromptCron[] }> {
    return request<{ crons: PromptCron[] }>('/api/crons/prompt')
  }

  /**
   * GET /api/fleet/version (ADR-101) — the current commit SHA of MC's /fleet
   * checkout. The Studio polls this to notice when published content changed
   * under an open editor (ADR-100 timer or ADR-101 webhook) and react.
   */
  fleetVersion(): Promise<{ sha: string; branch: string }> {
    return request<{ sha: string; branch: string }>('/api/fleet/version')
  }

  /**
   * GET /api/workflows/pending (ADR-114 D2) — operator|fleet_owner. Backs
   * the /workflows pending banner: "N changes staged on develop and not
   * yet live". Polled the same way useFleetVersion polls /api/fleet/version.
   */
  /**
   * @deprecated ADR-122 D8 — SUPERSEDED by getPendingChanges below, and no
   * longer called by any page. It answered "is something waiting to go live?"
   * with a repo-wide commit count (merge commits and automated platform syncs
   * included), which is the wrong object; the Ready worklist answers it with
   * named changes. Kept because the backend route still exists and may have
   * consumers outside this client — do not add new callers.
   */
  getWorkflowsPending(): Promise<WorkflowsPendingStatus> {
    return request<WorkflowsPendingStatus>('/api/workflows/pending')
  }

  /**
   * GET /api/changes/pending (ADR-122 D1a/D1c/D1d) — operator|fleet_owner.
   * The Ready worklist: named Changes waiting to go live, in operator language,
   * whatever created them. This is what replaces getWorkflowsPending's
   * repo-wide commit count as the thing an operator is shown (D8).
   */
  getPendingChanges(): Promise<PendingChangesStatus> {
    return request<PendingChangesStatus>('/api/changes/pending')
  }

  /**
   * GET /api/fleet/pending-paths (ADR-111 D2) — operator|fleet_owner. Backs
   * the Fleet Editor's per-artifact "staged" badges (see
   * FleetPendingPathsStatus) — fetched once per facet mount, the same
   * lightweight poll-once pattern getWorkflowsPending uses for its banner.
   */
  getFleetPendingPaths(): Promise<FleetPendingPathsStatus> {
    return request<FleetPendingPathsStatus>('/api/fleet/pending-paths')
  }

  /**
   * POST /api/workflows/deploy (ADR-114 D2) — fleet_owner ONLY. Promotes
   * develop→main directly (the same github.Client.Promote primitive
   * /improvements' Approve & Deploy uses), for changes stranded on develop
   * with no PR to review.
   */
  deployWorkflowsPending(): Promise<DeployWorkflowsPendingResult> {
    return post<DeployWorkflowsPendingResult>('/api/workflows/deploy', {})
  }

  /**
   * GET /api/promotion/status (ADR-130 D4) — what is actually happening to an
   * in-flight promotion. Cheap by design (the reconciler's last resolved view,
   * not a live GitHub call), so the worklist can poll it while one is open.
   */
  getPromotionStatus(): Promise<PromotionStatus> {
    return request<PromotionStatus>('/api/promotion/status')
  }

  /**
   * POST /api/crons/convert/propose (fleet_owner) — commit the drafted workflow
   * AND rewrite the cron `prompt:`→`activity:`, as one PR + in-app improvement.
   */
  proposeCronConversion(
    agent: string,
    cronName: string,
    layer: 'fleet' | 'agent',
    activity: { name: string; yaml: string },
    message?: string,
  ): Promise<ProposeSkillResult> {
    return post<ProposeSkillResult>('/api/crons/convert/propose', { agent, cron_name: cronName, layer, activity, message })
  }

  /**
   * POST /api/crons/validate (ADR-094 ext) — live field/structure validation of
   * a proposed cron, the same rules validate-crons.py enforces.
   */
  validateCron(input: CronInput): Promise<{ ok: boolean; errors: string[] }> {
    return post<{ ok: boolean; errors: string[] }>('/api/crons/validate', input)
  }

  /**
   * POST /api/crons/propose (fleet_owner) — create or edit a cron (deterministic
   * YAML write into the layer's cron file) as one PR + in-app improvement.
   */
  proposeCron(input: CronInput, message?: string): Promise<ProposeSkillResult> {
    return post<ProposeSkillResult>('/api/crons/propose', { ...input, message })
  }

  /** GET /api/crons — every cron (any kind, both layers) for the management list. */
  listCrons(): Promise<{ crons: CronSummary[] }> {
    return request<{ crons: CronSummary[] }>('/api/crons')
  }

  /** POST /api/crons/delete (fleet_owner) — remove a cron as a PR + improvement. */
  deleteCron(input: { layer: 'fleet' | 'agent'; agent: string; name: string }, message?: string): Promise<ProposeSkillResult> {
    return post<ProposeSkillResult>('/api/crons/delete', { ...input, message })
  }

  /**
   * GET /api/agents — the full fleet roster (all agents in fleet.yaml,
   * regardless of whether they have activities yet). Backs the "New workflow"
   * agent picklist, which must offer every agent (you scaffold a workflow
   * precisely for an agent that has none).
   */
  listAgents(): Promise<{ agents: string[] }> {
    return request<{ agents: string[] }>('/api/agents')
  }

  /**
   * GET /api/agents/:agent — the ONE agent's current profile fields (model,
   * enabled). A sibling of listAgents() rather than a widened response
   * shape, since two pickers depend on listAgents()'s exact `{agents:
   * string[]}` body. Backs the Fleet Editor's Profile facet pre-select
   * (ADR-111 IA-4): the current model/enabled, not always "keep current".
   */
  getAgentProfile(agent: string): Promise<AgentProfile> {
    return request<AgentProfile>(`/api/agents/${encodeURIComponent(agent)}`)
  }

  /**
   * GET /api/models (ADR-182 D2/D5) — the generated model catalog grouped by
   * vendor, each vendor carrying whether this fleet holds its credential.
   *
   * ONE read, not two: a picker that rendered the model list before the
   * credential status arrived would briefly offer a vendor this fleet cannot
   * reach, and "briefly wrong in the permissive direction" is how a bad choice
   * gets made.
   */
  getModelCatalog(): Promise<ModelCatalogResponse> {
    return request<ModelCatalogResponse>('/api/models')
  }

  /**
   * GET /api/assistant-model — which model answers this fleet's Workflow
   * Assistant, and which layer chose it (ADR-194).
   *
   * Readable by any authenticated role, viewer included: which model authors a
   * fleet is not a privileged fact, and hiding it from the people most likely
   * to ask about a result is how the pin became undiscoverable in the first place.
   */
  getAssistantModel(): Promise<AssistantModelView> {
    return request<AssistantModelView>('/api/assistant-model')
  }

  /**
   * GET /api/assistant-model/fleet-file — the proposable content for a FLEET pin
   * (ADR-194 D2, fleet_owner).
   *
   * Composes; it does not write. The content is rendered server-side beside the
   * reader, so what a proposal contains is what the runtime will later read —
   * a renderer in the browser could drift from the parser in Go without either
   * changing. The caller posts this through the ONE propose lane
   * (`proposeFleetFile`), so the fleet pin still lands as a reviewed PR.
   */
  getFleetPinTemplate(provider: string, model: string): Promise<{
    path: string; content: string; message: string
  }> {
    const qs = new URLSearchParams({ provider, model }).toString()
    return request<{ path: string; content: string; message: string }>(
      `/api/assistant-model/fleet-file?${qs}`)
  }

  /**
   * PUT /api/assistant-model/me — pin a model for THE CALLER.
   *
   * There is no operator parameter and there must never be one: the backend
   * takes the identity from the verified token and its request body has no
   * field for an owner, so a caller has nowhere to name someone else.
   */
  setMyAssistantModel(pin: { provider: string; model: string }): Promise<void> {
    return putVoid('/api/assistant-model/me', pin)
  }

  /** DELETE /api/assistant-model/me — back to the fleet's pin, or the default. */
  clearMyAssistantModel(): Promise<void> {
    return del('/api/assistant-model/me')
  }

  /**
   * GET /api/agents/:agent/capabilities — the agent's BOUND MCP capabilities,
   * joined against the bundled platform-manifest catalog (ADR-111 D4/D5
   * addendum phase 1). Read-only; binding lands in phase 3.
   *
   * Replaces the Capabilities facet's cross-link placeholder. The data was
   * always there — internal/copilot has joined the same two files since
   * ADR-079 D3 — only the route was missing.
   */
  getAgentCapabilities(agent: string): Promise<AgentCapabilities> {
    return request<AgentCapabilities>(`/api/agents/${encodeURIComponent(agent)}/capabilities`)
  }

  /** GET /api/capabilities — the platform catalog to enable FROM (ADR-111 3c-iii). */
  listCapabilityCatalog(): Promise<{ capabilities: CapabilityCatalogEntry[] }> {
    return request<{ capabilities: CapabilityCatalogEntry[] }>('/api/capabilities')
  }

  /**
   * POST /api/agents/:agent/capabilities/propose — bind or unbind a capability
   * as one reviewable changeset (ADR-111 3c-ii). fleet_owner only.
   *
   * Enable needs Console (the credential resolution lives there) and 503s if it
   * is unreachable; disable is pure removal and keeps working regardless.
   */
  proposeCapability(
    agent: string,
    input: { capability: string; action: 'enable' | 'disable'; message?: string },
  ): Promise<CapabilityProposeResult> {
    return post<CapabilityProposeResult>(
      `/api/agents/${encodeURIComponent(agent)}/capabilities/propose`, input)
  }

  /**
   * GET /api/fleet/core-agents (ADR-103) — the manifest-derived core-agent
   * set, sorted and always non-null. Used to exclude core agents from every
   * "New X" authoring picker (New skill/workflow/cron, Convert) without
   * widening listAgents()'s plain string[] shape, which two pickers already
   * consume as-is. Core agents still appear in read-only browse lists.
   */
  listCoreAgents(): Promise<{ agents: string[] }> {
    return request<{ agents: string[] }>('/api/fleet/core-agents')
  }

  /** GET /api/fleet/platform-skills — universal platform skills and their roster bindings. */
  listPlatformSkills(): Promise<PlatformSkills> {
    return request<PlatformSkills>('/api/fleet/platform-skills')
  }

  /**
   * GET /api/fleet/tree (ADR-111 D2.1, operator|fleet_owner) — every agent's
   * overlay files (SOUL.md, skills, crons, activities, …), the shared
   * resources/ folder, and config/fleet.yaml, grouped for the Fleet Editor's
   * Files tab left rail.
   */
  getFleetTree(): Promise<FleetTree> {
    return request<FleetTree>('/api/fleet/tree')
  }

  /**
   * GET /api/fleet/file?path=… (ADR-111 D2.2a, operator|fleet_owner) — read
   * one overlay file by its repo-relative path. A structured 404 with
   * `found: false` is the normal answer for a file that does not exist yet.
   */
  getFleetFile(path: string): Promise<FleetFile> {
    return readFleetFile(path)
  }

  /**
   * POST /api/fleet/file/propose (ADR-111 D2.2b, fleet_owner) — propose an
   * edit to one overlay file at an arbitrary repo-relative path, as one PR +
   * in-app improvement. Refused (core_owned) for a core-owned target (ADR-103).
   */
  /**
   * POST /api/fleet/file/propose with a CHANGESET (ADR-134 D1) — N overlay files as ONE
   * branch, ONE PR, ONE in-app improvement.
   *
   * Use this whenever a change is only coherent as a set. The case it exists for: creating a
   * reference-data set is TWO files, because a schema with no data document is valid to CI but
   * invisible to the Data area — proposing them separately produces a PR whose result the
   * operator cannot see, then a second PR to fix it.
   *
   * `op` is write-only here. A delete goes through the ADR-123 lane
   * (POST /api/fleet/delete/propose), which computes the reference cascade this endpoint
   * cannot; asking for one here is refused by name rather than downgraded to a write.
   */
  proposeFleetChangeset(
    files: { path: string; content: string }[],
    message?: string,
  ): Promise<ProposeSkillResult> {
    return post<ProposeSkillResult>('/api/fleet/file/propose', {
      files: files.map(f => ({ ...f, op: 'write' })),
      message,
    })
  }

  /**
   * POST /api/improvements/:id/pr/rerun — re-fire this PR's failed check runs.
   * The ADR-130 D3 primitive the promotion reconciler uses, extended to
   * improvement PRs so a failure is recoverable without leaving the product.
   * `rerun: 0` is a real answer (nothing was re-runnable), not an error.
   *
   * `refused` counts the runs the server tried to start and could not, and it
   * is why the shape is not just a total: a 200 can be a PARTIAL re-run, and
   * reporting `rerun` alone told the operator a clean re-run had happened while
   * a check they were waiting for had never been started. `detail` is the
   * sentence to show in that case and is present only when `refused > 0`.
   */
  rerunImprovementPRChecks(id: string): Promise<RerunImprovementPRChecksResult> {
    return post<RerunImprovementPRChecksResult>(`/api/improvements/${encodeURIComponent(id)}/pr/rerun`, {})
  }

  /**
   * POST /api/improvements/:id/pr/update-branch — merge the fleet's own branch
   * into this change's branch, so the next check run tests the change against
   * today's fleet.
   *
   * Not a re-run: GitHub replays a run at the merge commit pinned in the
   * original event, so a fix that landed on the fleet afterwards is not in it
   * and the run fails identically.
   *
   * `accepted` is scheduled, not done — GitHub answers 202 and carries no sha,
   * so `head_sha_at_request` is the commit the update was aimed at and never a
   * new head. A 409 carries `head_moved`, `update_refused`, `not_open` or
   * `head_elsewhere`; a core change is refused 403 `core_owned`.
   */
  updateImprovementPRBranch(id: string): Promise<UpdateImprovementPRBranchResult> {
    return post<UpdateImprovementPRBranchResult>(
      `/api/improvements/${encodeURIComponent(id)}/pr/update-branch`,
      {}
    )
  }

  /** GET /api/fleet/in-flight (ADR-167 D1) — everything running on this fleet now.
   *  `working` / `unreadable` / `not_configured` are COUNTED by the server, because
   *  "nothing is running" must never be derived from an empty array (D3). */
  cardAction(id: string, action: string, body: Record<string, unknown>): Promise<unknown> {
    return post('/api/fleet/in-flight/cards/'+encodeURIComponent(id)+'/'+encodeURIComponent(action), body)
  }

  fleetInFlight(): Promise<InFlightResult> {
    return request<InFlightResult>('/api/fleet/in-flight')
  }

  /** GET /api/activities/authoring-run (ADR-166 D7) — the phase state of MY run on
   *  this slot.
   *
   *  `not_configured` is a SEPARATE field from a null run on purpose. "this Mission
   *  Control does not record phase state" and "this workflow has no run" are different
   *  facts, and a caller that could not tell them apart would show an operator that
   *  their run vanished when it was never recorded. */
  authoringRun(agent: string, workflow: string, runOperator?: string): Promise<AuthoringRunResult> {
    // ADR-200 D6 — `runOperator` reads an UNATTENDED run (keyed on the machine identity),
    // the one run other than their own a person may read. Omitted, it is the caller's own.
    const op = runOperator ? `&operator=${encodeURIComponent(runOperator)}` : ''
    return request<AuthoringRunResult>(
      `/api/activities/authoring-run?agent=${encodeURIComponent(agent)}&workflow=${encodeURIComponent(workflow)}${op}`)
  }

  /** POST /api/activities/authoring-run/decide (ADR-166 D6) — continue, redirect, stop,
   *  or ANSWER. The decider is the authenticated caller server-side; deliberately NOT a
   *  field here, so a client cannot attribute a decision to someone else.
   *
   *  `answer` is D6b's payload and it was missing from this client entirely: the route
   *  and `DecisionAnswer` have accepted one since D6b, and nothing in the app ever sent
   *  it — so an operator answering a needs-information question had to put the fact in
   *  `redirect`, where the next turn reads it as an instruction to change direction
   *  rather than as evidence it may cite. Its own field, for that reason. */
  decideAuthoringCheckpoint(
    agent: string, workflow: string, decision: string, redirect: string, answer?: string,
    runOperator?: string, fenceLiftId?: string,
    taskReplan?: { id: string; tasks: string[] },
  ): Promise<AuthoringRunResult> {
    // `fence_lift_id` names the offer a `lift_fence` answers; the server refuses a lift whose
    // offer is no longer the one waiting (ADR-200 D7, unscoped runs).
    return post<AuthoringRunResult>('/api/activities/authoring-run/decide',
      { agent, workflow, decision, redirect, answer: answer ?? '',
        ...(runOperator ? { run_operator: runOperator } : {}),
        ...(fenceLiftId ? { fence_lift_id: fenceLiftId } : {}),
        ...(taskReplan ? { task_replan_id: taskReplan.id, tasks: taskReplan.tasks } : {}) })
  }

  /** POST /api/activities/authoring-run/decide (ADR-200 stage 1) — the `"brief"`
   *  decision: answer some or all of a scoping round's open questions, and/or confirm
   *  the draft brief.
   *
   *  Posted on the SAME route as decideAuthoringCheckpoint, above, with
   *  `decision: "brief"`. A separate method rather than another optional argument on
   *  that one: `answers` is a map keyed by question id because a scoping round can carry
   *  several open questions at once, where `decideAuthoringCheckpoint`'s `answer` is
   *  ADR-181 D6b's single-question shape and does not fit.
   *
   *  `answers[id] === ""` means "accept the proposed default for that question" — the
   *  build contract's own reading of a blank value, not this client's invention. */
  decideAuthoringBrief(
    agent: string, workflow: string, answers: Record<string, string>, confirm: boolean,
    runOperator?: string,
  ): Promise<AuthoringRunResult> {
    // ADR-200 D6 — `runOperator` decides an UNATTENDED run's brief. The server records the
    // AUTHENTICATED caller as the one who answered or confirmed, never the run's machine key.
    return post<AuthoringRunResult>('/api/activities/authoring-run/decide',
      { agent, workflow, decision: 'brief', answers, confirm,
        ...(runOperator ? { run_operator: runOperator } : {}) })
  }

  /** Keep the agreed brief exactly as-is and rerun the phase that raised its amendment. */
  reconfirmAuthoringBrief(
    agent: string, workflow: string, runOperator?: string,
  ): Promise<AuthoringRunResult> {
    return post<AuthoringRunResult>('/api/activities/authoring-run/decide',
      { agent, workflow, decision: 'brief', keep_unchanged: true,
        ...(runOperator ? { run_operator: runOperator } : {}) })
  }

  /** POST /api/activities/authoring-run/brief/store (ADR-200 D6) — save a run's CONFIRMED
   *  brief so an unattended start can reference it (`brief_ref`) instead of parking for a
   *  person. Returns the stored brief, whose `id` is the reference. */
  storeAuthoringBrief(
    agent: string, workflow: string, runOperator?: string,
  ): Promise<{ stored_brief: { id: string; confirmed_by: string; confirmed_at: string } }> {
    return post('/api/activities/authoring-run/brief/store',
      { agent, workflow, ...(runOperator ? { run_operator: runOperator } : {}) })
  }

  /** POST /api/activities/authoring-run/reset (ADR-167 D8) — discard the phase state on
   *  this slot so the next turn starts a NEW run.
   *
   *  A run is keyed (agent, workflow, operator) and otherwise lives forever, so a run that
   *  finished — or wedged itself at a phase after a 1200s timeout — is what every later
   *  turn merges into. Without this the Process tab has no way back to empty, which is the
   *  dead end D8 says is a design bug.
   *
   *  Clears ONLY the phase state. The draft, the transcript and the conversation are the
   *  editor's and are left alone. */
  async resetAuthoringRun(agent: string, workflow: string, expectedRunId?: string): Promise<{ reset: boolean }> {
    try {
      return await post<{ reset: boolean }>('/api/activities/authoring-run/reset', {
        agent, workflow, ...(expectedRunId ? { expected_run_id: expectedRunId } : {}),
      })
    } catch (err) {
      throw runChangedFrom(err) ?? err
    }
  }

  /** POST /api/activities/authoring-run/cancel — stop a run, in any state.
   *
   *  DISTINCT FROM resetAuthoringRun, and the difference is the whole point. Reset clears
   *  the slot and is DISABLED while a turn is running, because discarding the phase state
   *  does not stop the turn: the work finishes and lands nowhere. Cancel stops the run and
   *  KEEPS the record, and Mission Control discards the in-flight turn's result when it
   *  lands — so it is safe, and offered, mid-turn. That state (a 75-minute turn after a
   *  container restart) is the one an operator most needs to escape and had no exit from. */
  cancelAuthoringRun(agent: string, workflow: string): Promise<{ cancelled: boolean; outcome?: string }> {
    return post<{ cancelled: boolean; outcome?: string }>(
      '/api/activities/authoring-run/cancel', { agent, workflow })
  }

  /** POST /api/activities/authoring-run/auto-advance (ADR-178 D5) — pause, resume or
   *  advance now.
   *
   *  The countdown itself is SERVER-SIDE by decision (D1): this asks Mission Control to
   *  change its own timer, it does not run one. A browser that owned the clock would stop
   *  the rail silently when the tab closed, which is the failure shape the ADR rejects. */
  autoAdvance(agent: string, workflow: string, action: 'pause' | 'resume' | 'next'):
    Promise<{ action: string; mode: string; next_turn_at?: string; phase?: string }> {
    return post('/api/activities/authoring-run/auto-advance', { agent, workflow, action })
  }

  /** GET /api/activities/authoring-runs (ADR-177 D1/D3) — the run HISTORY.
   *
   *  Deliberately not the same call as authoringRun() above, which answers "what is MY
   *  run on this slot doing right now" and is operator-scoped. History is read
   *  fleet-wide, the same asymmetry ADR-137 applies to the conversation catalog: a
   *  history only its author could read is useless for the thing it exists for, which is
   *  a second person working out why a stage keeps refusing.
   *
   *  Both filters empty is the fleet-wide index. */
  authoringRunList(
    opts: { agent?: string; workflow?: string; limit?: number } = {},
  ): Promise<AuthoringRunListResult> {
    const q = new URLSearchParams()
    if (opts.agent) q.set('agent', opts.agent)
    if (opts.workflow) q.set('workflow', opts.workflow)
    if (opts.limit) q.set('limit', String(opts.limit))
    const s = q.toString()
    return request<AuthoringRunListResult>(`/api/activities/authoring-runs${s ? `?${s}` : ''}`)
  }

  /** GET /api/activities/attribution-coverage (ADR-183 Phase 5b) — how much of MC's
   *  own recorded work carries model attribution, per table, over a window.
   *
   *  REPORTS THE GAP RATHER THAN HIDING IT. Three of the five tables with
   *  attribution columns have no writer, because the model that produces their
   *  content runs on the FLEET side (a delegation) and DelegateReply does not
   *  carry it. Each such row says so, and why — so a future regression in a
   *  working writer cannot be mistaken for the status quo.
   *
   *  The window is bounded (default 24h) on purpose: an unbounded report averages
   *  today's rows against every row written before attribution shipped, which
   *  hides whether the writer works today. */
  attributionCoverage(hours?: number): Promise<AttributionCoverageResult> {
    const q = hours && hours > 0 ? `?hours=${encodeURIComponent(String(hours))}` : ''
    return request<AttributionCoverageResult>(`/api/activities/attribution-coverage${q}`)
  }

  /** GET /api/activities/authoring-runs/:id (ADR-177 D3) — one run, with the document
   *  the tree renders from. 404 when there is no such run; a read failure is a 500, and
   *  the two must not be collapsed. */
  authoringRunDetail(id: string): Promise<AuthoringRunDetailResult> {
    return request<AuthoringRunDetailResult>(
      `/api/activities/authoring-runs/${encodeURIComponent(id)}`)
  }

  /** GET /api/fleet/file — one file from the fleet overlay as it stands on disk.
   *
   *  The route has existed since ADR-111; nothing in the client called it. ADR-177 needs
   *  it twice over: to diff a phase's authored file against the published one, and to
   *  open the scripts a claim CITES — `drive_upload.py`, `freshness_guard.py`,
   *  `toolchain/activity-commands.yaml`. A citation an operator cannot open is a
   *  reference, not evidence.
   *
   *  A 404 with `found: false` is a NORMAL answer for a path a run authored but has not
   *  proposed yet. Other 404 bodies and other HTTP failures remain errors. */
  fleetFile(path: string): Promise<FleetFileResult> {
    return readFleetFile(path)
  }

  /** POST /api/activities/authoring-run/redrive (ADR-177) — do ONE phase again, with the
   *  reason the operator gave.
   *
   *  Rewinds the run to that phase and attaches the note; it does NOT run the turn. The
   *  two are separate for the same reason D6 separates a checkpoint's decision from the
   *  turn that follows it — a redrive requested and then abandoned leaves a readable run
   *  parked at a phase, not a turn in flight nobody asked to start.
   *
   *  `note` is required by the server: without it the turn is asked the same question that
   *  produced the answer being rejected. */
  redriveAuthoringPhase(
    agent: string, workflow: string, phase: string, note: string,
  ): Promise<AuthoringRunResult> {
    return post<AuthoringRunResult>('/api/activities/authoring-run/redrive',
      { agent, workflow, phase, note })
  }

  /** POST /api/activities/authoring-run/resume (ADR-166) — put a RECORDED run back in its
   *  slot, for when the live state is gone and the record is not.
   *
   *  NOT A REDRIVE, and the difference is the whole point: redrive rewinds a run that is IN
   *  the slot and answers 404 when the slot is empty, which is exactly the case this
   *  covers. spartan 20260917T174002Z-1a0d63 had walked all six phases to `results` and the
   *  operator's only option was to start over — "there is no place to select continue". */
  resumeAuthoringRun(agent: string, workflow: string, runId: string):
  Promise<AuthoringRunResult> {
    return post<AuthoringRunResult>('/api/activities/authoring-run/resume',
      { agent, workflow, run_id: runId })
  }

  /** POST /api/activities/authoring-run/propose (ADR-178 D3) — ship a FINISHED run.
   *
   *  THE CLICK THAT WAS MISSING. Until 2026-09-17 a run at `done` proposed itself, and five
   *  pull requests landed on one workflow with no operator action — all closed by hand.
   *  ShouldPropose now requires a recorded request, so this call is the only way a run
   *  becomes a pull request. */
  proposeAuthoringRun(agent: string, workflow: string): Promise<AuthoringRunResult> {
    return post<AuthoringRunResult>('/api/activities/authoring-run/propose',
      { agent, workflow })
  }

  proposeFleetFile(path: string, content: string, message?: string): Promise<ProposeSkillResult> {
    return post<ProposeSkillResult>('/api/fleet/file/propose', { path, content, message })
  }

  /**
   * POST /api/agents/propose (ADR-111 D2.3, fleet_owner) — create OR edit an
   * agent profile in config/fleet.yaml, as one PR + in-app improvement.
   */
  proposeAgent(input: AgentProposeInput): Promise<ProposeSkillResult> {
    return post<ProposeSkillResult>('/api/agents/propose', input)
  }

  /**
   * POST /api/agents/:agent/routing/propose (ADR-111 item 1, fleet_owner) —
   * set a specialist's routing: intent/capabilities in config/fleet.yaml AND
   * regenerate the brain's fleet-roster, as one PR + in-app improvement.
   */
  proposeRouting(agent: string, input: RoutingProposeInput): Promise<ProposeSkillResult> {
    return post<ProposeSkillResult>(`/api/agents/${encodeURIComponent(agent)}/routing/propose`, input)
  }

  convertSkill(
    agent: string,
    skill: string,
    mode: 'plan' | 'materialize',
    plan?: string,
    note?: string,
    onProgress?: (elapsedMs: number) => void,
  ): Promise<ConvertResult> {
    // Plan mode is a background job too now: it runs at 8192 max_tokens, which
    // crosses the heavy-call threshold and gets a 210s per-call budget — over
    // twice Cloudflare's 100s edge ceiling, so held synchronous it could 524
    // into an error the browser couldn't parse.
    return llmJobRequest<ConvertResult>(
      '/api/activities/convert',
      { agent, skill, mode, plan: plan ?? '', note: note ?? '' },
      id => `/api/activities/convert/status/${encodeURIComponent(id)}`,
      onProgress,
    )
  }

  /**
   * ADR-080 WS3c materialize — runs as a BACKGROUND JOB (the inline version once
   * OOM-crashed MC). POSTs the accepted plan, gets a job id back, then polls
   * /convert/status/:id until the leaner skill + validated workflows are ready.
   * `onProgress` (optional) is called each poll so the UI can show it's working.
   */
  async convertMaterialize(
    agent: string,
    skill: string,
    plan: string,
    note?: string,
    onProgress?: () => void,
  ): Promise<ConvertResult> {
    // Same transport as every other editor LLM call (llmJobRequest) — this one
    // pioneered it; the rest were folded in once the synchronous ones proved they
    // could die at the edge with no diagnosable error.
    return llmJobRequest<ConvertResult>(
      '/api/activities/convert',
      { agent, skill, mode: 'materialize', plan, note: note ?? '' },
      id => `/api/activities/convert/status/${encodeURIComponent(id)}`,
      () => onProgress?.(),
    )
  }

  /**
   * POST /api/activities/convert/propose — ADR-080 WS4 multi-file atomic
   * propose: commits the leaner skill + N workflows as one branch/PR/in-app
   * improvement (fleet_owner). Every activity must already be DSL-valid.
   */
  proposeConversion(
    agent: string,
    skill: { name: string; content: string },
    activities: { name: string; yaml: string }[],
    message?: string,
  ): Promise<ProposeConversionResult> {
    return post<ProposeConversionResult>('/api/activities/convert/propose', {
      agent, skill, activities, message: message ?? '',
    })
  }

  /** POST /api/modules/:id/data/:sourceId */
  getModuleData(
    id: string,
    sourceId: string,
  ): Promise<{ ok: boolean; data: unknown }> {
    return post<{ ok: boolean; data: unknown }>(
      `/api/modules/${encodeURIComponent(id)}/data/${encodeURIComponent(sourceId)}`,
      {},
    )
  }

  /**
   * GET /api/logs — filterable, paginated log query (ADR-024 D6).
   *
   * Empty-valued params are omitted from the query string.
   * `level`, `type`, and `source` accept comma-separated multi-values.
   *
   * 403 → ForbiddenError (requires operator or fleet_owner).
   */
  getLogs(params: LogsParams): Promise<LogsResponse> {
    const qs = new URLSearchParams()
    const entries = Object.entries(params) as [string, string | number | undefined][]
    for (const [key, val] of entries) {
      if (val !== undefined && val !== null && val !== '') {
        qs.set(key, String(val))
      }
    }
    const query = qs.toString()
    return request<LogsResponse>(`/api/logs${query ? `?${query}` : ''}`)
  }

  /**
   * GET /api/logs/stats — pipeline health counters (fleet_owner only).
   * 403 → ForbiddenError (requires fleet_owner).
   */
  getLogsStats(): Promise<LogsStats> {
    return request<LogsStats>('/api/logs/stats')
  }

  /**
   * GET /api/delegations — list recent delegations (ADR-051 D1).
   * 403 → ForbiddenError (requires operator or fleet_owner).
   */
  getDelegations(params?: DelegationsParams): Promise<DelegationsListResponse> {
    const qs = new URLSearchParams()
    if (params?.window_hours !== undefined) qs.set('window_hours', String(params.window_hours))
    if (params?.outcome && params.outcome !== 'all') qs.set('outcome', params.outcome)
    if (params?.limit !== undefined) qs.set('limit', String(params.limit))
    const query = qs.toString()
    return request<DelegationsListResponse>(`/api/delegations${query ? `?${query}` : ''}`)
  }

  /**
   * GET /api/delegations/trace — per-call attribution trace (ADR-051 D1).
   * Requires at least one of turn_id or delegation_id.
   * 403 → ForbiddenError (requires operator or fleet_owner).
   */
  getDelegationTrace(params: { turn_id?: string; delegation_id?: string }): Promise<DelegationTraceResponse> {
    const qs = new URLSearchParams()
    if (params.turn_id) qs.set('turn_id', params.turn_id)
    if (params.delegation_id) qs.set('delegation_id', params.delegation_id)
    return request<DelegationTraceResponse>(`/api/delegations/trace?${qs.toString()}`)
  }

  /** GET /api/llm-usage/rollup — token and call aggregates. */
  getLlmUsageRollup(params?: LlmUsageQueryParams): Promise<LlmUsageRollupResponse> {
    return request<LlmUsageRollupResponse>(llmUsagePath('rollup', params))
  }

  /** GET /api/llm-usage/traces — recent trace roots. */
  getLlmUsageTraces(params?: LlmUsageQueryParams): Promise<LlmUsageTracesResponse> {
    return request<LlmUsageTracesResponse>(llmUsagePath('traces', params))
  }

  /** GET /api/llm-usage/trace — one trace's paginated spans. */
  getLlmUsageTrace(params?: LlmUsageQueryParams): Promise<LlmUsageTraceResponse> {
    return request<LlmUsageTraceResponse>(llmUsagePath('trace', params))
  }

  /** GET /api/llm-usage/daily — one UTC day's provider totals. */
  getLlmUsageDaily(params?: Pick<LlmUsageQueryParams, 'day'>): Promise<LlmUsageDailyResponse> {
    return request<LlmUsageDailyResponse>(llmUsagePath('daily', params))
  }

  // -------------------------------------------------------------------------
  // Chat API (Phase 1 — non-streaming)
  // -------------------------------------------------------------------------

  /** GET /api/chat/conversations — list conversations with optional filters. */
  listConversations(params?: ListConversationsParams): Promise<Conversation[]> {
    const qs = new URLSearchParams()
    if (params) {
      const entries = Object.entries(params) as [string, string | undefined][]
      for (const [key, val] of entries) {
        if (val !== undefined && val !== '') qs.set(key, val)
      }
    }
    const query = qs.toString()
    // The backend wraps the list as { conversations: [...] }; tolerate a bare
    // array too. Always resolve to an array so callers can safely .map/.length.
    return request<{ conversations?: Conversation[] } | Conversation[]>(
      `/api/chat/conversations${query ? `?${query}` : ''}`,
    ).then(body => (Array.isArray(body) ? body : body?.conversations ?? []))
  }

  /**
   * POST /api/chat/conversations — create a new conversation.
   * All fields optional; a blank conversation is valid.
   */
  createConversation(body: {
    title?: string
    kind?: string
    project_id?: string
    primary_agent?: string
  }): Promise<Conversation> {
    return post<Conversation>('/api/chat/conversations', body)
  }

  /**
   * POST /api/activity-approvals/:id/decision — ADR-203. The preview hash is
   * the one the operator was shown; a decision on any other preview, or on an
   * approval already decided or superseded, is refused with a 409.
   */
  decideActivityApproval(id: string, decision: 'approve' | 'reject', previewSha256: string, note?: string): Promise<ActivityApproval> {
    return post<ActivityApproval>(`/api/activity-approvals/${encodeURIComponent(id)}/decision`, {
      decision,
      preview_sha256: previewSha256,
      ...(note?.trim() ? { note: note.trim() } : {}),
    })
  }

  /** GET /api/chat/conversations/:id — fetch a conversation with full message history. */
  getConversation(id: string): Promise<ConversationDetail> {
    return request<ConversationDetail>(`/api/chat/conversations/${encodeURIComponent(id)}`)
  }

  /**
   * PATCH /api/chat/conversations/:id — update title, archive, and/or project_id.
   * Pass project_id: null to unassign, a UUID string to assign.
   */
  updateConversation(
    id: string,
    body: { title?: string; archived?: boolean; project_id?: string | null },
  ): Promise<{ ok: boolean }> {
    return patch<{ ok: boolean }>(`/api/chat/conversations/${encodeURIComponent(id)}`, body)
  }

  /** DELETE /api/chat/conversations/:id — permanently delete a conversation. */
  deleteConversation(id: string): Promise<void> {
    return del(`/api/chat/conversations/${encodeURIComponent(id)}`)
  }

  /**
   * POST /api/chat/conversations/:id/messages — send a message.
   * Returns the persisted operator message + the assistant reply.
   * The assistant message may have status:"error" with the error text as content.
   */
  // `composed` marks a message the client wrote itself rather than one the
  // operator typed; it is never bound as `chat: operator_messages` (ADR-202 D1).
  sendMessage(id: string, content: string, opts: { composed?: boolean } = {}): Promise<SendMessageResponse> {
    return post<SendMessageResponse>(
      `/api/chat/conversations/${encodeURIComponent(id)}/messages`,
      opts.composed ? { content, composed: true } : { content },
    )
  }

  /** POST /api/chat/conversations/:id/command-preview — preview a composer command. */
  previewChatCommand(conversationId: string, content: string): Promise<ChatCommandPreviewResponse> {
    return post<ChatCommandPreviewResponse>(
      `/api/chat/conversations/${encodeURIComponent(conversationId)}/command-preview`,
      { content },
    )
  }

  /** GET /api/chat/projects — list projects. */
  listProjects(): Promise<Project[]> {
    return request<{ projects?: Project[] } | Project[]>('/api/chat/projects')
      .then(body => (Array.isArray(body) ? body : body?.projects ?? []))
  }

  /** POST /api/chat/projects — create a new project. */
  createProject(body: { name: string; description?: string; core_prompt?: string }): Promise<Project> {
    return post<Project>('/api/chat/projects', body)
  }

  /** PATCH /api/chat/projects/:id — update name and/or core_prompt. */
  updateProject(id: string, body: UpdateProjectBody): Promise<{ ok: boolean }> {
    return patch<{ ok: boolean }>(`/api/chat/projects/${encodeURIComponent(id)}`, body)
  }

  /** DELETE /api/chat/projects/:id — delete a project (unassigns conversations). */
  deleteProject(id: string): Promise<{ ok: boolean }> {
    const token = tokenManager.getToken()
    return fetch(`/api/chat/projects/${encodeURIComponent(id)}`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
    }).then(async resp => {
      if (!resp.ok) { const t = await resp.text(); throw new Error(`HTTP ${resp.status}: ${t}`) }
      return resp.json() as Promise<{ ok: boolean }>
    })
  }

  /** POST /api/chat/conversations/:id/files — upload a file attachment. */
  uploadFile(conversationId: string, file: File): Promise<ChatFile> {
    const token = tokenManager.getToken()
    const form = new FormData()
    form.append('file', file)
    return fetch(`/api/chat/conversations/${encodeURIComponent(conversationId)}/files`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` },
      body: form,
    }).then(async resp => {
      if (!resp.ok) {
        const text = await resp.text()
        let msg = `HTTP ${resp.status}: ${text}`
        try { const j = JSON.parse(text); msg = j.message ?? j.error ?? msg } catch { /* ignore */ }
        throw new Error(msg)
      }
      return resp.json() as Promise<ChatFile>
    })
  }

  /** GET /api/chat/conversations/:id/files — list file metadata for a conversation. */
  listFiles(conversationId: string): Promise<ChatFile[]> {
    return request<{ files?: ChatFile[] } | ChatFile[]>(
      `/api/chat/conversations/${encodeURIComponent(conversationId)}/files`,
    ).then(body => (Array.isArray(body) ? body : body?.files ?? []))
  }

  /** DELETE /api/chat/conversations/:id/files/:fileId — remove an attachment. */
  deleteFile(conversationId: string, fileId: string): Promise<{ ok: boolean }> {
    const token = tokenManager.getToken()
    return fetch(
      `/api/chat/conversations/${encodeURIComponent(conversationId)}/files/${encodeURIComponent(fileId)}`,
      { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } },
    ).then(async resp => {
      if (!resp.ok) { const t = await resp.text(); throw new Error(`HTTP ${resp.status}: ${t}`) }
      return resp.json() as Promise<{ ok: boolean }>
    })
  }

  /** POST /api/chat/conversations/:id/tags — add a tag to a conversation. */
  addTag(id: string, tag: string): Promise<{ tags: string[] }> {
    return post<{ tags: string[] }>(
      `/api/chat/conversations/${encodeURIComponent(id)}/tags`,
      { tag },
    )
  }

  /** DELETE /api/chat/conversations/:id/tags/:tag — remove a tag from a conversation. */
  removeTag(id: string, tag: string): Promise<{ tags: string[] }> {
    // Need authenticated DELETE that returns JSON, so we use the del helper won't parse body.
    // Use post with a custom approach — call fetch directly via the request helper instead.
    const token = tokenManager.getToken()
    return fetch(`/api/chat/conversations/${encodeURIComponent(id)}/tags/${encodeURIComponent(tag)}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
    }).then(async resp => {
      if (!resp.ok) {
        const text = await resp.text()
        throw new Error(`HTTP ${resp.status}: ${text}`)
      }
      return resp.json() as Promise<{ tags: string[] }>
    })
  }

  // -------------------------------------------------------------------------
  // Mentions API (Phase 4b)
  // -------------------------------------------------------------------------

  /** GET /api/chat/mentions/candidates?q= — email picker for @mention typeahead. */
  listMentionCandidates(q?: string): Promise<{ candidates: MentionCandidate[] }> {
    const qs = q ? `?q=${encodeURIComponent(q)}` : ''
    return request<{ candidates: MentionCandidate[] }>(`/api/chat/mentions/candidates${qs}`)
  }

  /** GET /api/chat/mentions/unread — unread mentions for the current user. */
  listUnreadMentions(): Promise<{ mentions: UnreadMention[]; count: number }> {
    return request<{ mentions: UnreadMention[]; count: number }>('/api/chat/mentions/unread')
  }

  /** POST /api/chat/mentions/read — mark mentions in a conversation as read. */
  markMentionsRead(conversationId: string): Promise<{ ok: boolean }> {
    return post<{ ok: boolean }>('/api/chat/mentions/read', { conversation_id: conversationId })
  }

  // -------------------------------------------------------------------------
  // Channels API (Phase 4a)
  // -------------------------------------------------------------------------

  /** POST /api/chat/channels */
  createChannel(body: CreateChannelBody): Promise<Conversation> {
    return post<Conversation>('/api/chat/channels', body)
  }

  /** GET /api/chat/channels */
  listChannels(): Promise<Conversation[]> {
    return request<{ channels?: Conversation[] } | Conversation[]>('/api/chat/channels')
      .then(body => (Array.isArray(body) ? body : body?.channels ?? []))
  }

  /** POST /api/chat/channels/:id/agents */
  addChannelAgent(channelId: string, body: AddChannelAgentBody): Promise<ChannelAgent> {
    return post<ChannelAgent>(`/api/chat/channels/${encodeURIComponent(channelId)}/agents`, body)
  }

  /** GET /api/chat/channels/:id/agents */
  listChannelAgents(channelId: string): Promise<ChannelAgent[]> {
    return request<{ agents?: ChannelAgent[] } | ChannelAgent[]>(
      `/api/chat/channels/${encodeURIComponent(channelId)}/agents`,
    ).then(body => (Array.isArray(body) ? body : body?.agents ?? []))
  }

  /** PATCH /api/chat/channels/:id/agents/:agent */
  updateChannelAgent(channelId: string, agent: string, body: { delivery_mode?: string; also_post_slack?: boolean; active?: boolean }): Promise<ChannelAgent> {
    return patch<ChannelAgent>(
      `/api/chat/channels/${encodeURIComponent(channelId)}/agents/${encodeURIComponent(agent)}`,
      body,
    )
  }

  /** DELETE /api/chat/channels/:id/agents/:agent */
  removeChannelAgent(channelId: string, agent: string): Promise<void> {
    return del(
      `/api/chat/channels/${encodeURIComponent(channelId)}/agents/${encodeURIComponent(agent)}`,
    )
  }

  // -------------------------------------------------------------------------
  // Phase 5 — Export, Message Actions, Branch
  // -------------------------------------------------------------------------

  /**
   * GET /api/chat/conversations/:id/export?format=markdown|csv
   * Downloads the conversation transcript as a file attachment.
   * Returns a Blob for the caller to trigger a browser download.
   */
  async exportConversation(
    id: string,
    format: 'markdown' | 'csv',
  ): Promise<{ blob: Blob; filename: string }> {
    const token = tokenManager.getToken()
    const resp = await fetch(
      `/api/chat/conversations/${encodeURIComponent(id)}/export?format=${encodeURIComponent(format)}`,
      {
        headers: { Authorization: `Bearer ${token}` },
      },
    )
    if (resp.status === 401) throw new UnauthorizedError()
    if (resp.status === 403) throw new ForbiddenError()
    if (!resp.ok) {
      const text = await resp.text()
      throw new Error(`HTTP ${resp.status}: ${text}`)
    }
    const blob = await resp.blob()
    // Extract filename from Content-Disposition: attachment; filename="<name>"
    const cd = resp.headers.get('Content-Disposition') ?? ''
    const match = cd.match(/filename="([^"]+)"/)
    const filename = match ? match[1] : `conversation.${format === 'markdown' ? 'md' : 'csv'}`
    return { blob, filename }
  }

  /** POST /api/chat/conversations/:id/messages/:messageId/regenerate */
  stopMessage(conversationId: string, messageId: string): Promise<{ok: boolean}> {
    return post(`/api/chat/conversations/${encodeURIComponent(conversationId)}/messages/${encodeURIComponent(messageId)}/stop`, {})
  }

  resolveUnknownRun(conversationId: string, messageId: string): Promise<{ok: boolean}> {
    return post(`/api/chat/conversations/${encodeURIComponent(conversationId)}/messages/${encodeURIComponent(messageId)}/resolve-unknown`, {})
  }

  regenerateMessage(
    conversationId: string,
    messageId: string,
  ): Promise<RegenerateMessageResponse> {
    return post<RegenerateMessageResponse>(
      `/api/chat/conversations/${encodeURIComponent(conversationId)}/messages/${encodeURIComponent(messageId)}/regenerate`,
      {},
    )
  }

  /** POST /api/chat/conversations/:id/messages/:messageId/edit */
  editMessage(
    conversationId: string,
    messageId: string,
    content: string,
  ): Promise<EditMessageResponse> {
    return post<EditMessageResponse>(
      `/api/chat/conversations/${encodeURIComponent(conversationId)}/messages/${encodeURIComponent(messageId)}/edit`,
      { content },
    )
  }

  /** POST /api/chat/conversations/:id/branch */
  branchConversation(
    conversationId: string,
    fromMessageId: string,
    title?: string,
  ): Promise<ConversationDetail> {
    return post<ConversationDetail>(
      `/api/chat/conversations/${encodeURIComponent(conversationId)}/branch`,
      { from_message_id: fromMessageId, ...(title ? { title } : {}) },
    )
  }
}

export const api = new MCClient()
